import type { ReactNode } from "react";
import React from "react";
import { listFormatConjunction } from "../lib/time/intl";
import { capitalize, lowercase, printListStrings } from "diagnostics-lib/string";
import { fr as DashboardsI18nFr } from "../components/Project/Dashboards/i18n";
import { fr as FaultEventI18nFr } from "../components/Project/Events/i18n";
import { MAX_ANALYSIS_COUNT, MAX_QUANTITY_COUNT } from "diagnostics-domain/constants";
import type { Reparability } from "../api/Reparability";
import type { Compliance } from "diagnostics-domain/quantity/compliance";
import formatNumber from "@ui/i18n/formatNumber";
import type { CustomTranslations } from "../i18n/types";
import { frValidations } from "./frValidations";
import { MAX_PROCESS_INDICATOR_COUNT } from "diagnostics-domain/quantity/processIndicator";

const renderFileText = (modelType: string, fileNumber: number) => (
  <>{`Modèle (fichier ${fileNumber}/${modelType === "DYMOLA" ? "2" : "3"}) :`}</>
);

const renderFileValue = (modelType: string, fileNumber: number) => {
  switch (fileNumber) {
    case 1:
      return <>{modelType === "DYMOLA" ? " dymosim" : " .fmu"}</>;
    case 2:
      return (
        <>
          {modelType === "DYMOLA"
            ? " dsin.txt"
            : " .csv (nom contenant iteration_variables / facultatif)"}
        </>
      );
    case 3:
      return " .csv (nom contenant parameters / facultatif)" as string;
    default:
      return;
  }
};

const fr = {
  ...DashboardsI18nFr,
  ...FaultEventI18nFr,
  "action.confirm": "Confirmer",
  "action.reset": "Réinitialiser",
  "activitities.refresh.tooltip"({ count }: { count: number }) {
    return count === 1
      ? "Afficher une nouvelle actualité"
      : `Afficher les ${count} nouvelles actualités`;
  },
  "activity.badge.project.deleted": "Projet supprimé",
  "activity.blank_state.global_description": "Les actions réalisées apparaîtront ici.",
  "activity.blank_state.project_description":
    "Les actions réalisées sur ce projet apparaîtront ici.",
  "activity.blank_state.title": "Aucune activité",
  "activity.body.details.title": "Détails",
  "activity.body.see_more": "Plus",
  "activity.event.badge.manual": "Manuel",
  "activity.header.title": "Activité",
  "activity.hide_automatic_actions": "Masquer les actions automatiques",
  "activity.hide_automatic_actions.tooltip":
    "Masquer les actions réalisées sans action utilisateur (import de données, réalisation de calcul de simulation et diagnostic)",
  "activity.paragraph.title": "Fil d'activité",
  "activity.selectFilter.activity": "Activité",
  "activity.selectFilter.authors": "Utilisateur",
  "activity.selectFilter.events": "Événement",
  "activity.selectFilter.projects": "Projet",
  "activity.title": "Activité",
  "administration.application.api_key.column.api_key": "Clé API",
  "administration.application.api_key.column.created_at": "Date de création",
  "administration.application.api_key.column.created_by": "Créée par",
  "administration.application.api_key.modal.copy.button": "Copier la clé API",
  "administration.application.api_key.modal.create.button": "Générer une clé API",
  "administration.application.api_key.modal.create.text":
    "La clé API générée servira à l’authentification du flux de données en temps réel. Elle ne sera consultable qu’à la création et en cas de perte ou corruption, elle devra être supprimée et une nouvelle clé devra être utilisée.",
  "administration.application.api_key.modal.create.title": "Générer une clé API",
  "administration.application.api_key.modal.revoke.button": "Supprimer",
  "administration.application.api_key.modal.revoke.text":
    "Cette action est irréversible. Vous allez supprimer définitivement la clé API, et le flux de données l’utilisant ne sera plus fonctionnel sans l’utilisation d’une autre clé API. Souhaitez-vous supprimer cette clé API ?",
  "administration.application.api_key.modal.revoke.title": "Supprimer la clé API",
  "administration.application.api_key.title": "Flux de données en temps réel",
  "administration.application.api_key.transaction.copy.failure": "Échec de la copie de la clé API",
  "administration.application.api_key.transaction.copy.success":
    "La clé API a été copiée dans le presse-papier",
  "administration.application.api_key.transaction.create.failure":
    "Échec de la création de la clé API",
  "administration.application.api_key.transaction.create.success": "La clé API a été créée",
  "administration.application.api_key.transaction.revoke.failure":
    "Échec de la suppression de la clé API",
  "administration.application.api_key.transaction.revoke.success": "La clé API a été supprimée",
  "administration.classification.header.failures": "Défaillances",
  "administration.classification.header.quantities": "Grandeurs",
  "administration.failures.modal.action.confirm": (count: number) =>
    count === 1 ? "1 défaillance a été mise à jour" : `${count} défaillances ont été mises à jour`,
  "administration.failures.table.header.actions.update": (count: number) =>
    count === 0 ? "Éditer les défaillances" : `Éditer les défaillances (${count})`,
  "administration.failures.table.header.category": "Catégorie",
  "administration.failures.table.header.degradation_type": "Type de dégradation",
  "administration.failures.table.header.magnitude": "Amplitude",
  "administration.failures.table.header.max": "Max",
  "administration.failures.table.header.min": "Min",
  "administration.failures.table.header.reparability": "Réparabilité",
  "administration.failures.table.header.suspectedElements": "Éléments suspectés",
  "administration.header.classification": "Gestion d'actifs",
  "administration.header.customization": "Application",
  "administration.header.processes": "Processus",
  "administration.header.title": "Administration",
  "administration.header.users": "Utilisateurs",
  "administration.modal.multiple_value": "Valeurs multiples",
  "administration.processes.batches.last_updated_at": "Dernière mise à jour",
  "administration.processes.batches.priority": "Priorité",
  "administration.processes.batches.process_type": "Type de processus",
  "administration.processes.batches.progress": "Progression",
  "administration.processes.batches.progress.done": "terminés",
  "administration.processes.batches.progress.queued": "en attente",
  "administration.processes.batches.progress.running": "en cours",
  "administration.processes.batches.project_name": "Projet",
  "administration.processes.batches.status": "Statut",
  "administration.processes.batches.status.done": "Terminé",
  "administration.processes.batches.status.pending": "En attente",
  "administration.processes.batches.status.running": "En cours",
  "administration.processes.batches.title": "Lots de processus",
  "administration.processes.empty": "Aucun processus trouvé.",
  "administration.processes.filters.all": "Tous",
  "administration.processes.filters.any": "N'importe lequel",
  "administration.processes.filters.project.filter_this": "Filtrer sur ce projet",
  "administration.processes.filters.status": "Statut",
  "administration.processes.filters.type": "Type",
  "administration.processes.filters.updated_since": "Modifiés depuis",
  "administration.processes.filters.updated_since.any": "N'importe quand",
  "administration.processes.filters.updated_since.last_30_minutes": "30 dernières minutes",
  "administration.processes.filters.updated_since.last_6_hours": "6 dernières heures",
  "administration.processes.filters.updated_since.last_day": "Dernier jour",
  "administration.processes.filters.updated_since.last_week": "Dernière semaine",
  "administration.processes.overview.by_project": "Par projet",
  "administration.processes.overview.by_status_type": "Par statut et type",
  "administration.processes.overview.count": "Nombre",
  "administration.processes.overview.project": "Projet",
  "administration.processes.overview.status": "Statut",
  "administration.processes.overview.title": "Populations de processus",
  "administration.processes.project_name.unknown": "Projet inconnu",
  "administration.processes.subtab.monitoring": "Suivi",
  "administration.processes.subtab.trigger": "Déclenchement",
  "administration.processes.title": "Suivi des processus",
  "administration.quantities.modal.action.confirm": (count: number) =>
    count === 1 ? "1 grandeur a été mise à jour" : `${count} grandeurs ont été mises à jour`,
  "administration.quantities.table.header.actions.edit": "Éditer les grandeurs",
  "administration.quantities.table.header.actions.update": ({ count }: { count: number }) =>
    count === 0 ? "Éditer les grandeurs" : `Éditer les grandeurs (${count})`,
  "administration.quantities.table.header.project_name": "Projet",
  "administration.quantities.table.header.project_setup": "Paramétrage",
  "administration.quantities.table.header.project_site": "Site",
  "administration.quantities.table.header.project_type": (
    translations: CustomTranslations,
  ): string => capitalize(translations.FR.TYPE),
  "administration.quantities.table.header.system": "Système",
  "administration.table.failures.selection_button_title":
    "Sélectionner les défaillances dans le tableau",
  "administration.table.quantities.selection_button_title":
    "Sélectionner les grandeurs dans le tableau",
  "analysis.chart.tooltip.description.analysisPublication_status.included_in_reporting":
    "Analyse publiée et incluse dans le reporting",
  "analysis.chart.tooltip.description.analysisPublication_status.not_included_in_reporting":
    "Analyse publiée et non incluse dans le\nreporting (analyse plus récente publiée pour ce mois)",
  "app.administration.application.link.modal.form.delete.content": (nameLink: ReactNode) => (
    <>
      Vous êtes sur le point de supprimer définitivement le lien de {nameLink}.
      <br />
      <br />
      Cette suppression entraînera sa disparition du menu de l’application.
    </>
  ),
  "app.administration.application.link.modal.form.edit.link_name.error.empty_field":
    "Le texte du lien ne doit pas être vide.",
  "app.administration.application.link.modal.form.edit.url.error.empty_field":
    "L'URL ne doit pas être vide.",
  "app.administration.application.link.modal.form.input.error.cannot_exceed_250_characters":
    "Le texte du lien ne peut pas dépasser 250 caractères.",
  "app.administration.application.link.modal.form.input.url.error.cannot_exceed_4000_characters":
    "L'URL ne peut pas dépasser 4000 caractères.",
  "app.administration.application.link.modal.form.text_en.label": "Texte en anglais",
  "app.administration.application.link.modal.form.text_fr.label": "Texte en français",
  "app.administration.application.link.modal.title.delete": "Supprimer le lien",
  "app.administration.application.link.modal.title.edit": "Éditer le lien",
  "app.customization.aggregation_period.power_outputs.button": "Mettre en cache les puissances",
  "app.customization.aggregation_period.power_outputs.description":
    "Mettre en file d’attente le calcul des puissances pour tous les projets en production.",
  "app.customization.aggregation_period.power_outputs.queued":
    "La mise en cache des puissances a été mise en file d’attente.",
  "app.customization.aggregation_period.select.label": "Période d’agrégation",
  "app.customization.aggregation_period.select.tooltip":
    "La période sélectionnée correspond à la période affichée par défaut dans les graphes et utilisée pour le calcul des valeurs agrégées dans les pages de synthèse et des événements notamment.",
  "app.customization.algorithm.select.label": "Version par défaut",
  "app.header.project_spinner.awaiting_step_evaluation": ({
    percentage,
    step,
  }: {
    percentage: number;
    step: string;
  }) => `${percentage} % en attente d'évaluation des ${step}`,
  "app.header.project_spinner.project_initialization_in_progress":
    "Initialisation du projet en cours",
  "app.header.project_spinner.step.diagnosis_inputs": "entrées de diagnostic",
  "app.header.project_spinner.step.external_gap_inputs": "entrées d'écarts externes",
  "app.header.project_spinner.step.simulation_inputs": "entrées de simulation",
  "app.header.project_spinner.update_in_progress_for_analyses": ({
    outdatedCount,
    activeAnalysisCount,
  }: {
    outdatedCount: number;
    activeAnalysisCount: number;
  }) => `Mise à jour en cours pour ${outdatedCount} analyses (sur ${activeAnalysisCount} au total)`,
  "app.legals_modal.legals_link": "Consulter les mentions légales",
  "app.legals_modal.skip_message": "Ne plus me montrer ce message à mes prochaines connexions",
  "app.modal.legals.title": (year: string) => `Mention d'information METROSCOPE ${year}`,
  "button.refresh": "Rafraîchir",
  "chart.display_options.tooltip": "Options d'affichage du graphe",
  "chart.period.no_analysis_yet": "Aucune analyse disponible pour le moment",
  "chart.period.no_data_yet": "Aucune donnée disponible pour le moment",
  "chart.period.now": "Maintenant",
  "chart.period.zoom_all": "Aucune donnée disponible sur cette période. Afficher toute la période",
  "chart.validity.reset_zoom.title": "Réinitialiser le zoom",
  "chart.validity.reset_zoom.tootltip":
    "La plage affichée inclura toutes les mesures valides, en excluant certaines mesures invalides aberrantes",
  "charts.flag.item.description": ({ name, description }: { name: string; description: string }) =>
    `${name} : ${description}`,
  "charts.info_tooltip.more": "En savoir plus",
  "charts.legend.confidence_area": "Zone de confiance",
  "charts.legend.internal_losses": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "charts.legend.internal_losses_global": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)}`,
  "charts.legend.remaining_symptom": "Symptôme résiduel",
  "charts.legend.symptom": "Symptôme",
  "charts.legend.unexplained_losses": (translations: {
    FR: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
    EN: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
  }): string => `${capitalize(translations.FR.LOSSES)} ${lowercase(translations.FR.UNEXPLAINED)}`,
  "classification.title": "Gestion d'actifs",
  "comments.answer": "Répondre au commentaire",
  "comments.author": "Auteur",
  "comments.blank_state_analysis.call_to_action": "Laisser un commentaire",
  "comments.blank_state_analysis.title": "Aucun commentaire",
  "comments.create.placeholder": "Rédiger un nouveau commentaire",
  "comments.fault_event.menu.description": ({
    startsAt,
    closedAt,
    status,
  }: {
    startsAt: string;
    closedAt: string | null;
    status: string;
  }) => {
    if (closedAt === null) return `Première détection : ${startsAt}, ${status}`;
    return `${startsAt} - ${closedAt}, ${status}`;
  },
  "comments.filters.event": "Événement",
  "comments.filters.user": "Utilisateur",
  "comments.setup.label": "Commentaire de paramétrage",
  "comments.setup.toggle": "Créer un commentaire de paramétrage",
  "comments.setup.tooltip":
    "Créer un commentaire réservé aux utilisateurs ayant le rôle Paramétrage, visible en mode paramétrage.",
  "comments.tooltip.analysis_outside_of_event_range":
    "Analyse en dehors de la plage de l’événement, association non possible",
  "comments.tooltip.associate_event": "Associer à un événement",
  "comments.tooltip.connect_current_analysis": `Associer à l'analyse sélectionnée`,
  "comments.tooltip.feature_under_development":
    "Fonctionnalité en cours de développement, pas encore accessible",
  "comments.tooltip.toggle_current_analysis_comments":
    "Afficher uniquement les commentaires associés à la sélection",
  "comments.tooltip.toggle_filters": "Filtrer les commentaires",
  "commom.widgets.failures.title": "Défaillances",
  "commom.widgets.losses.title": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)}`,
  "commom.widgets.processIndicators.title": "Indicateurs de conduite",
  "common.button.add_comment_on_analysis": "Ajouter un commentaire",
  "common.button.export_analysis": "Exporter les données d'analyse",
  "common.date.last_analysis": "Dernière analyse",
  "common.date.no_last_analysis": "Aucune analyse",
  "common.date.no_published_analysis": "Aucune analyse publiée",
  "common.empty.activeAnalyses.action": "Aller à la page Données",
  "common.empty.activeAnalyses.description": "Veuillez créer une analyse depuis la page Données",
  "common.empty.activeAnalyses.title": "Le projet ne contient aucune analyse",
  "common.fault_status.legacy_rejected.relaunch": "(sans le relancement de diagnostic)",
  "common.fault_status.rejected.relaunch": "(avec le relancement de diagnostic)",
  "common.file": "Fichier :",
  "common.file.import": "Importer",
  "common.legal_notice": "Mentions légales :",
  "common.legal_notice.title": "Importer le fichier de mentions légales",
  "common.none": "Aucun",
  "common.processIndicators.blank.title": "Aucun indicateur de conduite",
  "common.processIndicators.blank.title.description":
    "Vous pouvez les définir dans la page de Réglages",
  "common.search.no_result": "Aucun résultat",
  "common.see_more": "Voir plus",
  "common.select": "Sélectionner",
  "common.select.empty": "Aucun",
  "common.symptom.expected": "Attendu",
  "common.symptom.measured": "Mesuré",
  "common.value": "Valeur",
  "commons.analysis_title": "Détail de l’analyse sélectionnée",
  "commons.analysis_title.group": ({ group }: { group: string }) => `Cycle ${group}`,
  "commons.analysis_title.group_and_date": ({ group, date }: { group: string; date: string }) =>
    `Cycle ${group} - ${date}`,
  "commons.analysis_title.group_and_label": ({ group, label }: { group: string; label: string }) =>
    `Cycle ${group} - Essai ${label}`,
  "commons.analysis_title.group_and_label_and_date": ({
    group,
    label,
    date,
  }: {
    group: string;
    label: string;
    date: string;
  }) => `Cycle ${group} - Essai ${label} - ${date}`,
  "commons.analysis_title.label": ({ label }: { label: string }) => `Essai ${label}`,
  "commons.analysis_title.test_and_date": ({ label, date }: { label: string; date: string }) =>
    `Test ${label} - ${date}`,
  "commons.button.activate": "Créer",
  "commons.button.cancel": "Annuler",
  "commons.button.deactivate": "Supprimer",
  "commons.button.publish": "Publier",
  "commons.button.save": "Enregistrer",
  "commons.button.unpublish": "Dépublier",
  "commons.copyDetail": "Copier le détail",
  "commons.copyDetail.toast": "Détail copié",
  "commons.date_picker.from": "De",
  "commons.date_picker.to": "à",
  "commons.details": "Détails",
  "commons.failures.table.manualTitle": "Défaillance non modélisée",
  "commons.input.error.text_to_long": "Texte dépassant la limite de caractères autorisée",
  "commons.message_card.answer": "Répondre",
  "commons.message_card.answers": ({ count }: { count: number }) =>
    count === 1 ? `${count} réponse` : `${count} réponses`,
  "commons.message_card.ask_delete": "Supprimer ?",
  "commons.message_card.ask_delete_thread": "Supprimer la conversation ?",
  "commons.message_card.cancel": "Annuler",
  "commons.message_card.confirm": "Confirmer",
  "commons.message_card.confirm_delete": "Supprimer",
  "commons.message_card.delete": "Supprimer",
  "commons.message_card.delete_thread": "Supprimer la conversation",
  "commons.message_card.edit": "Éditer",
  "commons.message_card.export": "Exporter",
  "commons.message_card.replace": "Remplacer",
  "commons.modal.confirm_loading.message": "Souhaitez-vous confirmer l’import des fichiers ?",
  "commons.modal.expected_file.link": "Fichier d'exemple",
  "commons.modal.expected_file_format": "Formats de fichiers attendus :",
  "commons.modal.identified_file": "Fichier identifié :",
  "commons.modal.identified_files": "Fichiers identifiés :",
  "commons.modal.not_valid_form.message": ({ icon }: { icon: React.ReactNode }) => (
    <>
      {icon} L’import de ces fichiers est impossible. Merci d’effectuer les modifications requises
      et de charger à nouveau les fichiers.
    </>
  ),
  "commons.no_quantities_yet.text": "Aucune grandeur n'existe dans le projet.",
  "commons.none_value": ({ icon }: { icon: React.ReactNode }) => (
    <>
      <> aucun {icon}</>
    </>
  ),
  "commons.overview.header.legend.filled": "Confirmée / Fausse détection",
  "commons.overview.header.legend.filled.description":
    "Défaillance confirmée ou déclarée en fausse détection",
  "commons.overview.header.legend.manual_failure": "Défaillance non modélisée",
  "commons.overview.header.legend.manual_failure.description": "Défaillance non modélisée",
  "commons.overview.header.legend.not_filled": "Suspectée",
  "commons.overview.header.legend.not_filled.description":
    "Défaillance suspectée : elle est en cours d'analyse et doit être confirmée pour apparaître en page de synthèse.",
  "commons.overview.header.legend.to_be_analysed.description":
    "Défaillance proposée par l'algorithme, devant être analysée par l'utilisateur avant de changer son statut",
  "commons.placeholder.search": "Rechercher",
  "commons.quantities.selectFilter.filters": "Filtres",
  "commons.quantities.selectFilter.filters.activation": "Analyse",
  "commons.quantities.selectFilter.filters.metrology": "Metrologique",
  "commons.quantities.selectFilter.indicator": "Indicateur",
  "commons.quantities.selectFilter.role": "Rôle",
  "commons.quantities.selectFilter.system": "Système",
  "commons.quantities.selectFilter.threshold": "Seuils de validité",
  "commons.quantities.selectFilter.type": "Nature",
  "commons.quantities.selectFilter.type.dashboard": "Tableau de bord",
  "commons.quantities.selectFilter.type.formula": "Formule",
  "commons.quantities.selectFilter.type.setup": "Paramétrage",
  "commons.quantities.selectFilter.unit": "Unité",
  "commons.quantities.selectTypeOption.calculated_value": "Calcul",
  "commons.quantities.selectTypeOption.indicator.performance": "Indicateur de performance",
  "commons.quantities.selectTypeOption.indicator.process": "Indicateur de conduite",
  "commons.quantities.selectTypeOption.measure": "Mesure",
  "commons.quantities.selectTypeOption.observable": "Observable",
  "commons.quantities.selectTypeOption.others": "Aucun",
  "commons.quantities.selectTypeOption.simulationInput": "Entrée du modèle",
  "commons.quantities.selectTypeOption.simulationOutput": "Sortie du modèle",
  "commons.quantities.threshold.atleast.one.label": "Au moins un seuil",
  "commons.quantities.threshold.none.label": "Pas de seuil",
  "commons.select.manage_columns": "Choix des colonnes",
  "commons.title.plant_state": "État de la centrale",
  "commons.tooltip.research": "Projet de recherche",
  "commons.tooltip.warning.selected_analysis_not_filter_compliant":
    "L'analyse sélectionnée ne respecte pas les filtres d'analyse",
  "commons.tooltip.warning.selected_analysis_not_filter_compliant.view_activation_filters":
    "Voir les filtres d'analyse",
  "commons.tooltip.warning.selected_analysis_not_filter_compliant_with_details": (
    compliance: Compliance,
    link: ReactNode,
    active: boolean,
  ) => {
    const filters = Object.entries(compliance).reduce<[string, React.JSX.Element][]>(
      (acc, [quantityName, { unit, missing, min, max, maxStd }]) => {
        if (missing === true) acc.push([quantityName, <>valeur manquante</>]);

        if (min) {
          const fValue = formatNumber(min.actual, { precision: 2 });
          const fThreshold = formatNumber(min.threshold, { unit });
          acc.push([
            quantityName,
            <>
              sous la valeur seuil ({fValue} &lt; {fThreshold})
            </>,
          ]);
        }

        if (max) {
          const fValue = formatNumber(max.actual, { precision: 2 });
          const fThreshold = formatNumber(max.threshold, { unit });
          acc.push([
            quantityName,
            <>
              au dessus de la valeur seuil ({fValue} &gt; {fThreshold})
            </>,
          ]);
        }

        if (maxStd)
          if (maxStd.actual === null) {
            acc.push([quantityName, <>valeur d’écart-type manquante</>]);
          } else {
            const fStd = formatNumber(maxStd.actual, { precision: 2 });
            const fStdThreshold = formatNumber(maxStd.threshold, { unit });
            acc.push([
              quantityName,
              <>
                au dessus de l’écart-type seuil ({fStd} &gt; {fStdThreshold})
              </>,
            ]);
          }

        return acc;
      },
      [],
    );
    filters.sort((a, b) => a[0].localeCompare(b[0]));

    return (
      <>
        {active ? "L’analyse sélectionnée" : "Le point sélectionné"} ne respecte pas{" "}
        {filters.length > 1 ? `ces ${filters.length} filtres` : "ce filtre"} :
        <br />
        <ul>
          {filters.map(([name, desc]) => (
            <li key={`${name}/${desc}`}>
              - <b>{name}</b> : {desc}
            </li>
          ))}
        </ul>
        <br />
        {link}
      </>
    );
  },
  "commun.tooltip.label_value"({
    label,
    value,
  }: {
    label: React.ReactNode;
    value: React.ReactNode;
  }) {
    return (
      <>
        {label} : {value}
      </>
    );
  },
  customization: "Application",
  "customization.algorithm.tooltip.text":
    "Cette version sera attribuée à tous les nouveaux paramétrages lors de leur création. La modification de la version par défaut n'aura aucun impact sur les paramétrages existants.",
  "customization.application.link.modal.subtitle":
    "Renseignez ci-dessous les textes et l’URL que vous souhaitez voir apparaître dans le menu",
  "customization.application.link.modal.title": "Ajouter un lien",
  "customization.demo_environment.action": "Action",
  "customization.demo_environment.checking_refresh_status":
    "Vérification du rafraîchissement en cours…",
  "customization.demo_environment.keep_demos_updated": "Sources à garder à jour automatiquement",
  "customization.demo_environment.keep_demos_updated.tooltip":
    "L’application décale automatiquement chaque jour les données des sources sélectionnées à la date actuelle.",
  "customization.demo_environment.keep_demos_updated_form.toast":
    "Les sources à garder recentes ont été mises à jour.",
  "customization.demo_environment.refresh_in_progress":
    "Un rafraîchissement est en cours. Les actions seront disponibles lorsqu’il sera terminé.",
  "customization.demo_environment.refresh_source": "Rafraîchir cette source maintenant",
  "customization.demo_environment.refresh_sources.button":
    "Rafraîchir toutes les sources maintenant",
  "customization.demo_environment.refresh_sources.toast":
    "Le rafraîchissement des sources est terminé.",
  "customization.demo_environment.remove_source": "Retirer la source",
  "customization.demo_environment.search_sources": "Rechercher des sources à ajouter",
  "customization.demo_environment.selected_sources": ({ count }: { count: number }) =>
    `${count} source${count === 1 ? "" : "s"} sélectionnée${count === 1 ? "" : "s"} pour un rafraîchissement quotidien`,
  "customization.legals.modal_prompt": "Contenu de la modale RGPD",
  "customization.modal.edit.translation.subtitle":
    "Renseignez ci-dessous les termes que vous souhaitez voir apparaître dans l’interface",
  "customization.modal.edit.translation.title": "Modifier la terminologie employée",
  "customization.modalForm.impactedElement": "Élément impacté",
  "customization.modalForm.input.english": "Terme en anglais",
  "customization.modalForm.input.error.cannot_be_empty": "Ce champ ne peut pas être vide",
  "customization.modalForm.input.error.text_to_long": "Ce champ est limité à 100 caractères",
  "customization.modalForm.input.french": "Terme en français",
  "customization.section.ab.db_prisma_replace_enabled":
    "Activer la base de données PRISMA pour certaines fonctionnalités node-pg. Voir le test db de grafana pour comparer les temps d'exécution.",
  "customization.section.advanced_features.title": "Fonctionnalités avancées",
  "customization.section.cac.dispatch.button": ({ count }: { count: number }) =>
    `Dispatcher le CAC pour ${count} projet${count > 1 ? "s" : ""}`,
  "customization.section.cac.dispatch.label": "dispatch",
  "customization.section.cac.empty_state": "Aucun projet ne correspond à votre recherche.",
  "customization.section.cac.filter.show_production_projects": "Afficher les projets de production",
  "customization.section.cac.filter.show_research_projects": "Afficher les projets de recherche",
  "customization.section.cac.global_status": ({
    running,
    waiting,
    success,
    failed,
  }: {
    running: number;
    waiting: number;
    success: number;
    failed: number;
  }) => {
    const done = success + failed;
    return `${running} en cours, ${waiting} en attente, ${done} terminé${
      done > 1 ? "s" : ""
    } (${success} succès, ${failed} échec${failed > 1 ? "s" : ""})`;
  },
  "customization.section.cac.launch.button": ({ count }: { count: number }) =>
    `Lancer le CAC pour ${count} projet${count > 1 ? "s" : ""}`,
  "customization.section.cac.launch.label": "lancement",
  "customization.section.cac.modal.title": "Sélection des projets sur lesquels dispatcher le CAC",
  "customization.section.cac.no_operation_launched": "Aucune opération lancée",
  "customization.section.cac.open_modal_button": "Dispatcher le CAC pour plusieurs projets…",
  "customization.section.cac.project_status.failed": "Échec",
  "customization.section.cac.project_status.running": "En cours…",
  "customization.section.cac.project_status.selected": "Sélectionné",
  "customization.section.cac.project_status.success": "Terminé",
  "customization.section.cac.project_status.waiting": "En attente",
  "customization.section.cac.reset.button": ({ count }: { count: number }) =>
    `Réinitialiser le CAC pour ${count} projet${count > 1 ? "s" : ""}`,
  "customization.section.cac.reset.label": "réinitialisation",
  "customization.section.cac.running_hint": ({ operation }: { operation: string }) =>
    `${capitalize(operation)} en cours : la sélection est verrouillée jusqu’à la fin.`,
  "customization.section.cac.search.aria_label": "Rechercher un projet",
  "customization.section.cac.search.placeholder": "Rechercher par nom…",
  "customization.section.cac.select_all": ({ total }: { total: number }) =>
    `Tout sélectionner (${total})`,
  "customization.section.cac.title": "Gestion du CAC",
  "customization.section.data_display.switch.display_financial_impact_dashboard.text":
    "Activer l’affichage de l’estimation de l’impact financier (Tableaux de bord)",
  "customization.section.data_display.switch.display_publication_markers.text":
    "Afficher les marqueurs de publication par défaut",
  "customization.section.data_display.switch.group_quantities_by_system.text":
    "Grouper les grandeurs par système (pages Données et Symptômes)",
  "customization.section.data_display.switch.hide_symptoms_global_impact.text":
    "Masquer les symptômes en vue Impact global (page Défaillances)",
  "customization.section.data_display.title": "Affichage des données",
  "customization.section.data_display.tooltips.display_publication_markers.text":
    "Dans tous les graphes, affiche par défaut le marqueur indiquant si l’analyse est publiée",
  "customization.section.data_display.tooltips.group_quantities_by_system.text":
    "En pages Données et Symptômes, présente les grandeurs dans le sélecteur à gauche groupés selon leur système",
  "customization.section.data_display.tooltips.hide_symptoms_global_impact.text":
    "En page Défaillances, masque les données liées aux symptômes et symptômes résiduels lorsque le graphe est en vue Impact global",
  "customization.section.data_import.switch.active_context_file_format.text":
    "Activer l’utilisation du format de fichier .context",
  "customization.section.data_import.switch.active_data_import.text":
    "Activer l'import de données depuis un projet",
  "customization.section.data_import.switch.source_import_conflict.text":
    "Activer le remplacement de données en cas de conflit",
  "customization.section.data_import.title": "Import de données",
  "customization.section.data_import.tooltips.active_data_import.text":
    "Fait apparaître un bouton permettant d'importer des données depuis la page d'un projet",
  "customization.section.data_import.tooltips.source_import_conflict.text":
    "Si activé, il sera possible de remplacer les snapshots sur lesquels un conflit est identifié lors de l’import de données",
  "customization.section.digital_twin.title": "Jumeau numérique",
  "customization.section.event.enable_manual_events_dev_mode":
    "Activer la création et l’affichage des événements manuels",
  "customization.section.events.admin.switch": "Activer les événements (Admin uniquement)",
  "customization.section.events.disable_event_group_constraint":
    "Ignorer le groupe d'événement lors de l'agrégation des événements de défaut",
  "customization.section.events.grouping":
    "Activer le groupement et le dégroupement des événements",
  "customization.section.events.priority_toggle": "Rendre inactive la priorité Moyen",
  "customization.section.events.switch": "Activer les événements",
  "customization.section.failure_analysis.migration.description":
    "Tester le fonctionnement de la migration des défaillances manuelles vers les événements manuels (mode DRY-RUN)",
  "customization.section.failure_analysis.migration.dry_run.blocked": ({
    blocked,
    total,
  }: {
    blocked: number;
    total: number;
  }) =>
    `Aperçu de la migration téléchargé. ${blocked} détections sur ${total} sont bloquées et nécessitent une attention particulière.`,
  "customization.section.failure_analysis.migration.dry_run.button":
    "Télécharger l'aperçu de la migration",
  "customization.section.failure_analysis.migration.dry_run.loading": "Génération de l'aperçu...",
  "customization.section.failure_analysis.migration.dry_run.success": ({
    total,
  }: {
    total: number;
  }) => `Aperçu de la migration téléchargé. Les ${total} détections sont prêts à migrer.`,
  "customization.section.failure_analysis.migration.execute.button": "Lancer la migration",
  "customization.section.failure_analysis.migration.execute.close": "Fermer",
  "customization.section.failure_analysis.migration.execute.description":
    "Migrer les défaillances manuelles vers les événements manuels (mode RUN).",
  "customization.section.failure_analysis.migration.execute.error":
    "Une erreur s'est produite lors de la migration.",
  "customization.section.failure_analysis.migration.execute.failure_reason": "Raison de l'échec",
  "customization.section.failure_analysis.migration.execute.failures_title": "Échecs de migration",
  "customization.section.failure_analysis.migration.execute.loading":
    "Exécution de la migration...",
  "customization.section.failure_analysis.migration.execute.project_id": "ID du projet",
  "customization.section.failure_analysis.migration.execute.show_more": "Afficher plus",
  "customization.section.failure_analysis.migration.execute.success": ({
    migratedProjectsCount,
    migratableProjectsCount,
    failedProjectMigrationsCount,
    blockedCount,
  }: {
    migratedProjectsCount: number;
    migratableProjectsCount: number;
    failedProjectMigrationsCount: number;
    blockedCount: number;
  }) =>
    `${migratedProjectsCount} projets migrés sur ${migratableProjectsCount} potentiels avec ${failedProjectMigrationsCount} migrations échouées. Il y a ${blockedCount} qui restent bloqués pour la migration.`,
  "customization.section.failure_analysis.migration.rollback.description":
    "Supprimer les événements manuels créés lors de la migration pour les projets sélectionnés.",
  "customization.section.failure_analysis.migration.rollback.empty_state":
    "Aucun projet n'est disponible pour un rollback.",
  "customization.section.failure_analysis.migration.rollback.global_status": ({
    deleting,
    failed,
    success,
    waiting,
  }: {
    deleting: number;
    failed: number;
    success: number;
    waiting: number;
  }) =>
    `Rollback en cours ${deleting}. En attente ${waiting}. Succès ${success}. Échecs ${failed}.`,
  "customization.section.failure_analysis.migration.rollback.load_error":
    "Impossible de charger les projets à rollback.",
  "customization.section.failure_analysis.migration.rollback.loading":
    "Chargement des projets à rollback...",
  "customization.section.failure_analysis.migration.rollback.modal.title": "Gestion du rollback",
  "customization.section.failure_analysis.migration.rollback.open_modal_button":
    "Ouvrir le gestionnaire de rollback des événements manuels…",
  "customization.section.failure_analysis.migration.rollback.project_events": ({
    count,
  }: {
    count: number;
  }) => `${count} événements manuels`,
  "customization.section.failure_analysis.migration.rollback.project_status.deleting":
    "Rollback en cours",
  "customization.section.failure_analysis.migration.rollback.project_status.failed": "Échec",
  "customization.section.failure_analysis.migration.rollback.project_status.selected":
    "Sélectionné",
  "customization.section.failure_analysis.migration.rollback.project_status.success":
    "Rollback effectué",
  "customization.section.failure_analysis.migration.rollback.project_status.waiting": "En attente",
  "customization.section.failure_analysis.migration.rollback.rollback.button": ({
    count,
  }: {
    count: number;
  }) => `Rollback de ${count} projets`,
  "customization.section.failure_analysis.migration.rollback.running_hint":
    "Le rollback est en cours. Gardez cette fenêtre ouverte jusqu'à la fin.",
  "customization.section.failure_analysis.migration.rollback.search.aria_label":
    "Rechercher des projets",
  "customization.section.failure_analysis.migration.rollback.search.placeholder":
    "Rechercher des projets",
  "customization.section.failure_analysis.migration.rollback.select_all": ({
    total,
  }: {
    total: number;
  }) => `Tout sélectionner (${total})`,
  "customization.section.failure_analysis.migration.rollback.title": "Gestion du rollback",
  "customization.section.failure_analysis.migration.short_title":
    "Migration des défaillances manuelles",
  "customization.section.failure_analysis.migration.title":
    "Migration des défaillances manuelles vers les événements manuels",
  "customization.section.failure_analysis.switch.allow_dymosim_fmu_t0.text":
    "Autoriser l’usage des fichiers dymosim et FMU_T0",
  "customization.section.failure_analysis.switch.restart_diagnostics_manually.text":
    "Relancer automatiquement les diagnostics à la mise en fausse détection d'une défaillance",
  "customization.section.failure_analysis.title": "Analyse de défaillances",
  "customization.section.failure_analysis.tooltips.allow_dymosim_fmu_t0.text":
    "Permet d’utiliser des fichiers dymosim et FMU_T0_INJECTION comme modèles dans le paramètrage",
  "customization.section.failure_analysis.tooltips.restart_diagnostics_manually.text":
    "Si l'option n'est pas activée, l'utilisateur pourra relancer le diagnostic manuellement.",
  "customization.section.failure_grouping.switch": "Activer le regroupement des défaillances",
  "customization.section.fleet_view.repairability_matrix": "Activer la matrice de réparabilité",
  "customization.section.fleet_view.switch": "Activer la vue parc",
  "customization.section.formula_versions_enabled":
    "Activer la création et l’affichage des versions de formule",
  "customization.section.hide_legacy_summary_page": "Masquer l’ancienne page de synthèse",
  "customization.section.legals.title": "Gestion des mentions légales",
  "customization.section.link.title": "Personnalisation des liens",
  "customization.section.link.title.button.add": "Ajouter un lien",
  "customization.section.quantities.new_navigation":
    "Activer la nouvelle navigation au sein d’un projet",
  "customization.section.quantity.versioning.name":
    "Autoriser la création de plusieurs versions pour une grandeur",
  "customization.section.setup_mode": "Activer le mode setup",
  "customization.section.show_legacy_summary_badge":
    "Afficher le badge « Obsolète » sur l’onglet Synthèse",
  "customization.section.terminology.title": "Personnalisation de la terminologie employée",
  "customization.settings.update.success":
    "Les paramètres de l'application ont été modifiés avec succès",
  "customization.table.translation.average": "Moyennes",
  "customization.table.translation.dashboard": "Tableau de bord",
  "customization.table.translation.dashboards": "Tableaux de bord",
  "customization.table.translation.en": "Terme en anglais",
  "customization.table.translation.explained": "Expliquées",
  "customization.table.translation.fr": "Terme en français",
  "customization.table.translation.key": "Nom",
  "customization.table.translation.loss": "Perte",
  "customization.table.translation.losses": "Pertes",
  "customization.table.translation.reparability_0": "Niveau de réparabilité 0",
  "customization.table.translation.reparability_1": "Niveau de réparabilité 1",
  "customization.table.translation.reparability_2": "Niveau de réparabilité 2",
  "customization.table.translation.type": "Type du projet",
  "customization.table.translation.unexplained": "Non expliquées",
  "customization.title": "Administration de l’application",
  "dataSource.import.analysis.section.title": "Analyse des données à importer",
  "dataSource.import.conflict.check.confirmation_force_conflict.text":
    "Confirmer la suppression et le remplacement",
  "dataSource.import.conflict.confirmation_force_conflict.text":
    "En confirmant, les anciennes valeurs seront supprimées et remplacées par les nouvelles.",
  "dataSource.import.error.fileTypeNotSupported": (type: string) =>
    `Le type de fichier ${type} n’est pas supporté. Merci de charger un fichier .json, .csv ou .context.`,
  "dataSource.import.error.multipleFileTypes": (types: string[]) =>
    `Plusieurs types de fichiers ont été détectés (${types
      .join(", ")
      .replace("", "context")}). Veuillez charger un seul type de fichier à la fois.`,
  "dataSource.import.error.unexpected_source_id":
    "Le nom de la source contient des caractères non autorisé ([a-zA-Z0-9_]{0,49})",
  "dataSource.import.extraInfo.section.title": "Informations complémentaires pour l’import",
  "dataSource.import.info.timezoneDefault": ({ projectTimeZone }: { projectTimeZone: boolean }) =>
    `Le fuseau horaire des données à importer n’a pas été détecté dans le fichier. Veuillez confirmer le fuseau horaire à utiliser (par défaut, celui de votre ${
      projectTimeZone ? "projet" : "navigateur"
    }).`,
  "dataSource.import.parsing.error.conflicting_sensor_value": () =>
    "Les valeurs d'un capteur pour un snapshot ne correspondent pas",
  "dataSource.import.parsing.error.conflicting_source_id": () =>
    "Les sources des snapshots ne correspondent pas",
  "dataSource.import.parsing.error.duplicated_snapshot_date":
    "2 snapshots ne peuvent se référer à la même date",
  "dataSource.import.parsing.error.empty_data_extraction": "Aucune donnée extraite du fichier",
  "dataSource.import.parsing.error.invalid_group_too_long": () =>
    "Le groupe ne respecte pas le format attendu (100 caractères maximum composés de caractères alphanumériques et de _)",
  "dataSource.import.parsing.error.invalid_json": "Le format du fichier JSON est incorrect",
  "dataSource.import.parsing.error.invalid_label_too_long": () =>
    "Le label ne respecte pas le format attendu (100 caractères maximum composés de caractères alphanumériques et de _)",
  "dataSource.import.parsing.error.invalid_sensor_value": () =>
    "La valeur d'un capteur d'un snapshot est invalide",
  "dataSource.import.parsing.error.invalid_sensor_value_missing_mean": () =>
    "La valeur moyenne d'un capteur d'un snapshot est manquante",
  "dataSource.import.parsing.error.invalid_source_id": () =>
    "Le champ sourceId ne doit pas dépasser 100 caractères",
  "dataSource.import.parsing.error.malformed_json": "La syntaxe du fichier JSON est incorrecte",
  "dataSource.import.parsing.error.missing_snapshot_date": () =>
    `La date du snapshot est manquante`,
  "dataSource.import.parsing.error.missing_source_id":
    "La sourceId est obligatoire pour le format .json",
  "dataSource.import.parsing.error.snapshot_metadata_missmatch": () =>
    "Les métadonnées du snapshot ne correspondent pas",
  "dataSource.import.project.bypass_source_id_missmatch": (currentSourceId: string) =>
    `Poursuivre l’import dans la source ${currentSourceId}`,
  "dataSource.import.project.source_id_missmatch": ({
    expectedSourceId,
    currentSourceId,
  }: {
    expectedSourceId: string;
    currentSourceId: string;
  }) =>
    `La source du fichier (${currentSourceId}) n’est pas la même que celle du projet en cours (${expectedSourceId}).`,
  "dataSource.import.projectInfo": ({
    projectCount,
    sourceId,
    productionProjectNames,
    researchProjectNames,
    inProject,
  }: {
    projectCount: number;
    sourceId: string;
    productionProjectNames: string[];
    researchProjectNames: string[];
    inProject: boolean;
  }) => {
    if (
      (productionProjectNames.length === 1 || researchProjectNames.length === 1) &&
      projectCount === 1
    ) {
      return (
        <>
          Les données importées se déverseront dans {inProject ? "l'autre" : "le"} projet{" "}
          {productionProjectNames.length === 1
            ? "de production"
            : researchProjectNames.length === 1
              ? "de recherche"
              : ""}{" "}
          <strong>{productionProjectNames[0] || researchProjectNames[0]}</strong> associé à la
          source <strong>{sourceId}</strong>.
        </>
      );
    }

    if (productionProjectNames.length === 1 && projectCount > 1) {
      return (
        <>
          Les données importées se déverseront dans{" "}
          <strong>
            les {projectCount} {inProject ? " autres" : ""} projets
          </strong>{" "}
          associés à la source de données <strong>{sourceId}</strong>, dont le projet{" "}
          {productionProjectNames[0]}.
        </>
      );
    }

    return (
      <>
        Les données importées se déverseront dans{" "}
        {projectCount === 1 ? (
          <>
            <strong>{inProject ? "l'autre" : "le"} projet</strong> associé
          </>
        ) : (
          <>
            <strong>
              les {projectCount} {inProject ? " autres" : ""} projets
            </strong>{" "}
            associés
          </>
        )}{" "}
        à la source de données <strong>{sourceId}</strong>
        {productionProjectNames?.length > 1 ? (
          <>
            , dont les projets de productions <strong>{productionProjectNames.join(", ")}</strong>.
          </>
        ) : productionProjectNames?.length === 1 ? (
          <>
            , dont le projet de production <strong>{productionProjectNames.join(", ")}</strong>.
          </>
        ) : (
          <>.</>
        )}
      </>
    );
  },
  "dataSource.import.projectInfo.details.summary": (researchProjectCount: number) => (
    <>Projets de recherche impactés ({researchProjectCount})</>
  ),
  "dataSource.import.snapshot.conflict.export": "Détails des points (.json)",
  "dataSource.import.snapshotInfo": ({
    sensorCount,
    snapshotCount,
    startDate,
    endDate,
  }: {
    sensorCount: number;
    snapshotCount: number;
    startDate: string;
    endDate: string;
  }) => {
    if (snapshotCount === 1) {
      return (
        <>
          Les données identifiées comportent <strong>{sensorCount} capteurs</strong> et
          correspondent au <strong>{startDate}</strong> :
        </>
      );
    }

    return (
      <>
        Les données identifiées comportent <strong>{sensorCount} capteurs</strong> et correspondent{" "}
        {startDate === endDate ? (
          <>au {startDate}</>
        ) : (
          <>
            à la période{" "}
            <strong>
              {startDate} - {endDate}
            </strong>
          </>
        )}
        {snapshotCount > 1 && (
          <>
            {" "}
            (<strong>{snapshotCount}</strong> points de données)
          </>
        )}{" "}
        :
      </>
    );
  },
  "dataSource.import.snapshotInfo.details.text.conflictSnapshot": (conflictSnapshotCount: number) =>
    `Points existants, en conflit : ${conflictSnapshotCount}.`,
  "dataSource.import.snapshotInfo.details.text.conflictSnapshot_info":
    "Les valeurs diffèrent de celles déjà présentes dans la source de données.",
  "dataSource.import.snapshotInfo.details.text.ignoredSnapshot": (ignoredSnapshot: number) =>
    `Points existants, non impactés : ${ignoredSnapshot}`,
  "dataSource.import.snapshotInfo.details.text.mergedSnapshot": (mergedSnapshotCount: number) =>
    `Points existants, complétés : ${mergedSnapshotCount}`,
  "dataSource.import.snapshotInfo.details.text.newSnapshot": (newSnapshotCount: number) =>
    `Nouveaux points : ${newSnapshotCount}`,
  "dataSource.import.snapshotInfo.empty":
    "L’import n'est pas nécessaire car toutes les données sont déjà présentes.",
  "dataSource.import.source_id_input_name": "Source",
  "dataSource.import.warning.expected_source_id":
    "Aucune source n'a été détectée dans le fichier. Veuillez renseigner la source ci-dessous.",
  "dateSource.import.timezone.selectLabel": "Fuseau horaire",
  "datetime.fractional": ({
    type,
    years,
    days,
    months,
  }: {
    type: "date" | "duration";
    years: number;
    days: number;
    months: number;
  }) => {
    const parts: string[] = [];
    if (years > 0) {
      parts.push(`${years} an${years > 1 ? "s" : ""}`);
    }
    if (months > 0) {
      parts.push(`${months} mois`);
    }
    if (days > 0 || (years === 0 && months === 0 && parts.length === 0)) {
      parts.push(`${days} jour${days > 1 ? "s" : ""}`);
    }
    if (parts.length === 0) {
      return "0 jour";
    }
    let formattedString =
      parts.length > 1 ? parts.slice(0, 2).join(type === "date" ? " et " : ", ") : parts[0];
    if (type === "date") {
      formattedString = "Il y a " + formattedString;
    }
    return formattedString;
  },
  deleted: "Supprimé",
  "diagnosis.chart.evolution.axis.y": (
    performanceIndicatorName: string,
    performanceIndicatorUnit: string | null,
  ) =>
    `Impact sur ${performanceIndicatorName} ${performanceIndicatorUnit ? `(${performanceIndicatorUnit})` : ""}`,
  "diagnosis.chart.flag_selected_quantities": "Sélection synchronisée",
  "diagnosis.chart.flag_selected_quantities.tooltip.description":
    "Permet de sélectionner des grandeurs (Ctrl + clic) et de synchroniser cette sélection dans les deux sens entre Défaillances, Données et Symptômes. Les grandeurs sélectionnées apparaissent en premier.",
  "diagnosis.chart.legend.tooltip.is_manual_failure": "Défaillance non modélisée",
  "diagnosis.chart.show_deactivated_observables": "Afficher désactivées",
  "diagnosis.chart.show_deactivated_observables.tooltip":
    "Les observables désactivées apparaîtront avec un nom grisé.",
  "diagnosis.chart.tooltip.amplitude": "Amplitude",
  "diagnosis.chart.tooltip.button.confirmed": "Confirmée",
  "diagnosis.chart.tooltip.button.false_detection": "Fausse détection",
  "diagnosis.chart.tooltip.button.suspected": "Suspectée",
  "diagnosis.chart.tooltip.button.to_be_analysed": "À analyser",
  "diagnosis.chart.tooltip.description.diagnosticError": (statusInfo: string) =>
    `Erreur lors de la réalisation du diagnostic\n${statusInfo}\n...\nCliquez sur le marqueur rouge pour copier l'intégralité du message d'erreur`,
  "diagnosis.chart.tooltip.description.no_failure_suspected":
    "Aucune défaillance détectée lors du diagnostic",
  "diagnosis.chart.tooltip.description.uncompletedDiagnostic": "Diagnostic non réalisé",
  "diagnosis.chart.tooltip.event.link": "Voir",
  "diagnosis.chart.tooltip.event.title": "Événement :",
  "diagnosis.chart.tooltip.losses"({ qoi }: { qoi: string }) {
    return `Impact sur ${qoi}`;
  },
  "diagnosis.chart.tooltip.message.incompatible_status": (falseDetectionCount: number) =>
    `La sélection contient ${falseDetectionCount} élément${
      falseDetectionCount > 1 ? "s" : ""
    } en “Fausse détection”, la modification groupée de statut est impossible.`,
  "diagnosis.chart.tooltip.message.relaunch_diagnostic":
    "Aucun diagnostic n'a été relancé suite à cette mise en fausse détection",
  "diagnosis.chart.tooltip.selectedPoints": ({ count }: { count: number }) =>
    count === 1 ? `${count} défaillance sélectionnée` : `${count} défaillances sélectionnées`,
  "diagnosis.chart.tooltip.status.action.reintegrate_in_diagnosis": "Réintégrer dans le diagnostic",
  "diagnosis.chart.tooltip.status.action.relaunch_diagnostic": "Relancer le diagnostic",
  "diagnosis.chart.tooltip.subtitle_link.update_reparability": "Modifier la réparabilité:",
  "diagnosis.chart.tooltip.subtitle_link.update_status": "Mettre à jour le statut:",
  "diagnosis.charts.symptoms.axis.y": (
    performanceIndicatorName: string,
    performanceIndicatorUnit: string | null,
  ) => `Impact sur ${performanceIndicatorName} (${performanceIndicatorUnit})`,
  "diagnosis.charts.symptoms.legend.confirmed": "Confirmée",
  "diagnosis.charts.symptoms.legend.suspected": "Suspectée",
  "diagnosis.charts.symptoms.legend.symptoms": "Symptôme",
  "diagnosis.charts.symptoms.legend.tooltip.confirmed":
    "Défaillance confirmée manuellement par un utilisateur",
  "diagnosis.charts.symptoms.legend.tooltip.no_symptom":
    "Valeur indisponible pour cette observable",
  "diagnosis.charts.symptoms.legend.tooltip.sigma":
    "Zone en dehors de laquelle un écart sur une observable est considéré comme un symptôme",
  "diagnosis.charts.symptoms.legend.tooltip.suspected":
    "Elle doit être confirmée pour apparaître en page de synthèse",
  "diagnosis.charts.symptoms.no_symptoms": "Symptôme non calculable",
  "diagnosis.charts.symptoms.observable.axis.y": "Impact normalisé",
  "diagnosis.charts.symptoms.title.obs": "Observables",
  "diagnosis.charts.symptoms.title.obs.many": ({ count }: { count: number }) =>
    `Observables (${count})`,
  "diagnosis.charts.symptoms.title.obs.one": "Observable",
  "diagnosis.charts.symptoms.title.qoi": "Indicateurs de performance",
  "diagnosis.charts.symptoms.title.qoi.many": ({ count }: { count: number }) =>
    `Indicateurs de performance (${count})`,
  "diagnosis.charts.symptoms.title.qoi.one": "Indicateur de performance",
  "diagnosis.header.dropdown.manage_manual_failure": "Gérer les défaillances non modélisées",
  "diagnosis.legend.empty_period": "Pas de détection sur période affichée",
  "diagnosis.legend.group_faults.button": "Gérer les groupes de défaillances",
  "diagnosis.legend.group_faults.column.failure": "Défaillances",
  "diagnosis.legend.group_faults.column.group": "Groupe",
  "diagnosis.legend.group_faults.empty": "Aucune défaillance disponible",
  "diagnosis.legend.group_faults.modal.subtitle":
    "Créez des groupes en sélectionnant les défaillances détectables dans ce projet.",
  "diagnosis.legend.group_faults.modal.title": "Gérer les groupes de défaillances",
  "diagnosis.legend.group_faults.search.empty": "Aucune défaillance correspondante",
  "diagnosis.legend.group_faults.search.placeholder": "Rechercher une défaillance",
  "diagnosis.legend.group_faults.select_placeholder": "Tapez pour rechercher ou créer un groupe",
  "diagnosis.legend.group_faults.toast.saved": "Groupes de défaillances mis à jour",
  "diagnosis.legend.group_faults.ungrouped": "Non groupé",
  "diagnosis.legend.marker.to_be_analysed": "À analyser",
  "diagnosis.legend.no_failure": "Pas de défaillance",
  "diagnosis.overview.amplitude": "Amplitude",
  "diagnosis.overview.card.title": "Évolution des défaillances",
  "diagnosis.overview.header.option": ({ name }: { name: string }) => `${name}`,
  "diagnosis.overview.header.option.global": "Impact global",
  "diagnosis.overview.header.option.global.description":
    "Cet affichage est celui qui représente le mieux l'état du système dans sa globalité.\nL'axe des ordonnées quantifie l'écart global du système à son état attendu, en se basant sur l'ensemble des observables.\nL'affichage des symptômes correspond à la norme des écarts et celui des défaillances à la norme des impacts.",
  "diagnosis.overview.header.option.qoi": "Impact sur les indicateurs de performance",
  "diagnosis.overview.header.option.qoi.description": ({
    translations,
  }: {
    translations: {
      FR: {
        LOSSES: string;
        UNEXPLAINED: string;
      };
      EN: {
        LOSSES: string;
        UNEXPLAINED: string;
      };
    };
  }) =>
    `Cet affichage offre une vue ciblée sur un indicateur particulier, avec les ${lowercase(
      translations.FR.LOSSES,
    )} internes et ${lowercase(
      translations.FR.UNEXPLAINED,
    )} associées. Cette vue permet également d'afficher les autres défaillances du projet et leur impact sur ces indicateurs.`,
  "diagnosis.overview.header.option.title": "Mode d'affichage",
  "diagnosis.overview.legend.tooltip.confidence_area":
    "Zone dans laquelle le symptôme est considéré comme non significatif",
  "diagnosis.overview.legend.tooltip.internal_gap.global":
    "Norme de l'écart global du système à son état attendu, intégrant l'ensemble des observables",
  "diagnosis.overview.legend.tooltip.internal_losses":
    "Écart entre la valeur attendue et la valeur mesurée de l'indicateur de performance",
  "diagnosis.overview.legend.tooltip.no_failure":
    "Aucune défaillance n'a été diagnostiquée par le Metroscope",
  "diagnosis.overview.legend.tooltip.unexplained_gap.global":
    "Norme de l'écart résiduel entre le système et son état attendu, après prise en compte de l'impact des défaillances confirmées",
  "diagnosis.overview.legend.tooltip.unexplained_gap.integrate_suspected":
    "Intégrer l’impact des défaillances non confirmées",
  "diagnosis.overview.legend.tooltip.unexplained_losses":
    "Écart résiduel entre la valeur attendue et la valeur mesurée de l'indicateur de performance, après prise en compte de l'impact des défaillances",
  "diagnosis.symptoms.legend.tooltip.quantity_gap":
    "Écart entre la valeur mesurée et la valeur attendue de la grandeur",
  "diagnosis.symptoms.legend.tooltip.quantity_unexplained_gap":
    "Écart résiduel entre la valeur mesurée et la valeur attendue de la grandeur, après prise en compte de l'impact des défaillances",
  "diagnosis.title.observables.order.alphabetical": "A-Z",
  "diagnosis.title.observables.order.alphabetical.description": "Ordre alphabétique",
  "diagnosis.title.observables.order.decreasing.impact": "Décroissant",
  "diagnosis.title.observables.order.decreasing.impact.description":
    "Les observables sont ordonnées par taille des barres visibles décroissante.\nPar exemple, si on n'affiche que les symptômes résiduels à l'aide de la légende, on verra en premier les grandeurs dont les symptômes sont le moins bien expliqués.",
  "doNotbreakBetweenGroups.text": "Afficher les inter-cycles",
  "errors.backend_error": "Réponse du serveur inattendue. Rechargez la page ou réessayez.",
  "errors.conflict": "Un élément avec le même identifiant existe déjà.",
  "errors.field_cannot_be_empty": "Ce champ doit être rempli",
  "errors.field_must_be_less_than_1_year": "L’intervalle total doit être inférieur à 1 an.",
  "errors.field_must_be_number": "Ce champ doit contenir un nombre",
  "errors.field_must_be_strictly_positive_number":
    "Ce champ doit contenir un nombre strictement positif",
  "errors.forbidden":
    "Vous n'avez pas accès à l'une des ressources demandée, veuillez contacter votre administrateur.",
  "errors.network": "Pas de connexion, êtes-vous en ligne ?",
  "errors.not_found": "Ressource introuvable.",
  "errors.profile_not_found":
    "Vous êtes connecté, mais vous ne disposez pas encore des droits d'accès à cette application. Veuillez contacter support@metroscope.tech afin que nous puissions vous accorder les autorisations nécessaires.",
  "errors.should_be_positive_integer": "Doit être un entier positif",
  "errors.tmp_backend_error": "Une erreur temporaire est survenue. Veuillez réessayer plus tard.",
  "errors.unauthorized": "Vous êtes déconnecté.",
  "errors.unexpected_error": "Une erreur est survenue. Rechargez la page ou réessayez.",
  "failureTooltip.equipments": "Équipements",
  "failureTooltip.notInSetup":
    "Cette défaillance n'est pas dans le paramétrage de l'analyse sélectionnée",
  "failureTooltip.suspectedElements": "Éléments suspectés",
  "failureTooltip.system": "Système",
  "fallback.reconnect": "Se reconnecter",
  "fallback.retry": "Réessayer",
  "fault.chart.evolution.tooltip.status.confirmed": "Confirmée",
  "fault.chart.evolution.tooltip.status.rejected": "Fausse détection",
  "fault.chart.evolution.tooltip.status.suspected": "Suspectée",
  "fault.chart.evolution.tooltip.status.to_be_analysed": "À analyser",
  "flags_legend.allData_activeAnalysis": "Point de données utilisé pour une analyse",
  "flags_legend.allData_activeAnalysis.description": "Point de données utilisé pour une analyse",
  "flags_legend.fault_flag.multiple_statuses":
    "Plusieurs statuts de défaillance détectés sur la période",
  "flags_legend.intercycles": "Inter-cycle",
  "flags_legend.intercycles.description": "Inter-cycle : période entre deux cycles",
  "flags_legend.noDataIntervals": "Donnée non disponible",
  "flags_legend.noDataIntervals.description": "Donnée non disponible",
  "flags_legend.out_of_fault_event_period": "En dehors de l’événement",
  "flags_legend.out_of_fault_event_period.description": "En dehors de la période de l’événement",
  "flags_legend.published_analysis_in_reporting": "Incluse dans le reporting",
  "flags_legend.published_analysis_in_reporting.description": "Incluse dans le reporting",
  "flags_legend.published_analysis_not_in_reporting": "Publiée et non incluse dans le reporting",
  "flags_legend.published_analysis_not_in_reporting.description":
    "Publiée et non incluse dans le reporting",
  "flags_legend.quantity_flag.invalid_value": "Valeur invalidée",
  "flags_legend.quantity_flag.invalid_value.description":
    "Valeur invalidée : des informations complémentaires sont disponibles au survol du point",
  "flags_legend.quantity_flag.invalid_value.median":
    "Au moins une valeur invalidée sur la période (non prise en compte dans l’agrégation)",
  "flags_legend.quantity_flag.missing_value": "Valeur manquante",
  "flags_legend.quantity_flag.missing_value.description":
    "Valeur manquante : des informations complémentaires sont disponibles au survol du point",
  "flags_legend.quantity_flag.missing_value_and_invalid_value": "Valeur manquante et invalidée",
  "flags_legend.quantity_flag.missing_value_and_invalid_value.description":
    "Valeur manquante et invalidée : des informations complémentaires sont disponibles au survol du point",
  "flags_legend.sigma": "Zone de confiance",
  "flags_legend.sigma.amplitude.description":
    "Zone en dehors de laquelle une mesure est considérée comme symptomatique",
  "flags_legend.sigma.gap.description":
    "Zone en dehors de laquelle un écart est considéré comme un symptôme",
  "flags_legend.simulation_diagnosis_error": "Échec de la simulation/du diagnostic",
  "flags_legend.simulation_diagnosis_error.description":
    "Échec de la simulation/du diagnostic : des informations complémentaires sont disponibles au survol du point",
  "flags_legend.synchronizing": "Synchronisation en cours",
  "flags_legend.unavailable_simulation": "Simulation indisponible",
  "flags_legend.unavailable_simulation.description":
    "Simulation non réalisable : des informations complémentaires sont disponibles au survol du point",
  "flags_legend.uncomplete_simulation_diagnosis": "Simulation / Diagnostic non réalisé",
  "flags_legend.uncomplete_simulation_diagnosis.description":
    "Simulation / Diagnostic en cours ou à lancer",
  "form.no_value": "Aucune valeur",
  "hamburger.activity.title": "Fil d'activité",
  "hamburger.group_title.user_name": ({ name }: { name: string }) => `${name}`,
  "hamburger.help.title": "Aide",
  "hamburger.legals.title": "Mentions légales",
  "hamburger.option.datasources": "Sources de données",
  "hamburger.option.jupyterHub": "JupyterHub",
  "hamburger.option.permalink": "Copier le lien vers la page",
  "hamburger.option.permalink.toast.success": "Lien copié dans le presse-papiers !",
  "hamburger.option.print": "Export de la page en .png",
  "hamburger.option.print.toast.error": "Erreur lors de l'export",
  "hamburger.option.print.toast.in_progress": "Export en cours",
  "hamburger.option.print.toast.success": "Export terminé",
  "hamburger.setup_mode": "Mode paramétrage",
  "hamburger.setup_mode.tooltip":
    "Affiche les éléments d'interface utilisateur de paramétrage selon vos droits sur chaque projet.",
  "hamburger.user.logout": "Se déconnecter",
  "hamburger.user.profile": "Profil",
  "hamburger.user.projects_count": ({ count }: { count: number }) =>
    count === 1 ? `${count} projet` : `${count} projets`,
  "hamburger.user_guide.tooltip": "Guide utilisateur",
  "header.help_center.documentation": "Documentation",
  "header.help_center.releases.latest_badge": "Dernière",
  "header.help_center.releases.new_badge": "Nouveau",
  "header.help_center.releases.title": "Nouveautés",
  "header.help_center.releases.unread_aria": "Nouvelle note de version disponible",
  "header.help_center.support.copy_aria": "Copier l'e-mail du support",
  "header.help_center.support.copy_error": "Impossible de copier l'e-mail du support",
  "header.help_center.support.copy_success": "E-mail du support copié dans le presse-papiers",
  "header.help_center.support.message":
    "Besoin d'aide supplémentaire ou envie de nous faire part d'un problème rencontré ? Envoyez un e-mail à :",
  "header.help_center.title": "Centre d'aide",
  "manualFailure.modal.form.add.line": "Nouvelle défaillance non modélisée",
  "manualFailure.modal.form.everyAnalyses": ({ analysesCount }: { analysesCount: number }) =>
    analysesCount === 0
      ? "Aucune"
      : analysesCount === 1
        ? "1 analyse"
        : `${analysesCount} analyses`,
  "manualFailure.modal.form.header.linkedAnalyses": "Analyses associées",
  "manualFailure.modal.form.header.name": "Nom",
  "manualFailure.modal.form.header.qoi": ({ name, unit }: { name: string; unit: string | null }) =>
    `Impact sur ${name}` + (unit === null ? "" : ` (${unit})`),
  "manualFailure.modal.form.header.reparability": "Réparabilité",
  "manualFailure.modal.submit.success": (impactedAnalyses: number) =>
    `Les défaillances non modélisées ont été modifiées sur ${
      impactedAnalyses === 1 ? "1 analyse" : impactedAnalyses + " analyses"
    }.`,
  "manualFailure.modal.subtitle": ({
    analysesCount,
    lowerBoundary,
    upperBoundary,
  }: {
    analysesCount: number;
    lowerBoundary: string;
    upperBoundary: string;
  }) => {
    if (analysesCount === 1) {
      return `Bilan sur la période sélectionnée (analyse du ${lowerBoundary})`;
    }
    return `Bilan sur la période sélectionnée (${analysesCount} analyses du ${lowerBoundary} au ${upperBoundary})`;
  },
  "manualFailure.modal.title": "Gestion des défaillances non modélisées",
  "measures.chart.analysis.modal.activate.description":
    "Voulez-vous créer une analyse sur la base de ce point de données ?",
  "measures.chart.analysis.modal.activate.success": "L'analyse a été ajoutée.",
  "measures.chart.analysis.modal.activate.warning.description":
    "Les données à cette date ne respectent pas les filtres d'activation.",
  "measures.chart.analysis.modal.deactivate.description": () => (
    <>
      La suppression de l&apos;analyse entraîne la disparition des résultats de simulation et de
      diagnostic. Les données resteront présentes dans la source de données.
      <br />
      <br />
      Souhaitez-vous confirmer la suppression de l&apos;analyse ?
    </>
  ),
  "measures.chart.analysis.modal.deactivate.success": "L'analyse a été supprimée.",
  "measures.chart.analysis.modal.toggle.analysis": ({ isActive }: { isActive: boolean }) =>
    isActive ? <>Supprimer</> : <>Analyser</>,
  "measures.chart.analysis.modal.toggle.analysis.title": ({ active }: { active: boolean }) =>
    active ? <>Supprimer l&apos;analyse</> : <>Analyser le point</>,
  "measures.chart.legend.analysisFilter.description": "Non-respect des filtres d’analyses",
  "measures.chart.legend.analysisFilter.title": "Non-respect des filtres d’analyses",
  "measures.chart.legend.invalid.inherited.name": "Invalidée par héritage",
  "measures.chart.legend.invalid_auto.name": "Invalidée automatiquement",
  "measures.chart.legend.invalid_manual.name": "Invalidée manuellement",
  "measures.chart.legend.modal.invalid_auto.description": () => (
    <>
      Mesure invalidée en raison de :
      <br />• non-respect des critères métrologiques sur elle-même,
      <br />• ou via un filtre croisé,
      <br />• ou en raison d&apos;une mesure invalidée sur un parent de la grandeur
    </>
  ),
  "measures.chart.legend.modal.invalid_manual.description":
    "Mesure invalidée suite à une action utilisateur",
  "measures.chart.legend.modal.valid_auto.description":
    "Mesure respectant les différents critères métrologiques",
  "measures.chart.legend.modal.valid_manual.description":
    "Mesure invalidée automatiquement et validée via une action utilisateur",
  "measures.chart.legend.no_validation.name": "Inactive",
  "measures.chart.legend.std.max.description": "Écart-type maximum pour une valeur non calculée",
  "measures.chart.legend.std.max.title": "Écart-type maximum",
  "measures.chart.legend.valid_auto.name": "Validée automatiquement",
  "measures.chart.legend.valid_manual.name": "Validée manuellement",
  "measures.chart.legend.value.minmax.description": "Seuils de la grandeur sélectionnée",
  "measures.chart.legend.value.minmax.title": "Seuils de grandeur",
  "measures.chart.not.used.analysis": "Point de données non utilisé",
  "measures.chart.not.visible.analysis": "Point de données non visible - zoomez",
  "measures.chart.selected.analyses": ({
    showInactiveAnalyses,
    snapshotCount,
    activeAnalysisCount,
  }: {
    showInactiveAnalyses: boolean;
    snapshotCount: number;
    activeAnalysisCount: number;
  }) => {
    if (showInactiveAnalyses) {
      return `${snapshotCount} point${snapshotCount > 1 ? "s" : ""} de données (${activeAnalysisCount} analyse${
        activeAnalysisCount > 1 ? "s" : ""
      })`;
    } else {
      return `${activeAnalysisCount} analyse${activeAnalysisCount > 1 ? "s" : ""} (${snapshotCount} point${
        snapshotCount > 1 ? "s" : ""
      } de données)`;
    }
  },
  "measures.chart.selected.analyses.activate.button.disabled.tooltip":
    "Aucun point de données disponible pour ajouter des analyses sur la période sélectionnée.",
  "measures.chart.selected.analyses.activate.button.name": "Ajouter des analyses",
  "measures.chart.selected.analyses.activate.deactivate.button": "Créer / Supprimer des analyses",
  "measures.chart.selected.analyses.deactivate.button.name": "Supprimer des analyses",
  "measures.chart.selected.analyses.deactivate.button.name.disabled.no_active_analysis":
    "Aucune analyse à supprimer sur la période sélectionnée",
  "measures.chart.selected.analyses.modal.activate.buttons.submit": "Ajouter",
  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small":
    "La fréquence saisie doit être supérieure à la fréquence d'analyse actuelle.",
  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small_for_selected_period":
    "La durée entre 2 points doit être plus petite que la durée de la période sélectionnée.",
  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small_to_create_analyses":
    "La fréquence choisie ne conduit pas à la création d'aucune analyse étant donné les analyses existantes. Veuillez modifier la fréquence.",
  "measures.chart.selected.analyses.modal.activate.form.extra_data_points.checkbox.label": ({
    filtersLink,
  }: {
    filtersLink: React.ReactNode;
  }) => <>Autoriser l&lsquo;utilisation des points ne respectant pas les {filtersLink}</>,
  "measures.chart.selected.analyses.modal.activate.form.extra_data_points.label": (
    points: number,
  ) => {
    const pointsText = points > 1 ? `${points} points supplémentaires` : "1 point supplémentaire";

    return `${pointsText}, ne respectant pas les filtres d'analyse du projet, pourraient devenir des analyses. Pour utiliser également ces points, cocher la case ci-dessous.`;
  },
  "measures.chart.selected.analyses.modal.activate.form.labels.days": "Jours",
  "measures.chart.selected.analyses.modal.activate.form.labels.hours": "Heures",
  "measures.chart.selected.analyses.modal.activate.form.labels.minutes": "Minutes",
  "measures.chart.selected.analyses.modal.activate.information.indicate_interval":
    "Veuillez indiquer la fréquence d'analyse souhaitée :",
  "measures.chart.selected.analyses.modal.activate.labels.analyses_to_create": "Analyses à créer :",
  "measures.chart.selected.analyses.modal.activate.labels.target_frequency.information":
    "Pour sélectionner les analyses à créer, la période sélectionnée est découpée en intervalles réguliers sur la base de la fréquence cible. Pour chaque intervalle sans analyse existante, une analyse sera créée lorsque possible.",
  "measures.chart.selected.analyses.modal.activate.labels.target_frequency.title":
    "Fréquence d'analyse cible",
  "measures.chart.selected.analyses.modal.activate.title": "Ajouter des analyses",
  "measures.chart.selected.analyses.modal.checkbox.filters.link": "filtres d'analyse",
  "measures.chart.selected.analyses.modal.checkbox.label"({
    filtersLink,
    invalidFiltersCount,
  }: {
    invalidFiltersCount: number;
    filtersLink: React.ReactNode;
  }) {
    return (
      <>
        Permettre d’utiliser des points ne respectant pas les {filtersLink} du projet (
        {invalidFiltersCount} point{invalidFiltersCount > 1 ? "s" : ""})
      </>
    );
  },
  "measures.chart.selected.analyses.modal.deactivate.form.errors.erase_count": ({
    count,
  }: {
    count: number;
  }) => `Le nombre d'analyses à supprimer ne peut pas dépasser ${count}.`,
  "measures.chart.selected.analyses.modal.deactivate.form.labels.erase_count":
    "Nombre d'analyses à supprimer",
  "measures.chart.selected.analyses.modal.deactivate.information.analyses": "Analyses :",
  "measures.chart.selected.analyses.modal.deactivate.information.current_analysis_frequency":
    "Fréquence d'analyse actuelle :",
  "measures.chart.selected.analyses.modal.deactivate.information.data_points":
    "parmi les points de données :",
  "measures.chart.selected.analyses.modal.deactivate.information.erase_count":
    "Veuillez indiquer combien d'analyses vous souhaitez supprimer :",
  "measures.chart.selected.analyses.modal.deactivate.information.remaining_analyses":
    "Analyses restantes :",
  "measures.chart.selected.analyses.modal.deactivate.information.resulting_frequency":
    "Fréquence d'analyse résultante :",
  "measures.chart.selected.analyses.modal.deactivate.information.selected_period":
    "Période sélectionnée :",
  "measures.chart.selected.analyses.modal.deactivate.title": "Supprimer des analyses",
  "measures.chart.selected.analyses.select.range.success": (analysesDifferenceCount: number) => {
    if (analysesDifferenceCount > 0) {
      return analysesDifferenceCount > 1
        ? analysesDifferenceCount + " analyses ont été créées"
        : "1 analyse a été créée";
    }
    return analysesDifferenceCount < -1
      ? Math.abs(analysesDifferenceCount) + " analyses ont été supprimées"
      : "1 analyse a été supprimée";
  },
  "measures.chart.selected.analysis.activated.date": "Point de données utilisé pour une analyse",
  "measures.chart.selected.analysis.deactivated.date": "Pas d'analyse à ce moment",
  "measures.chart.title": "Données",
  "measures.chart.title.switch": "Tous les points",
  "measures.chart.toggle.byStd": "Écart-type",
  "measures.chart.toggle.byStd.description":
    "Evolution des écart-types de la ou des grandeurs sélectionnées.\n L’écart-type correspond à une mesure de la dispersion des données brutes au sein de l'échantillon ayant conduit au calcul du point (par exemple période de 20 minutes pour 1 point toutes les 20 minutes).\nL’écart-type n’est disponible que pour certaines grandeurs, lorsque importé avec les données, et n’est pas disponible pour les grandeurs calculées.",
  "measures.chart.toggle.byValue": "Valeur",
  "measures.chart.toggle.byValue.description":
    "Evolution des valeurs mesurées ou calculées de la ou des grandeurs sélectionnées.\nLa valeur correspond à une agrégation des données brutes sur l’échantillon ayant conduit au calcul du point (par exemple période de 20 minutes pour 1 point toutes les 20 minutes).",
  "measures.chart.tooltip.button.invalidate": "Invalider",
  "measures.chart.tooltip.button.validate": "Valider",
  "measures.chart.tooltip.description.invalid": "Valeur invalidée",
  "measures.chart.tooltip.description.invalid_auto": "Invalidée en raison de sa propre valeur",
  "measures.chart.tooltip.description.invalid_auto_cross_filter": (quantityName: string) =>
    `Invalidée par un filtre croisé avec la grandeur ${quantityName}`,
  "measures.chart.tooltip.description.invalid_herited": "Invalidée en raison d’un parent invalidé",
  "measures.chart.tooltip.description.invalid_manual": "Invalidée manuellement",
  "measures.chart.tooltip.description.missingValue": "Valeur manquante",
  "measures.chart.tooltip.description.noModelOutput": "Écart non calculable",
  "measures.chart.tooltip.description.valid_auto": "Validée automatiquement",
  "measures.chart.tooltip.description.valid_manual": "Validée manuellement",
  "measures.chart.tooltip.description.valid_partial_inputs": (
    inputCount: number,
    missingSmartArgsCount: number,
  ) =>
    `Les valeurs manquantes ou invalides ont été ignorées (${missingSmartArgsCount}/${inputCount}) dans le calcul de la formule`,
  "measures.chart.tooltip.quantity_field.label": "Valeur mesurée",
  "measures.chart.tooltip.selectedPoints": ({ count }: { count: number }) =>
    count === 1 ? `${count} point sélectionné` : `${count} points sélectionnés`,
  "measures.chart.tooltip.std": "Écart-type",
  "measures.empty.title": "La page de données est vide",
  "measuresSymptoms.blankstate.no_quantities_found": "Aucune grandeur trouvée",
  "measuresSymptoms.collapse.noGroup": "Sans système",
  "measuresSymptoms.collapse.title": "grandeurs",
  "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction": "Supprimer",
  "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction.disabled.locked":
    "Impossible de supprimer car la formule est verrouillée",
  "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction.disabled.usedInSetup": (
    setupName: string,
  ) =>
    `Impossible de supprimer car la grandeur (ou une grandeur qui en dépend) est utilisée comme entrée ou sortie du modèle dans le paramétrage ${setupName}`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.editFormulaAction": "Modifier",
  "measuresSymptoms.quantitiy.quantityIdentityCard.equipments": "Équipements :",
  "measuresSymptoms.quantitiy.quantityIdentityCard.formule": "Formule :",
  "measuresSymptoms.quantitiy.quantityIdentityCard.lockedFormula": "Verrouillée",
  "measuresSymptoms.quantitiy.quantityIdentityCard.offset": "Offset",
  "measuresSymptoms.quantitiy.quantityIdentityCard.process": ({ order }: { order: number }) =>
    `Indicateur de conduite (ordre ${order})`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.qoi": ({ order }: { order: number }) =>
    `Indicateur de performance (ordre ${order})`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.observable": "Observable",
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.setupName": (setupName: ReactNode) => (
    <>dans le paramétrage {setupName}</>
  ),
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.simulationInput": "Entrée du modèle",
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.simulationOutput": "Sortie du modèle",
  "measuresSymptoms.quantitiy.quantityIdentityCard.roles": `Rôles : `,
  "measuresSymptoms.quantitiy.quantityIdentityCard.sensorTag": "Tag capteur :",
  "measuresSymptoms.quantitiy.quantityIdentityCard.sigma": "Sigma",
  "measuresSymptoms.quantitiy.quantityIdentityCard.system": "Système :",
  "measuresSymptoms.quantitiy.quantityIdentityCard.type": "Nature :",
  "measuresSymptoms.quantitiy.quantityIdentityCard.unit": "Unité :",
  "measuresSymptoms.quantityIdentityCard.type.calculated_value": "Calcul",
  "measuresSymptoms.quantityIdentityCard.type.measure": "Mesure",
  "measuresSymptoms.sectionType.noGrouping": "non groupées",
  "measuresSymptoms.sectionType.system": "par Système",
  "measuresSymptoms.sectionType.type": "par Nature",
  "measuresSymptoms.sectionType.unit": "par Unité",
  "measuresSymptoms.toast.analyses_invalidation": ({ count }: { count: number }) =>
    count === 1 ? `${count} mesure a été invalidée` : `${count} mesures ont été invalidées`,
  "measuresSymptoms.toast.analyses_validation": ({ count }: { count: number }) =>
    count === 1 ? `${count} mesure a été validée` : `${count} mesures ont été validées`,
  "measures_symptoms.axis_grouped_by_unit": "Grouper les axes par unité",
  "millions.sampling.description": ({
    samplingLevel,
    humanDuration,
  }: {
    samplingLevel: number;
    humanDuration: string;
  }) => {
    const one = humanDuration.startsWith("1");
    if (one) humanDuration = humanDuration.slice(2);
    if (humanDuration.endsWith("s")) humanDuration = humanDuration.slice(0, -1);
    const feminin = humanDuration.endsWith("e");
    return `Échantillonnage des données affichées : un point ${feminin ? "toutes" : "tous"} les ${
      humanDuration + "s"
    } (${samplingLevel}%)`;
  },
  "modal.delete": "Supprimer",
  "modal.duplicate": "Dupliquer",
  "modalForm.button.name.add": "Ajouter",
  "modalForm.button.name.create": "Créer",
  "modalForm.button.name.delete": "Supprimer",
  "modalForm.button.name.duplicate": "Dupliquer",
  "modalForm.button.name.edit": "Éditer",
  "modalForm.button.name.import": "Importer",
  "modalForm.button.name.modify": "Modifier",
  "modalForm.button.name.pause": "Mettre en pause",
  "modalForm.button.name.remove": "Retirer",
  "modalForm.button.name.save": "Enregistrer",
  "modalForm.button.name.update": "Modifier",
  "period.selector.1m.long": "Le dernier mois",
  "period.selector.1y": "1A",
  "period.selector.1y.long": "L'année précédente",
  "period.selector.2m.long": "Les 2 derniers mois",
  "period.selector.2y.long": "Les 2 dernières années",
  "period.selector.30d.long": "Les 30 derniers jours",
  "period.selector.3m": "3M",
  "period.selector.3m.long": "Les 3 derniers mois",
  "period.selector.3w": "3S",
  "period.selector.3w.long": "Les 3 dernières semaines",
  "period.selector.6m.long": "Les 6 derniers mois",
  "period.selector.7d.long": "Les 7 derniers jours",
  "period.selector.all": "Tout",
  "period.selector.all.long": "Toute la période",
  "period.selector.custom": "Personnalisé",
  "period.selector.default": "Par défaut",
  "period.selector.description": () => (
    <>
      Ces options facilitent la navigation au sein du graphe, via des fenêtres temporelles
      prédéfinies :
      <br /> - Cycle en cours : dernière période de fonctionnement du système
      <br /> - 3S : 3 semaines précédentes
      <br /> - 3M : 3 mois précédents
      <br /> - 1A : année précédente
      <br /> - Tout : ensemble des points
    </>
  ),
  "period.selector.last_group": "Le cycle en cours",
  "period.selector.last_two_groups": "Les 2 derniers cycles",
  "project.analysis.toggle.display_invalid_points.text": "Afficher les points invalidés",
  "project.analysisActive.count_exceeded": ({
    count,
    countLimit,
  }: {
    count: number;
    countLimit: number;
  }) => (
    <>
      <strong>Limite atteinte :</strong> le projet a trop d&apos;analyses actives,{" "}
      {formatNumber(count)}/{formatNumber(countLimit)} max. Supprimez des analyses.
    </>
  ),
  "project.button.copying_project_analyses": "Recopier la sélection depuis un autre projet",
  "project.button.navbar.actions.launch_selection_of_analyses": "Lancer la création d’analyses",
  "project.chart.analysis.outside_event_period": "(en dehors de la plage de l’événement)",
  "project.chart.legend.fault_event.switch.display_all_faults.label":
    "Afficher toutes les défaillances",
  "project.charts.action.menu.modal.analysis_export.title": "Exporter les données d'analyse",
  "project.charts.confirm_analysis_publish_modal.subtitle": ({
    count,
    action,
  }: {
    count: number;
    action: "publish" | "unpublish";
  }): string =>
    count === 1
      ? `${
          action === "publish"
            ? "Souhaitez-vous confirmer la publication de l'analyse suivante ?"
            : "Souhaitez-vous confirmer la dépublication de l'analyse suivante ?"
        }`
      : `${
          action === "publish"
            ? "Souhaitez-vous confirmer la publication des analyses suivantes ?"
            : "Souhaitez-vous confirmer la dépublication des analyses suivantes ?"
        }`,
  "project.charts.confirm_analysis_publish_modal.title": ({
    action,
  }: {
    action: "publish" | "unpublish";
  }): string =>
    `${action === "publish" ? "Confirmer la publication" : "Confirmer la dépublication"}`,
  "project.charts.modal.form.analysis_export.button.name": "Exporter",
  "project.charts.modal.form.analysis_export.timezone": (tz: string) =>
    `Toutes les dates seront présentées dans le fuseau horaire du projet (${tz}).`,
  "project.charts.publish_analysis.success": ({
    count,
    action,
  }: {
    count: number;
    action: "publish" | "unpublish";
  }): string =>
    count === 1
      ? `1 analyse a été ${action === "publish" ? "publiée" : "dépubliée"}.`
      : `${count} analyses ont été ${action === "publish" ? "publiées" : "dépubliées"}.`,
  "project.charts.title.no_data_points_selected": ({
    showInactiveAnalyses,
  }: {
    showInactiveAnalyses: boolean;
  }): string =>
    showInactiveAnalyses ? "Aucun point de données sélectionné" : "Aucune analyse sélectionnée",
  "project.charts.title.selected_points": ({
    selectedPointsText,
  }: {
    selectedPointsText: React.ReactNode;
  }) => <>Sélection : {selectedPointsText}</>,
  "project.charts.title.selected_points.one.details": ({
    showInactiveAnalyses,
  }: {
    showInactiveAnalyses: boolean;
  }): string => {
    if (showInactiveAnalyses) return "1 point de données";
    else return "1 analyse";
  },
  "project.charts.tooltip.description.manual_failure": "Défaillance non modélisée",
  "project.comment.subtitle.deleted_analysis": "Analyse supprimée",
  "project.dashboard.actions.create": (translations: CustomTranslations) =>
    `Créer un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.actions.create.error": (translations: CustomTranslations) =>
    `Une erreur est survenue lors de la création du ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.actions.create.success": (translations: CustomTranslations) =>
    `Le ${translations.FR.DASHBOARD.toLowerCase()} a été créé`,
  "project.dashboard.editor.gui.no_system": "Aucun système",
  "project.dashboard.editor.gui.section.annotation_settings.show_description":
    "Afficher la description",
  "project.dashboard.editor.gui.section.annotation_settings.show_label": "Afficher le libellé",
  "project.dashboard.editor.priority_events.title": "Événement prioritaire",
  "project.dashboard.status.published": `Partagé`,
  "project.dashboard.status.unpublished": `Privé`,
  "project.dashboards.empty.subtitle": (translations: CustomTranslations) =>
    `Créez un ${translations.FR.DASHBOARD.toLowerCase()} pour afficher vos données`,
  "project.dashboards.empty.title": (translations: CustomTranslations) =>
    `Aucun ${translations.FR.DASHBOARD.toLowerCase()} disponible`,
  "project.dashboards.import.button.text": (translations: CustomTranslations) =>
    `Importer un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboards.navigation": (translations: CustomTranslations) => translations.FR.DASHBOARDS,
  "project.dashboards.table.column.author": "Auteur",
  "project.dashboards.table.column.published": "Statut",
  "project.dashboards.table.search.placeholder": "Rechercher",
  "project.diagnosis.action_menu.option.redirection_events_page.description":
    "via la création d’un événement manuel",
  "project.diagnosis.action_menu.option.redirection_events_page.title":
    "Créer une défaillance non modélisée",
  "project.diagnosis.symptom.histogram.tooltip.fault_type.gap": "Écart",
  "project.diagnosis.symptom.histogram.tooltip.fault_type.impact": "Impact",
  "project.display_filter.no_data.description":
    "Essayez de modifier les filtres ou la période sélectionnée.",
  "project.display_filter.no_data.reset": "Réinitialiser les filtres par défaut",
  "project.display_filter.no_data.title":
    "Aucune analyse ne correspond aux filtres d’affichage sélectionnés pour cette période.",
  "project.display_filter.select.footer.title": "Voir les paramètres des filtres",
  "project.display_filter.select.title": (count: number, on: number) =>
    `Filtres d’affichage${count > 0 ? ` (${count}/${on})` : ""}`,
  "project.display_filter.select.ungrouped.title": ({
    filterName,
    selectedCount,
    totalCount,
  }: {
    filterName?: string;
    selectedCount: number;
    totalCount: number;
  }) => {
    const remainingCount = totalCount - selectedCount;
    return filterName === undefined
      ? "Autres filtres"
      : remainingCount > 0
        ? `Autres filtres : ${filterName}, +${remainingCount}`
        : `Autres filtres : ${filterName}`;
  },
  "project.display_filter.select.ungrouped_vanilla.title": "Filtres",
  "project.display_filter.toggle.title": "Filtres d'affichage",
  "project.display_settings_saved": "Les modifications de couleurs ont bien été prises en compte",
  "project.document.title.dashboards": "Dashboards",
  "project.document.title.diagnosis": "Défaillances",
  "project.document.title.fault_events": "Événements",
  "project.document.title.measures": "Données",
  "project.document.title.new_navigation.exploration": "Exploration",
  "project.document.title.new_navigation.summary": "Accueil",
  "project.document.title.settings": "Réglages",
  "project.document.title.simulation": "Simulation",
  "project.document.title.summary": "Synthèse",
  "project.document.title.summary.legacy_badge": "Obsolète",
  "project.document.title.symptoms": "Symptômes",
  "project.fault_event.live.widget.average_losses.link": "Accéder au graphe",
  "project.filters.modal.import.toast.success": ({ count }: { count: number }) =>
    count === 1 ? `${count} filtre a été défini` : `${count} filtres ont été définis`,
  "project.filters.table.definitions.column.max_std": "Écart-type max",
  "project.filters.table.definitions.column.max_value": "Max",
  "project.filters.table.definitions.column.min_value": "Min",
  "project.filters.table.definitions.column.quantity": "Grandeur",
  "project.filters.title": "Filtres d'analyse",
  "project.header.actions.computations.computations_running": ({ count }: { count: number }) =>
    count === 1 ? `Calcul en cours (${count})` : `Calculs en cours (${count})`,
  "project.header.actions.computations.start.success": "Calculs lancés",
  "project.header.actions.computations.start_computations": ({ count }: { count: number }) =>
    count === 1 ? "Lancer le calcul" : `Lancer les calculs (${count})`,
  "project.header.actions.computations.stop.success": "Les calculs ont été arrêtés",
  "project.header.actions.computations.stop_computations": ({
    count,
  }: {
    count: number;
  }): string => (count === 1 ? `Stopper le calcul` : `Stopper les calculs (${count})`),
  "project.header.dropdown.delete": "Supprimer le projet",
  "project.header.dropdown.duplicate": "Dupliquer le projet",
  "project.header.dropdown.edit_project": "Éditer le projet",
  "project.header.dropdown.promote_production": "Mettre en production",
  "project.header.dropdown.relegate_to_research": "Passer en recherche",
  "project.header.toast.promoted_production": "Projet passé en production",
  "project.header.toast.relegated_to_research": "Projet passé en recherche",
  "project.measure.legend_right.active_setup.name": (setupName: string) =>
    `Paramétrage actif : ${setupName}`,
  "project.measures.title": "Données",
  "project.modal.analysis_export.option.format.csv_helium": ".csv - Version Helium",
  "project.modal.analysis_export.option.format.csv_lithium": ".csv - Version Lithium",
  "project.modal.analysis_export.radio_button.title.all_analyses": (nbActiveAnalyses: number) =>
    `Ensemble des analyses du projet [${nbActiveAnalyses}]`,
  "project.modal.analysis_export.radio_button.title.selected_analyses": (
    nbSelectedAnalyses: number,
  ) =>
    `${
      nbSelectedAnalyses > 1 ? "Analyses sélectionnées" : "Analyse sélectionnée"
    } uniquement [${nbSelectedAnalyses}]`,
  "project.modal.analysis_export.title.data_to_export": "Données à exporter",
  "project.modal.analysis_export.title.exported_analyses": "Liste des analyses exportées :",
  "project.modal.copying_project_analyses.subtitle":
    "Les analyses seront créées sur la base de celles présentes dans le projet sélectionné, lié à la même source de données",
  "project.modal.subtitle.launch_selection_of_analyses":
    "Les analyses seront créées sur la base des critères renseignés",
  "project.modal.title.launch_selection_of_analyses": "Lancer la création d’analyses",
  "project.modal_form.action_confirmation.description": (projectName: string) => (
    <>
      Vous vous apprêtez à relancer la sélection des analyses pour le projet <b>{projectName}</b>.
      Veuillez écrire ci-dessous le nom du projet pour confirmer cette action.
    </>
  ),
  "project.modal_form.additionnal_message.max_analyses_count": `Le nombre maximal d’analyses par projet est de ${MAX_ANALYSIS_COUNT}.`,
  "project.modal_form.button.activation_filters.with_filter": (filterCount: number) => (
    <>{filterCount > 1 ? "Voir les filtres" : "Voir le filtre"}</>
  ),
  "project.modal_form.button.activation_filters.without_filter": "Définir des filtres",
  "project.modal_form.copying_project_analyses.additionnal_message.info": (nbAnalyses: number) =>
    `Nombre d'analyses du projet: ${nbAnalyses}`,
  "project.modal_form.copying_project_analyses.ok_button_name": "Recopier la sélection",
  "project.modal_form.copying_project_analyses.select_label.project": "Projet",
  "project.modal_form.date_range.input.date_end": "Date de fin",
  "project.modal_form.date_range.input.date_start": "Date de début",
  "project.modal_form.estimated_analyses.text": (nbAnalyses: number, nbActiveAnalyses: number) => (
    <>
      Nombre maximum d’analyses estimé à : <b>{nbAnalyses}</b>
      {nbActiveAnalyses
        ? ` (actuellement, ${nbActiveAnalyses} analyse${
            nbActiveAnalyses > 1 ? "s sont présentes" : " est présente"
          } sur ce projet)`
        : ""}
    </>
  ),
  "project.modal_form.estimated_analyses.time_criteria.text":
    "sur la base des critères temporels, avant application des filtres ",
  "project.modal_form.launch_analysis_selection.input.end_date.error.cannot_be_empty":
    "La date de fin d'application ne peut pas être vide",
  "project.modal_form.launch_analysis_selection.input.end_date.error.date_should_be_greater_to_start_date":
    "La date de fin d'application doit être postérieure à la date de début d'application",
  "project.modal_form.launch_analysis_selection.input.end_date.error.date_should_be_inferior_or_equal_to_source":
    "La date de fin doit être inférieur ou égale à la date de fin de la source",
  "project.modal_form.launch_analysis_selection.input.start_date.error.cannot_be_empty":
    "La date de début d'application ne peut pas être vide",
  "project.modal_form.launch_analysis_selection.input.start_date.error.date_should_be_greater_or_equal_to_source":
    "La date de début doit être supérieure ou égale à la date de début de la source",
  "project.modal_form.launch_analysis_selection.input.start_date.error.date_should_be_inferior_to_end_date":
    "La date de début d'application doit être antérieure à la date de fin d'application",
  "project.modal_form.ok_button_name.launch_analysis_selection": "Lancer la création",
  "project.modal_form.ok_button_name.restart_analysis_selection": "Relancer la sélection",
  "project.modal_form.restart_analysis_selection.additionnal_message.warningt.loss_of_action":
    "Cette action entraînera la perte de toutes les actions réalisées liées aux analyses (notamment création et suppression manuelle, résultats de calcul, confirmation de défaillances, etc.).",
  "project.modal_form.switch.switch.activate_live_stream":
    "Activer le temps réel en utilisant ces critères",
  "project.modal_form.text.activation_filters.without_filter": "Aucun",
  "project.modal_form.text.date_range":
    "Les analyses seront créées entre les dates indiquées. Aucun analyse ne sera créée en dehors de cette plage.",
  "project.modal_form.text.time_criteria": "Critères temporels",
  "project.modal_form.title.action_confirmation": "Confirmation de l’action",
  "project.modal_form.title.activation_filters": "Filtres au niveau du projet",
  "project.modal_form.title.date_range": "Plage de date",
  "project.modal_form.title.live_data_stream": "Flux temps réel",
  "project.modal_form.title.minimum_interval": "Intervalle moyen",
  "project.modal_form.title.overlay.activation_filters":
    "Ensemble des filtres d'analyse devant être respectés pour qu’une analyse puisse être créée",
  "project.modal_form.title.overlay.date_range":
    "Restriction de la plage temporelle sur laquelle les analyses seront créées",
  "project.modal_form.title.overlay.live_data_stream":
    "Le flux en temps réel déclenche la création d’analyses lorsque de nouvelles données sont insérées dans la source associée au projet",
  "project.modal_form.title.overlay.minimum_interval":
    "Intervalle moyen entre deux analyses successives",
  "project.not_found.title": "Projet introuvable",
  "project.permalink.loaded": "Lien chargé",
  "project.plant_state.block.priority_event.button_link": "Accéder à tous les événements",
  "project.plant_state.block.priority_event.no_fault_event": () => (
    <>
      Il n&apos;y a actuellement aucun événement en priorité <strong>Haut</strong> ou{" "}
      <strong>Moyen</strong> détecté sur la période affichée.
    </>
  ),
  "project.plant_state.block.priority_event.table.priority": "Priorité",
  "project.plant_state.block.priority_event.title": "Événements prioritaires",
  "project.quantities.add.title": ({ newSensorCount }: { newSensorCount: number }) =>
    `Ajouter les tags capteur non utilisés (${newSensorCount})`,
  "project.quantities.button.newQuantities": ({ newSensorCount }: { newSensorCount: number }) =>
    `Tags capteur non utilisés (${newSensorCount})`,
  "project.quantities.delete.additionnal_message.sensor": () => (
    <>
      Les données associées n’apparaîtront plus dans le projet mais seront conservées dans la source
      de données.
    </>
  ),
  "project.quantities.delete.body": (quantityName: string) => (
    <>
      Souhaitez-vous confirmer la suppression de la grandeur <b>{quantityName}</b> ?
    </>
  ),
  "project.quantities.delete.forbidden": (quantityName: string, setupNames: string[]) => (
    <>
      La grandeur <b>{quantityName}</b> ne peut pas être supprimée car elle (ou une grandeur qui en
      dépend) est utilisée comme entrée ou sortie du modèle dans{" "}
      {setupNames.length > 1 ? "les paramétrages" : "le paramétrage"}{" "}
      <b>{printListStrings(setupNames, "et")}</b>.
    </>
  ),
  "project.quantities.delete.formula.body.quantities": ({
    quantityName,
    impactedQuantitiesCount,
  }: {
    quantityName: string;
    impactedQuantitiesCount: number;
  }) => (
    <>
      Cette suppression entraînera par ailleurs la suppression{" "}
      <b>
        {impactedQuantitiesCount > 1
          ? "des " + impactedQuantitiesCount + " grandeurs"
          : "de la grandeur"}
      </b>{" "}
      dépendant de <b>{quantityName}</b> dans {impactedQuantitiesCount > 1 ? "leur" : "son"} calcul
      (directement ou via des grandeurs intermédiaires), et des valeurs associées.
    </>
  ),
  "project.quantities.delete.formula.details.body": ({
    impactedCount,
  }: {
    impactedCount: number;
  }) => (
    <>
      Détails{" "}
      {impactedCount > 1
        ? "des " + impactedCount + " grandeurs supprimées "
        : "de la grandeur supprimée "}
      :
    </>
  ),
  "project.quantities.delete.formula.details.label.level1": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>et {dependencyCount > 1 ? "grandeurs" : "grandeur"} en dépendant :</>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.delete.formula.details.label.level2": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>
          (+ {dependencyCount} {dependencyCount > 1 ? "grandeurs" : "grandeur"} en dépendant)
        </>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.delete.formula.nota": ({ quantityName }: { quantityName: string }) => (
    <>
      Note : pour des raisons de lisibilité, seules les grandeurs dépendantes à un niveau 1 (direct)
      ou 2 de <b>{quantityName}</b> sont affichées
    </>
  ),
  "project.quantities.delete.title": "Supprimer la grandeur",
  "project.quantities.edit.body": (
    quantityName: string,
    isFormula: boolean,
    hasImpactOnMetrology: boolean,
  ) => {
    if (isFormula && !hasImpactOnMetrology) {
      return (
        <>
          Souhaitez-vous confirmer la modification de la formule liée à la grandeur{" "}
          <b>{quantityName}</b> ?
        </>
      );
    } else if (isFormula && hasImpactOnMetrology) {
      return (
        <>
          Souhaitez-vous confirmer la modification de la formule et des critères métrologiques liés
          à la grandeur <b>{quantityName}</b> ?
        </>
      );
    } else {
      return (
        <>
          Souhaitez-vous confirmer la modification des critères métrologiques liés à la grandeur{" "}
          <b>{quantityName}</b> ?
        </>
      );
    }
  },
  "project.quantities.edit.details.impacted_quantities": "Détails des grandeurs impactées",
  "project.quantities.edit.error": "La grandeur n'a pas pu être mise à jour",
  "project.quantities.edit.formula.body.evaluation_relaunch_by_type": ({
    hasImpactOnFormula,
    hasImpactOnMetrology,
  }: {
    hasImpactOnFormula: boolean;
    hasImpactOnMetrology: boolean;
  }) => {
    if (hasImpactOnFormula && !hasImpactOnMetrology) {
      return (
        <>
          <b>Le relancement de l’évaluation de la formule</b> pour cette grandeur
        </>
      );
    } else if (!hasImpactOnFormula && hasImpactOnMetrology) {
      return (
        <>
          <b>Le relancement de l’évaluation des validités</b> pour cette grandeur
        </>
      );
    } else {
      return (
        <>
          <b>Le relancement de l’évaluation de la formule et des validités</b> pour cette grandeur
        </>
      );
    }
  },
  "project.quantities.edit.formula.body.impacted_quantities": ({
    impactedQuantitiesCount,
  }: {
    impactedQuantitiesCount: number;
  }) => (
    <>
      <b>Le relancement de l’évaluation de la formule et des validités</b> pour{" "}
      {impactedQuantitiesCount > 1
        ? "les " + impactedQuantitiesCount + " grandeurs"
        : "la grandeur"}{" "}
      en dépendant
    </>
  ),
  "project.quantities.edit.formula.details.label.level1": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>et {dependencyCount > 1 ? "grandeurs" : "grandeur"} en dépendant :</>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.edit.formula.details.label.level2": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>
          (+ {dependencyCount} {dependencyCount > 1 ? "grandeurs" : "grandeur"} en dépendant)
        </>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.edit.formula.download.details_impacted_quantities":
    "Détails des grandeurs impactées (.json)",
  "project.quantities.edit.formula.nota": ({ quantityName }: { quantityName: string }) => (
    <>
      Note : pour des raisons de lisibilité, seules les grandeurs dépendantes à un niveau 1 (direct)
      ou 2 de <b>{quantityName}</b> sont affichées
    </>
  ),
  "project.quantities.edit.impacts.body": "Cette modification entraînera par ailleurs :",
  "project.quantities.edit.name.error.format":
    "Erreur de format : le nom doit au moins contenir une lettre et n'être composé que de caractères alphanumériques et de _",
  "project.quantities.edit.simulation.impacting.diagnostic": () => (
    <>
      <b>Le relancement du calcul des diagnostics</b> (au moins une grandeur impactée utilisée comme
      observable).
    </>
  ),
  "project.quantities.edit.simulation.impacting.quantity": () => (
    <>
      <b>Le relancement du calcul des simulations et diagnostics</b> (au moins une grandeur impactée
      utilisée comme entrée du modèle).
    </>
  ),
  "project.quantities.edit.success": "La grandeur a été modifiée",
  "project.quantities.formula.add": "Nouvelle grandeur calculée",
  "project.quantities.import.title": "Importer des grandeurs",
  "project.quantities.newQuantities.modal.description": ({ sourceId }: { sourceId: string }) => (
    <>
      Les capteurs ci-dessous ont été identifiés dans la source de données <b>{sourceId}</b> et ne
      sont pas associés à une grandeur. Sélectionnez parmi cette liste les capteurs pour lesquels
      une grandeur sera créée dans ce projet.
    </>
  ),
  "project.quantities.newQuantities.modal.table.column.name": "Nom",
  "project.quantities.search.name_or_description": "Nom ou description",
  "project.quantity.toast.deleted.success": "La grandeur a été supprimée",
  "project.secondary_header.display_filters.title": "Filtres d'affichage",
  "project.secondary_header.filters.title": "Filtres d'analyse",
  "project.secondary_header.quantities.title": "Grandeurs",
  "project.secondary_header.settings.title": "Réglages",
  "project.secondary_header.setup.setup_and_analysis_infos"({
    startDate,
    endDate,
    count,
  }: {
    startDate: string | null;
    endDate: string | null;
    count: number;
  }) {
    return (
      <>
        {startDate === null && endDate === null
          ? `Utilisé pour toutes les dates`
          : typeof startDate === "string" &&
            typeof endDate === "string" &&
            `Utilisé entre le ${startDate} et le ${endDate}`}
        {typeof startDate === "string" && endDate === null && `Utilisé à partir du ${startDate}`}
        {startDate === null && typeof endDate === "string" && `Utilisé jusqu’au ${endDate}`} (
        {count} {count > 1 ? `analyses` : `analyse`})
      </>
    );
  },
  "project.setting.filter.modal.delete.additionnal_message.info": () => (
    <>
      Cette action n’aura pas d’impact sur les données existantes. Aucun analyse ne sera créée ou
      supprimée mais les points en écart aux filtres apparaîtront de manière spécifique. <br /> Pour
      rejouer le choix des dates d’analyse, utilisez la fonction{" "}
      <b>Réinitialiser le choix des dates d’analyse</b> dans le menu du projet.
    </>
  ),
  "project.settings.add_new_setup.button": "Nouveau paramétrage",
  "project.settings.add_setup.button": "Créer un paramétrage",
  "project.settings.analyses.add_display_filters.modal.title": "Importer les filtres d’affichage",
  "project.settings.analyses.add_filters": "Ajouter",
  "project.settings.analyses.add_filters.invalid.file_type":
    "Le format du fichier n'est pas attendu.",
  "project.settings.analyses.add_filters.modal.description": `Les filtres seront vérifiés lors de l'import de nouvelles données, en plus de l'intervalle moyen, pour créer les analyses.`,
  "project.settings.analyses.add_filters.modal.info.description": () => (
    <>
      Cette action n’aura pas d’impact sur les données existantes. Aucune analyse ne sera créée ou
      supprimée mais les points en écart aux filtres apparaîtront de manière spécifique.
      <br />
      Pour rejouer le choix des dates d’analyse, utilisez la fonction{" "}
      <b>Réinitialiser le choix des dates d’analyse</b> dans le menu du projet.
    </>
  ),
  "project.settings.analyses.add_filters.modal.title": "Importer les filtres d'analyse",
  "project.settings.analyses.delete_display_filters.modal.description":
    "Souhaitez-vous confirmer la suppression des filtres ?",
  "project.settings.analyses.delete_display_filters.modal.title":
    "Supprimer les filtres d’affichage",
  "project.settings.analyses.delete_filters.modal.description":
    "Souhaitez-vous confirmer la suppression des filtres ?",
  "project.settings.analyses.delete_filters.modal.submit_button.label": "Supprimer",
  "project.settings.analyses.delete_filters.modal.title": "Supprimer les filtres d'analyse",
  "project.settings.analyses.description": "Intervalle moyen entre deux analyses :",
  "project.settings.analyses.edit_filters": "Modifier",
  "project.settings.analyses.filters": ({ filterCount }: { filterCount: number }) => {
    let filterLabel = "Aucun";
    if (filterCount > 0) {
      filterLabel = `${filterCount} filtre${filterCount > 1 ? "s" : ""}`;
    }

    return `Filtres d’analyse : ${filterLabel}`;
  },
  "project.settings.analyses.frequency_value": ({
    days,
    hours,
    minutes,
  }: {
    days: number;
    hours: number;
    minutes: number;
  }) => (
    <>
      {days > 0 && (days > 1 ? `${days} jours` : `${days} jour`)}
      {hours > 0 && (hours > 1 ? ` ${hours} heures` : ` ${hours} heure`)}
      {minutes > 0 && (minutes > 1 ? ` ${minutes} minutes` : ` ${minutes} minute`)}
    </>
  ),
  "project.settings.analyses.show_filters": "Afficher les filtres",
  "project.settings.analyses.subtitle": "Sélection des dates d'analyse",
  "project.settings.analyses.subtitle.data_flow": "Temps réel",
  "project.settings.analyses.subtitle.data_flow.field.activation": "État",
  "project.settings.analyses.subtitle.data_flow.field.interval": "Modifier",
  "project.settings.analyses.subtitle.setup": "Paramétrages",
  "project.settings.analyses.title": "Analyses",
  "project.settings.blank_state.add_setup.text":
    "Pour commencer à réaliser des simulations et diagnostics, créez votre premier paramétrage",
  "project.settings.blank_state.add_setup.title": "Aucun paramétrage n’est défini",
  "project.settings.computed_sensors_quantities_count": ({
    sensorCount,
    quantityCount,
  }: {
    sensorCount: number;
    quantityCount: number;
  }) =>
    quantityCount >= 1
      ? `${sensorCount} ${sensorCount > 1 ? `capteurs` : `capteur`} et ${quantityCount} ${
          quantityCount > 1 ? `grandeurs calculées ` : `grandeur calculée `
        } `
      : `${sensorCount} ${sensorCount > 1 ? `capteurs ` : `capteur `}`,
  "project.settings.data.name": "Source de données : ",
  "project.settings.data.title": "Données",
  "project.settings.data_definition.subtitle": "Définition des données",
  "project.settings.data_definition.subtitle.field.quantity": "Accéder à la page des grandeurs",
  "project.settings.data_flow.activated.modal.subtitle":
    "Des analyses seront créées pour les nouvelles données importées dans la source liée au projet",
  "project.settings.data_flow.activated.modal.title": "Activer le temps réel",
  "project.settings.data_flow.disabled.modal.subtitle":
    "Les imports de données dans la source ne généreront pas la création de nouvelles analyses",
  "project.settings.data_flow.disabled.modal.title": "Mettre en pause le temps réel",
  "project.settings.data_flow.status.activated": "Activé",
  "project.settings.data_flow.status.activated.link.text": "Mettre en pause",
  "project.settings.data_flow.status.disabled": "Non actif",
  "project.settings.data_flow.status.disabled.link.text": "Activer",
  "project.settings.datasource.modal.title": "Modifier la source de données",
  "project.settings.display.part_load_sensitive_kpis":
    "Afficher les KPI sensibles au fonctionnement à charge partielle",
  "project.settings.display.part_load_sensitive_kpis.tooltip":
    "Contrôle la visibilité des valeurs KPI du projet incluant des événements à charge partielle.",
  "project.settings.display.subtitle": "Affichage",
  "project.settings.display_filters.groups.default_badge": "Défaut",
  "project.settings.display_filters.name": ({
    filterCount,
    groupCount,
  }: {
    filterCount: number;
    groupCount: number;
  }) => {
    let filterLabel = "Aucun";
    if (filterCount > 0) {
      filterLabel = `${filterCount} filtre${filterCount > 1 ? "s" : ""}`;
    }
    return `Filtres d'affichage : ${filterLabel}, ${groupCount} groupe${groupCount > 1 ? "s" : ""}`;
  },

  "project.settings.display_filters.replace.modal.subtitle":
    "Le nouveau fichier remplacera les filtres actuels. Les filtres non présents dans le nouveau fichier seront supprimés.",
  "project.settings.display_filters.replace.modal.title": "Remplacer les filtres d’affichage",
  "project.settings.display_filters.tooltip":
    "Permettent de restreindre les analyses affichées dans les graphes selon les valeurs prises par une grandeur.",
  "project.settings.filters.tooltip":
    "Permettent de définir les conditions que doivent respecter les données pour qu’une analyse soit créée.",
  "project.settings.link.text": "Modifier",
  "project.settings.modal.data_flow.activated.additionnal_message.info.text":
    "Ces critères pourront être modifiés ultérieurement en page Réglages.",
  "project.settings.modal.data_flow.activated.criteria":
    "La création d’analyses se basera sur les critères suivants",
  "project.settings.modal.data_flow.activated.criteria.activation_filter": (nbFilters: number) => (
    <>
      Filtres : <b>{nbFilters > 0 ? `${nbFilters} filtre${nbFilters > 1 ? "s" : ""}` : "Aucun"}</b>
    </>
  ),
  "project.settings.modal.data_flow.activated.criteria.interval_minimal": (
    minutesBetweenActiveAnalysis: number,
    intervalMinimal: string,
  ) => (
    <>
      Intervalle moyen entre deux analyses :{" "}
      {minutesBetweenActiveAnalysis === 0 ? <b>Aucun</b> : <b>{intervalMinimal}</b>}
    </>
  ),
  "project.settings.modal.data_flow.activated.ok_button_name": "Activer",
  "project.settings.modal.data_flow.activated.question.text":
    "Souhaitez-vous confirmer l’activation ?",
  "project.settings.modal.data_flow.disabled.text": "Souhaitez-vous confirmer la mise en pause ?",
  "project.settings.none": "Aucun",
  "project.settings.performance_indicators.max.additionnal_message":
    "Vous pouvez ajouter jusqu’à 5 indicateurs de performance.",
  "project.settings.processIndicator.add.new": "Ajouter un indicateur de conduite",
  "project.settings.processIndicator.edit": "Modifier",
  "project.settings.processIndicator.no.data":
    "Aucun indicateur de conduite défini pour ce projet.",
  "project.settings.processIndicators.modal.title": ({ editMode }: { editMode: boolean }) =>
    `${editMode ? "Modifier les indicateurs de conduite" : "Liste des indicateurs de conduite"}`,
  "project.settings.processIndicators.name": ({
    processIndicatorsList,
  }: {
    processIndicatorsList: string;
  }) => `Indicateurs de conduite : ${processIndicatorsList ? processIndicatorsList : "Aucun"}`,
  "project.settings.process_indicators.max.additionnal_message": `Vous pouvez ajouter jusqu’à ${MAX_PROCESS_INDICATOR_COUNT} indicateur${MAX_PROCESS_INDICATOR_COUNT > 1 ? "s" : ""} de conduite.`,
  "project.settings.qoi.add.new": "Ajouter un indicateur de performance",
  "project.settings.qoi.edit": "Modifier",
  "project.settings.qoi.modal.title": ({ editMode }: { editMode: boolean }) =>
    `${
      editMode ? "Modifier les indicateurs de performance" : "Liste des indicateurs de performance"
    }`,
  "project.settings.qoi.name": ({ qoiList }: { qoiList: string }) =>
    `Indicateurs de performance : ${qoiList ? qoiList : "Aucun"}`,
  "project.settings.qoi.no.data": "Aucun indicateur de performance défini pour ce projet.",
  "project.settings.qoi.prompt": "Aucun",
  "project.settings.qoi.role.power": "Puissance produite",
  "project.settings.qoi.table.column.order": "Ordre d'affichage",
  "project.settings.qoi.table.column.quantity": "Grandeur",
  "project.settings.qoi.table.column.referenceValue": "Valeur de référence",
  "project.settings.qoi.table.column.referenceValue.error": "Doit être un nombre",
  "project.settings.qoi.table.column.role": "Rôle",
  "project.settings.quantities.name": "Grandeurs : ",
  "project.settings.quantities.usage_icons.tooltip.dashboard": "Utilisée dans les tableaux de bord",
  "project.settings.quantities.usage_icons.tooltip.formula": "Utilisée dans le calcul de formules",
  "project.settings.quantities.usage_icons.tooltip.setup": "Utilisée dans le paramétrage",
  "project.settings.quantity.side_panel.create.title": "Créer une grandeur calculée",
  "project.settings.quantity.side_panel.edit.title": "Détails de la grandeur",
  "project.settings.quantity.side_panel.edition.impacts_warning": "Impacts liés aux modifications",
  "project.settings.quantity.side_panel.section.definition_quantity.intermediate_calculation":
    "Calcul intermédiaire",
  "project.settings.quantity.side_panel.section.definition_quantity.intermediate_calculation_tooltip":
    "Masque cette grandeur par défaut hors des paramètres de grandeurs. Les utilisateurs setup peuvent l’afficher depuis les sélecteurs de grandeurs.",
  "project.settings.quantity.side_panel.section.definition_quantity.title":
    "Définition de la grandeur",
  "project.settings.quantity.side_panel.section.formula_input.duplicate_usage_period_error":
    "Une autre version de formule commence déjà à cette date.",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle": "Cycle",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_empty":
    "Aucun cycle disponible",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_label":
    "Renseigner la date de début depuis un cycle",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_search_placeholder":
    "Rechercher un cycle",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_prefix": "Trouver via",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_separator": "ou",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup": "Paramétrage",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_empty":
    "Aucun paramétrage disponible",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_label":
    "Renseigner la date de début depuis un paramétrage",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_search_placeholder":
    "Rechercher un paramétrage",
  "project.settings.quantity.side_panel.section.formula_input.help_link":
    "Apprendre la syntaxe des formules",
  "project.settings.quantity.side_panel.section.formula_input.input_label": "Formule",
  "project.settings.quantity.side_panel.section.formula_input.lock_title": "Verrouiller la formule",
  "project.settings.quantity.side_panel.section.formula_input.lock_tooltip":
    "Une fois verrouillée, la formule ne peut être modifiée que par les paramétreurs et administrateurs",
  "project.settings.quantity.side_panel.section.formula_input.locked_due_to_dependency":
    "Cette formule est verrouillée automatiquement en raison de son utilisation dans un paramétrage ou une formule verrouillée. Elle ne peut être modifiée que par un paramétreur ou administrateur",
  "project.settings.quantity.side_panel.section.formula_input.locked_warning":
    "Cette formule est verrouillée manuellement et ne peut être modifiée que par un paramétreur ou administrateur",
  "project.settings.quantity.side_panel.section.formula_input.title": "Saisie de la formule",
  "project.settings.quantity.side_panel.section.formula_input.unnamed_cycle": "Cycle sans nom",
  "project.settings.quantity.side_panel.section.formula_input.usage_period": (
    from: string | null,
    to: string | null,
  ) => {
    if (from && !to) return `Depuis le ${from}`;
    else if (!from && to) return `Jusqu'au ${to}`;
    else if (from && to) return `Du ${from} au ${to}`;
    else return "";
  },
  "project.settings.quantity.side_panel.section.formula_input.usage_period_label":
    "Date de début d'application",
  "project.settings.quantity.side_panel.section.metrological_filters.title":
    "Filtres métrologiques",
  "project.settings.quantity.side_panel.tab.formulae": "Formules",
  "project.settings.quantity.side_panel.tab.formulae.add": "Ajouter une formule",
  "project.settings.quantity.side_panel.tab.general_information": "Informations générales",
  "project.settings.setup.add_update_setup.modal.placeholder.name": "Nom",
  "project.settings.setup.add_update_setup.modal.title.description": "Description",
  "project.settings.setup.add_update_setup.modal.title.effective_start": "Début d’application",
  "project.settings.setup.add_update_setup.modal.title.name": "Nom",
  "project.settings.timezone": ({ timeZone }: { timeZone: string }) =>
    `Fuseau horaire d'affichage des données : ${timeZone}`,
  "project.settings.timezone.modal.title": "Modifier le fuseau horaire",
  "project.setup.header.dropdown.delete_setup": "Supprimer le paramétrage",
  "project.setup.header.dropdown.duplicate_setup": "Dupliquer le paramétrage",
  "project.setup.header.dropdown.edit_setup": "Éditer le paramétrage",
  "project.setup.header.dropdown.export_setup": "Exporter le paramétrage",
  "project.symptom.chart_tooltip.expected": "Valeur attendue",
  "project.symptom.chart_tooltip.measured": "Valeur mesurée",
  "project.symptoms.title": "Symptômes",
  "project.widget.average_losses.value": (losses: string) => `${capitalize(losses)} :`,
  "project.widget.productio_monitoring.tooltip.value": "Puissance produite :",
  "project.widget.production_monitoring.no_active_analysis": "Pas d’analyse à ce moment",
  "project.widget.production_monitoring.title": "Suivi de la production",
  "project.widget.production_monitoring.yAxis.max_value": (value: string) => `Max : ${value}`,
  "projects.actions.delete": "Supprimer",
  "projects.actions.delete.disabled.locked_formula":
    "Impossible de supprimer car la formule est verrouillée",
  "projects.actions.delete.disabled.side_panel_open":
    "Action bloquée tant que le panneau latéral est ouvert",
  "projects.actions.duplicate": "Dupliquer",
  "projects.actions.edit": "Modifier",
  "projects.actions.edit.disabled.side_panel_open":
    "Action bloquée tant que le panneau latéral est ouvert",
  "projects.actions.new_calculated_quantity.disabled.side_panel_open":
    "Action bloquée tant que le panneau latéral est ouvert",
  "projects.actions.sensor_tag.disabled.side_panel_open":
    "Action bloquée tant que le panneau latéral est ouvert",
  "projects.actions.view": "Détails",
  "projects.badge.datalake": "Datalake",
  "projects.badge.datalake.description": ({ sourceId }: { sourceId: string }) =>
    `Connecté à la datasource : ${sourceId}`,
  "projects.blank_state.ask_administrator_for_project_access":
    "Demandez l'accès aux projets à votre administrateur.",
  "projects.blank_state.create_project":
    "Créez un nouveau projet pour lancer votre première analyse",
  "projects.blank_state.title": "Bienvenue sur le Metroscope !",
  "projects.favourite_card.title.confirmed_faults": "Défaillances confirmées",
  "projects.favourite_card.title.internal_losses": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "projects.favourite_card.title.power_produced": "Puissance produite",
  "projects.header.actions.cancel_selection": "Annuler",
  "projects.header.actions.edit_projects": "Éditer les projets",
  "projects.header.actions.update_projects": ({ count }: { count: number }) =>
    `Éditer les projets (${count})`,
  "projects.header.actions.update_projects.info": "Sélectionner les projets dans le tableau",
  "projects.header.actions.update_projects_disabled": "Éditer les projets",
  "projects.header.display_draft": "Afficher les projets de recherche",
  "projects.header.display_manual_failures": "Défaillances non modélisées",
  "projects.header.title": "Projets",
  "projects.modal.analysis_export.title.format": "Format",
  "projects.modal.create_missing_analyses_cac.title": "Créer les analyses manquantes",
  "projects.modal.create_or_edit.input.name.error.cannot_be_empty":
    "Le nom du projet ne peut être vide",
  "projects.modal.create_or_edit.input.name.error.name_already_exists":
    "Le nom du projet existe déjà",
  "projects.modal.create_or_edit.input.name.error.too_long":
    "Le nom ne doit pas dépasser 100 caractères",
  "projects.modal.create_or_edit.input.publishedAnalysesLastDate": "Dernière analyse publiée",
  "projects.modal.create_or_edit.input.site": "Site",
  "projects.modal.create_or_edit.input.type": (translations: CustomTranslations): string =>
    capitalize(translations.FR.TYPE),
  "projects.modal.dataSource": "Source de données",
  "projects.modal.delete.are_you_sure": ({ name }: { name: string }) => (
    <>
      Vous vous apprêtez à supprimer définitivement le projet <strong>{name}</strong>.
      <br />
      Veuillez écrire ci-dessous le nom du projet pour confirmer la suppression.
    </>
  ),
  "projects.modal.delete.delete_project": "Supprimer le projet",
  "projects.modal.delete.input_placeholder": "Nom du projet",
  "projects.modal.delete.prod.project.tooltip":
    "La suppression des projets de production n’est pas possible. Merci de passer le projet en recherche pour le supprimer",
  "projects.modal.delete.project_name": "Nom du projet à supprimer",
  "projects.modal.dispatch_cac.title": "Dispatcher le CAC",
  "projects.modal.duplicate.duplicated_name": ({ name }: { name: string }) => `Copie de ${name}`,
  "projects.modal.duplicate.input.additional_message_info.deleted_comments":
    "En cas de présence de commentaires rattachés à des événements, ceux-ci seront supprimés.",
  "projects.modal.duplicate.input.include_comments": "Inclure les commentaires",
  "projects.modal.duplicate.input.include_dashboards": (translations: CustomTranslations) =>
    `Inclure les ${translations.FR.DASHBOARDS.toLowerCase()} (privés et partagés)`,
  "projects.modal.duplicate.input.include_events": "Inclure les événements",
  "projects.modal.duplicate.input.include_user_project_roles": "Inclure les droits par projet",
  "projects.modal.edit.input.dataSource.error": ({
    missingSensorTags,
  }: {
    missingSensorTags: string[];
  }) => (
    <>
      La source de donnée choisie n&apos;est pas compatible avec ce projet,{" "}
      {missingSensorTags.length} tags capteur utilisés dans le projet sont manquants. Merci de
      supprimer les grandeurs associées dans le projet ou d&apos;ajouter les tags capteurs dans la
      source.
      <br />
      <br />
      Liste des tags capteurs manquants :
      <br />
      <br />
      {missingSensorTags.join(", ")}
    </>
  ),
  "projects.modal.edit.input.dataSource.tooltip":
    "La source de donnée n'est pas modifiable sur plusieurs projets à la fois",
  "projects.modal.edit.update_project.toast": ({
    projectName,
    name,
    description,
    timeZone,
  }: {
    projectName: string;
    name: boolean;
    description: boolean;
    timeZone: boolean;
  }) => {
    const properties: string[] = [];
    if (name) {
      properties.push("nom");
    }
    if (description) {
      properties.push("description");
    }
    if (timeZone) {
      properties.push("fuseau horaire");
    }
    const propertiesList = listFormatConjunction(properties);

    return `${
      properties.length > 1
        ? `Les propriétés ${propertiesList} ont été modifiées`
        : `La propriété ${propertiesList} a été modifiée`
    } pour le projet ${projectName}`;
  },
  "projects.modal.edit.update_projects.toast": ({
    projectsCount,
    name,
    description,
    timeZone,
  }: {
    projectsCount: number;
    name: boolean;
    description: boolean;
    timeZone: boolean;
  }) => {
    const properties: string[] = [];
    if (name) {
      properties.push("nom");
    }
    if (description) {
      properties.push("description");
    }
    if (timeZone) {
      properties.push("fuseau horaire");
    }
    const propertiesList = listFormatConjunction(properties);

    return `${
      properties.length > 1
        ? `Les propriétés ${propertiesList} ont été modifiées`
        : `La propriété ${propertiesList} a été modifiée`
    } pour ${projectsCount} projet${projectsCount > 1 ? "s" : ""}`;
  },
  "projects.modal.launch_cac.title": "Lancer le CAC",
  "projects.modal.multiple_value": "Valeurs multiples",
  "projects.modal.name": "Nom",
  "projects.modal.reset_cac.title": "Réinitialiser le CAC",
  "projects.modal.restart_analysis_selection.subtitle":
    "La sélection des analyses sera annulée et réalisée à nouveau sur la base des critères choisis",
  "projects.modal.restart_analysis_selection.title": "Relancer la sélection des analyses",
  "projects.modal.title.create_project": "Créer un projet",
  "projects.modal.title.duplicate_project": "Dupliquer le projet",
  "projects.modal.toast.create_project.text": ({ name }: { name: string }) => `Projet ${name} créé`,
  "projects.modal.toast.delete_project": ({ name }: { name: string }) => `Projet ${name} supprimé`,
  "projects.modal.toast.delete_project_in_progress": ({ name }: { name: string }) =>
    `Suppression du projet ${name} en cours, cela peut prendre quelques minutes`,
  "projects.modal.update_project": "Éditer le projet",
  "projects.modal.update_projects.title.dataSource": "Source de données",
  "projects.modal.update_projects.title.description": "Description",
  "projects.modal.update_projects.title.name": "Nom",
  "projects.modal.update_projects.title.timezone": "Fuseau horaire d'affichage de données",
  "projects.paragraphs.actions.create_project": "Nouveau projet",
  "projects.paragraphs.favourites": "Favoris",
  "projects.paragraphs.projects": "Projets",
  "projects.paragraphs.projects.table.column.description": "Description",
  "projects.paragraphs.projects.table.column.lastAnalysisDate": "Dernière analyse",
  "projects.paragraphs.projects.table.column.loss": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "projects.paragraphs.projects.table.column.measured": "Puissance produite",
  "projects.paragraphs.projects.table.column.name": "Nom",
  "projects.paragraphs.projects.table.column.nbConfirmedFaults": "Défaillances confirmées",
  "projects.paragraphs.projects.table.column.sourceId": "Source de données",
  "projects.quantities.create.quantity.success.toast.title": "La grandeur a été créée",
  "projects.quantities.too_many": `Le nombre maximum de grandeurs autorisé est de ${MAX_QUANTITY_COUNT}.`,
  "projects.quantity.action.delete.disabled.has_impact_on_setups": (setupNames: string[]) => (
    <>
      Impossible de supprimer car la grandeur (ou une grandeur qui en dépend) est utilisée comme
      entrée ou sortie du modèle dans{" "}
      {setupNames.length > 1 ? (
        <>
          les paramétrages{" "}
          <strong>
            {printListStrings(
              setupNames.map(name => name.toUpperCase()),
              "et",
            )}
          </strong>
        </>
      ) : (
        <>
          le paramétrage <strong>{setupNames[0]?.toUpperCase()}</strong>
        </>
      )}
      .
    </>
  ),
  "projects.selectFilter.data_source": "Source de données",
  "projects.selectFilter.site": "Site",
  "projects.selectFilter.type": (translations: CustomTranslations) =>
    capitalize(translations.FR.TYPE),
  "projects.settings.analyses_frequency.additionnal_message.info.criteria": () => (
    <>
      Cette action n’aura pas d’impact sur les données existantes. Aucune analyse ne sera créée ou
      supprimée mais les points en écart aux filtres apparaîtront de manière spécifique.
      <br />
      Pour rejouer le choix des dates d’analyse, utilisez la fonction{" "}
      <b>Réinitialiser le choix des dates d’analyse</b> dans le menu du projet.
    </>
  ),
  "projects.settings.analyses_frequency.modal.edit.input.days": "Jours",
  "projects.settings.analyses_frequency.modal.edit.input.hours": "Heures",
  "projects.settings.analyses_frequency.modal.edit.input.minutes": "Minutes",
  "projects.settings.analyses_frequency.modal.submit.error.intervalToBig":
    "L'intervalle sélectionné doit être inférieur à 1 an",
  "projects.settings.analyses_frequency.modal.submit.toast.success.completed":
    "La création des analyses est terminée.",
  "projects.settings.analyses_frequency.modal.submit.toast.success.started":
    "La création des analyses a démarré.",
  "projects.settings.analyses_frequency.modal.switch.text": "Ne pas utiliser d’intervalle",
  "projects.settings.analyses_frequency.modal.title": "Intervalle moyen",
  "projects.title": "Projets",
  //   "projects.toast.duplication_ended.link": "Accéder au project dupliqué",
  "quantities.blankState.subtitle": "Importez la liste des grandeurs",
  "quantities.blankState.title": "Aucune grandeur n'est définie",
  "quantities.crossFilter.blankState.button_label": "Importer",
  "quantities.crossFilter.blankState.subtitle":
    "Importez la liste des filtres croisés de grandeurs",
  "quantities.crossFilter.blankState.title": "Aucun filtre croisé n’est défini",
  "quantities.crossFilter.info": (quantityName: string, filters: string) =>
    `Avec ${quantityName} valeur ${filters}`,
  "quantities.crossFilter.modal.delete.title": "Supprimer les filtres",
  "quantities.crossFilter.modal.import.title": "Importer les filtres",
  "quantities.crossFilter.table.evaluatedQuantity": "Grandeur source",
  "quantities.crossFilter.table.invalidatedQuantity": "Grandeur invalidée",
  "quantities.crossFilter.table.max": "Max",
  "quantities.crossFilter.table.maxStd": "Écart-type max",
  "quantities.crossFilter.table.min": "Min",
  "quantities.cross_filter.modal.delete.additionnal_message.warning":
    "Suite à cette action, les validités de mesure seront réévaluées pour les grandeurs sur lesquelles des filtres s'appliquent. Des calculs de simulation ou de diagnostic peuvent être relancés selon les changements de validité et les rôles des grandeurs",
  "quantities.cross_filter.modale.delete.button_name": "Supprimer",
  "quantities.cross_filter.modale.delete.question":
    "Souhaitez-vous confirmer la suppression des filtres ? ",
  "quantities.cross_filter.modale.import.info.metrological_filters_are_ignored":
    "Les filtres métrologiques simples sont ignorés lors de cet import.",
  "quantities.formula_version.version_count": (n: number) =>
    n < 2 ? `${n} version` : `${n} versions`,
  "quantities.header.cross_filters": "Filtres croisés",
  "quantities.header.quantities": "Grandeurs",
  "quantities.import.dry_run.message.analysis_filter_deleted": (quantityNames: string[]) =>
    `Le filtre d’analyse associé ${
      quantityNames.length > 1 ? "aux grandeurs " : "à la grandeur "
    } ${quantityNames.join(", ")} sera supprimé.`,
  "quantities.import.dry_run.message.cross_filter_deleted": (quantityNames: string[]) =>
    `Le filtre croisé associé ${
      quantityNames.length > 1 ? "aux grandeurs " : "à la grandeur "
    } ${quantityNames.join(", ")} sera supprimé.`,
  "quantities.import.dry_run.message.error.blocked_invalid_sensor_tag": (quantityName: string) =>
    `Grandeur ${quantityName} : le sensorTag est invalide.`,
  "quantities.import.dry_run.message.error.blocked_link_setup": (
    quantityName: string,
    setupNames: string[],
  ) =>
    `Grandeur ${quantityName} : la grandeur doit être présente dans le fichier car elle est utilisée dans ${
      setupNames.length > 1 ? "les paramétrages" : "le paramétrage"
    } ${setupNames.join(", ")}.`,
  "quantities.import.dry_run.message.formula_version": (
    quantityName: string,
    startsAt: string,
    oldValue: string,
    newValue: string,
  ) =>
    `Grandeur ${quantityName} : version de formule démarrant le ${startsAt} modifiée de "${oldValue}" à "${newValue}"`,
  "quantities.import.dry_run.message.formula_version_header": (quantityName: string) =>
    `Grandeur ${quantityName} : les versions de formule vont changer`,
  "quantities.import.dry_run.message.formula_version_line_added": (
    startsAt: string,
    newValue: string,
  ) => `${startsAt} — sera ajoutée : ${newValue}`,
  "quantities.import.dry_run.message.formula_version_line_modified": (
    startsAt: string,
    oldValue: string,
    newValue: string,
  ) => `${startsAt} — sera modifiée : ${oldValue} → ${newValue}`,
  "quantities.import.dry_run.message.formula_version_line_removed": (
    startsAt: string,
    oldValue: string,
  ) => `${startsAt} — sera supprimée : ${oldValue}`,
  "quantities.import.dry_run.message.formula_versions_info":
    "Les versions de formule de l’import peuvent ajouter, modifier ou supprimer des formules existantes.",
  "quantities.import.dry_run.message.formula_versions_summary":
    "Afficher les changements de versions de formule",
  "quantities.import.dry_run.message.performance_indicator_deleted": (quantityNames: string[]) =>
    `L’indicateur de performance associé ${
      quantityNames.length > 1 ? "aux grandeurs " : "à la grandeur "
    } ${quantityNames.join(", ")} sera supprimé.`,
  "quantities.import.dry_run.message.process_indicator_deleted": (quantityNames: string[]) =>
    `L’indicateur de conduite associé ${
      quantityNames.length > 1 ? "aux grandeurs " : "à la grandeur "
    } ${quantityNames.join(", ")} sera supprimé.`,
  "quantities.import.dry_run.message.reset_formula": () =>
    `Suite à cette action, le calcul des formules et des filtres métrologiques sera réinitialisé.`,
  "quantities.import.dry_run.message.reset_formula_simulation_diagnostic": () =>
    `Suite à cette action, le calcul des formules, des filtres métrologiques, des simulations et diagnostics sera réinitialisé, au moins une de ces grandeurs impactées étant utilisée dans un paramétrage.`,
  "quantities.metrologyFilter": "Filtres métrologiques :",
  "quantities.parse.error.cross_filter_by_pass":
    "Le fichier ne contient que des filtres de type ‘IMPORT’ ou des filtres métrologiques simples. Il n’est pas utilisable pour import.",
  "quantities.parse.error.data_invalidated_quantity_empty": `Le champ 'invalidatedQuantity' ne doit pas être vide.`,
  "quantities.parse.error.data_source_quantity_empty": `Le champ 'source' ne doit pas être vide.`,
  "quantities.parse.error.file_invalid_content":
    "Erreur de format : les colonnes attendues sont 'invalidatedQuantity', 'source', 'minimumValue', 'maximumValue' et 'maximumStd'.",
  "quantities.parse.error.only_import_and_metrology_filters":
    "Le fichier contient seulement les filtres de type “IMPORT” et métrologiques.",
  "quantitySelector.actionDisabledBecauseDrawerOpen":
    "Action bloquée tant que le panneau latéral est ouvert",
  "quantitySelector.actions.create": "Créer",
  "quantitySelector.actions.viewList": "Voir la liste",
  "quantitySelector.display_options.tooltip": "Options d'affichage des grandeurs",
  "quantitySelector.filter.tooltip": "Filtrer les grandeurs",
  "quantitySelector.group.alert.measures.current":
    "Indisponible sur au moins une analyse de la période sélectionnée.",
  "quantitySelector.group.alert.measures.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `Au moins une grandeur indisponible  dans ${alertPercentage}% (${alertCount}/${analysisCount}) des analyses de la période affichée.`,
  "quantitySelector.group.alert.symptoms.current":
    "Au moins une grandeur symptomatique sur une analyse de la période sélectionnée.",
  "quantitySelector.group.alert.symptoms.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `Au moins une grandeur symptomatique dans ${alertPercentage}% (${alertCount}/${analysisCount}) des analyses de la période affichée (lorsque la grandeur est disponible).`,
  "quantitySelector.quantity.alert.measures.current":
    "Indisponible sur au moins une analyse de la période sélectionnée.",
  "quantitySelector.quantity.alert.measures.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `Indisponible dans ${alertPercentage}% (${alertCount}/${analysisCount}) des analyses de la période affichée.`,
  "quantitySelector.quantity.alert.symptoms.current":
    "Symptomatique sur au moins une analyse de la période sélectionnée.",
  "quantitySelector.quantity.alert.symptoms.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `Symptomatique dans ${alertPercentage}% (${alertCount}/${analysisCount}) des analyses de la période affichée (lorsque la grandeur est disponible).`,
  "quantitySelector.selected.title": "Grandeurs sélectionnées",
  "quantitySelector.toggle.showDescriptions": "Afficher les descriptions",
  "quantitySelector.toggle.showIntermediateCalculations": "Afficher les calculs intermédiaires",
  "quantitySelector.toggle.symptomatic":
    "Uniquement les grandeurs symptomatiques pour les analyses sélectionnées",
  "quantitySelector.toggle.unavailable":
    "Uniquement les grandeurs indisponibles pour les analyses sélectionnées",
  "quantitySelector.toogle.filter": "Filtrer les grandeurs",
  reparability: (reparability: Reparability, translations: CustomTranslations) => {
    switch (reparability) {
      case "REPARABILITY_0":
        return translations.FR.REPARABILITY_0;
      case "REPARABILITY_1":
        return translations.FR.REPARABILITY_1;
      case "REPARABILITY_2":
        return translations.FR.REPARABILITY_2;
      default:
        throw new Error(reparability satisfies never);
    }
  },
  "reparability.none": "Non précisée",
  "reporting.chart.aggregation.failure.other": (translations: CustomTranslations) =>
    `Autres ${lowercase(translations.FR.LOSSES)} ${lowercase(translations.FR.EXPLAINED)}`,
  "reporting.chart.aggregation.failure.remaining_losses": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} ${lowercase(translations.FR.UNEXPLAINED)}`,
  "reporting.chart.aggregation.failure.total_losses": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} internes - Total`,
  "reporting.chart.aggregation.other": "Autres",
  "reporting.chart.failure_aggregation.category": "Par catégorie",
  "reporting.chart.failure_aggregation.degradationType": "Par type de degradation",
  "reporting.chart.failure_aggregation.failure": "Par défaillance",
  "reporting.chart.failure_aggregation.project": "Par projet",
  "reporting.chart.failure_aggregation.reparability": "Par réparabilité",
  "reporting.chart.failure_aggregation.site": "Par site",
  "reporting.chart.failure_aggregation.system": "Par système",
  "reporting.chart.failure_aggregation.type": (translations: CustomTranslations): string =>
    `Par ${lowercase(translations.FR.TYPE)}`,
  "reporting.chart.info.title": "Contenu du graphe",
  "reporting.chart.subtitle"({
    // filteredProjectCount,
    includedProjectCount,
    draft: includeResearch,
    siteCount,
  }: {
    // filteredProjectCount: number;
    includedProjectCount: number;
    draft: boolean;
    siteCount: number;
  }) {
    return `${includedProjectCount} projet${includedProjectCount > 1 ? "s" : ""} ${
      includeResearch ? "" : "de production "
    }affiché${includedProjectCount > 1 ? "s" : ""} (${siteCount} site${siteCount > 1 ? "s" : ""})`;
  },
  "reporting.chart.subtitle.filtered_failures"({ failureCount }: { failureCount: number }) {
    return `${failureCount} défaillances affichées`;
  },
  "reporting.chart.subtitle.selected_month"({
    selectedMonth,
    selectedYear,
  }: {
    selectedMonth: string;
    selectedYear: string;
  }) {
    return ` - ${selectedMonth} ${selectedYear}`;
  },
  "reporting.chart.switch.chart": "Graphique",
  "reporting.chart.switch.matrix": "Matrice",
  "reporting.charts.reparability_impacts_matrix.failure": "Défaillance",
  "reporting.charts.reparability_impacts_matrix.impact": "Impact",
  "reporting.charts.reparability_impacts_matrix.reparability": "Réparabilité",
  "reporting.errors.copy_details": "Copier le détail des projets concernés",
  "reporting.errors.multi_published_analyses"({ count }: { count: number }) {
    return `Sur la période affichée, pour ${count} projet${
      count > 1 ? "s" : ""
    }, plusieurs analyses ont été publiées sur le même mois.\nDans ce cas, la donnée présentée correspond à la dernière analyse publiée du mois.`;
  },
  "reporting.errors.no_published_analyses"({ count }: { count: number }) {
    return `Sur la période affichée, pour ${count} projet${
      count > 1 ? "s" : ""
    }, aucune donnée n'a été publiée sur au moins un mois.\nDans ce cas, la donnée présentée correspond à la dernière valeur publiée dans l'historique. Les données brutes sont affichées lorsque le bouton Compléter les mois sans donnée est décoché.`;
  },
  "reporting.export": "Exporter",
  "reporting.export.matrix": "Exporter la matrice au format xlsx",
  "reporting.failure_impact_reparability.description":
    "Cette vue présente l’ensemble des défaillances présentes sur un mois, avec une analyse selon leur réparabilité et leur impact.",
  "reporting.failure_impact_reparability.options.impact.description":
    "Quantitification de l’impact de la défaillance selon l’indicateur de performance sélectionné",
  "reporting.failure_impact_reparability.options.impact.title": "Impact (en ordonnée)",
  "reporting.failure_impact_reparability.options.reparability.description":
    "Indice de capacité à réparer une défaillance",
  "reporting.failure_impact_reparability.options.reparability.title": "Réparabilité (en abscisse)",
  "reporting.failure_impact_reparability.title":
    "Analyse des défaillances selon leur impact et réparabilité",
  "reporting.failure_impacts.info.description": (translations: CustomTranslations) =>
    `Cette vue présente les ${lowercase(
      translations.FR.LOSSES,
    )} internes par défaillance, qu'il est possible d'agréger par catégorie, réparabilité, type de dégradation ou système.`,
  "reporting.failure_impacts.title": "Évolution de l'impact des défaillances",
  "reporting.filters.failure.category": "Catégorie de défaillance",
  "reporting.filters.failure.degradationType": "Type de dégradation",
  "reporting.filters.failure.equipmentType": "Type d'équipement",
  "reporting.filters.failure.system": "Système",
  "reporting.filters.project.project": "Projet",
  "reporting.fleet_internal_gaps.chart.mean_expected": "Moy. attendu",
  "reporting.fleet_internal_gaps.chart.mean_internal_gap": (translations: CustomTranslations) =>
    `Moy. ${lowercase(translations.FR.LOSSES)} internes`,
  "reporting.fleet_internal_gaps.chart.relative_gaps": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} internes relatives`,
  "reporting.fleet_internal_gaps.chart.tootip.evolution": "Évolution",
  "reporting.fleet_internal_gaps.info.description": (translations: CustomTranslations) =>
    `La barre orange correspond au pourcentage moyen de ${lowercase(
      translations.FR.LOSSES,
    )} internes sur la période sélectionnée.\nLa barre bleue correspond à ce même calcul pour la période précédente de même durée.`,
  "reporting.fleet_internal_gaps.title": (translations: CustomTranslations) =>
    `Comparaison selon le niveau de ${lowercase(translations.FR.LOSSES)} internes`,
  "reporting.fleet_performance.chart.expected": "Valeur attendue",
  "reporting.fleet_performance.chart.internal_losses": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "reporting.fleet_performance.chart.measured": "Valeur mesurée",
  "reporting.fleet_performance.info.description": (translations: CustomTranslations) =>
    `Cette vue présente l’évolution des valeurs mesurées et attendues de l'indicateur de performance, conduisant au calcul des ${lowercase(
      translations.FR.LOSSES,
    )} internes.`,
  "reporting.fleet_performance.title": "Évolution de la performance",
  "reporting.fleet_performance.tooltip.project_subtitle": (
    projectCount: number,
    filledCount: number,
    percentProject: number,
  ) =>
    filledCount
      ? `Cumuls basés sur ${projectCount} projet${
          projectCount > 1 ? "s" : ""
        } (${percentProject}% — dont ${filledCount} avec des valeurs issues du mois précédent)`
      : `Cumuls basés sur ${projectCount} projet${projectCount > 1 ? "s" : ""} (${percentProject}%)`,
  "reporting.header.display_draft": "Inclure les projets de recherche",
  "reporting.header.fill_gaps": "Compléter les mois sans donnée",
  "reporting.header.title": "Vue parc",
  "reporting.internal_gaps": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "reporting.internal_gaps.description":
    "Valeur mesurée - Valeur attendue\n(pour l'indicateur de performance sélectionné)",
  "reporting.internal_gaps.on_expected": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} internes (% valeur attendue)`,
  "reporting.internal_gaps_evolution.chart.internal_gaps": (translation: CustomTranslations) =>
    `${capitalize(translation.FR.LOSSES)} internes - Total`,
  "reporting.internal_gaps_evolution.info.description": (translations: CustomTranslations) =>
    `Cette vue présente les ${lowercase(
      translations.FR.LOSSES,
    )} internes par projet, qu'il est possible d'agréger par ${lowercase(
      translations.FR.TYPE,
    )} ou site.`,
  "reporting.internal_gaps_evolution.title": (translations: CustomTranslations) =>
    `Évolution des ${lowercase(translations.FR.LOSSES)} internes`,
  "reporting.internal_gaps_percentage": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.LOSSES)} internes (%)`,
  "reporting.internal_gaps_percentage.description": (translations: CustomTranslations) =>
    `${capitalize(
      translations.FR.LOSSES,
    )} internes / Valeur attendue\n(pour l'indicateur de performance sélectionné)`,
  "reporting.no_results": ({ from, to }: { from: string | null; to: string | null }) =>
    `Aucun rapport disponible${
      from && to ? `, pour la période du ${from} au ${to}` : ""
    }, avec les paramètres sélectionnés.`,
  "reporting.page.diagnosis": "Diagnostic",
  "reporting.page.performance": "Performance",
  "reporting.period_selector.1y": "1A",
  "reporting.period_selector.3m": "3M",
  "reporting.period_selector.6m": "6M",
  "reporting.period_selector.all_time": "Tout",
  "reporting.repairability.matrix.cell_impact.cost":
    "Coût total de l'impact des défaillances de cette catégorie de réparabilité : impact (MW) × temps de réparation (heures) × 60€/MWh",
  "reporting.repairability.matrix.empty_cell": "Aucune donnée à afficher",
  "reporting.repairability.matrix.fault_impact":
    "Impact de la défaillance sur l'ensemble des tranches concernées",
  "reporting.repairability.matrix.legend.badge": "TEM : 3 sem | ATF : 2 mois | AT : 7000h",
  "reporting.repairability.matrix.legend.cost":
    "Calcul du coût estimé : Impact (MW) × Temps de réparation (heures) × 60€/MWh",
  "reporting.repairability.matrix.legend.recurring_faults":
    "Défaillance présente le mois précédent :",
  "reporting.repairability.matrix.legend.unknown_repairability": ({ atf }: { atf: string }) =>
    `Réparabilité non précisée (groupée dans ${atf}) :`,
  "reporting.repairability.matrix.minor_faults.hide": "Masquer les défaillances mineures",
  "reporting.repairability.matrix.minor_faults.hide_text": ({
    count,
    icon,
  }: {
    count: number;
    icon: string;
  }) =>
    `${icon} Masquer ${count} défaillance${count > 1 ? "s" : ""} mineure${count > 1 ? "s" : ""} (impact < 0.5 MW)`,
  "reporting.repairability.matrix.minor_faults.show": "Afficher les défaillances mineures",
  "reporting.repairability.matrix.minor_faults.show_text": ({
    count,
    icon,
  }: {
    count: number;
    icon: string;
  }) =>
    `${icon} Afficher ${count} défaillance${count > 1 ? "s" : ""} mineure${count > 1 ? "s" : ""} (impact < 0.5 MW)`,
  "reporting.repairability.matrix.total": "Total :",
  "reporting.repairability.matrix.total_financial_impact": "Impact financier total :",
  "reporting.reparability_impact_matrix.chart.otherFailureCategory": "Autres catégories",
  "reporting.title.failures.date.range": (years: number, months: number) => {
    const r = [
      years > 0 && `${years} an${years > 1 ? "s" : ""}`,
      years > 0 && months > 0 && "et",
      months > 0 && `${months} mois`,
    ]
      .filter(Boolean)
      .join(" ");
    return `(${r})`;
  },
  "reporting.title.failures.order.alphabetical": "Alphabétique",
  "reporting.title.failures.order.impact": "Décroissant",
  "setting.cross_filter.import.error.data_quantity_duplicated": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : deux filtres ne peuvent pas porter sur la même source`,
  "setting.cross_filter.import.error.invalid_source_quantity": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : le champ 'maximumStd' ne peut pas être renseigné pour une grandeur calculée.`,
  "setting.filter.import.error.column_not_expected": ({ columnName }: { columnName: string }) =>
    `Erreur de format : la colonne ${columnName} n’est pas attendue pour ce type de fichier.`,
  "setting.filter.import.error.columns_expected": `Erreur de format : les colonnes attendues sont 'quantity', 'minimumValue', 'maximumValue' et 'maximumStd'.`,
  "setting.filter.import.error.duplicate_quantity_name": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Grandeur ${quantityName} : elle ne peut apparaître sur deux lignes différentes.`,
  "setting.filter.import.error.empty_csv": `Le csv est vide.`,
  "setting.filter.import.error.invalid_number_value": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Grandeur ${quantityName} : le champ 'minimumValue' doit contenir des valeurs numériques.`,
  "setting.filter.import.error.least_one_filter_value_required": ({
    quantityName,
  }: {
    quantityName: string;
  }) =>
    `Grandeur ${quantityName} : au moins l'un des champs 'minimumValue', 'maximumValue' ou 'maximumStd' doit être renseigné.`,
  "setting.filter.import.error.max_inferior_to_min": ({ quantityName }: { quantityName: string }) =>
    `Grandeur ${quantityName} : le champ 'minimumValue' doit contenir une valeur inférieure à celle du champ 'maximumValue'.`,
  "setting.filter.import.error.maximumStd_isnt_allowed_for_formulas": ({
    quantityName,
  }: {
    quantityName: string;
  }) =>
    `Grandeur ${quantityName} : le champ 'maximumStd' ne peut pas être renseigné pour une grandeur calculée.`,
  "setting.filter.import.error.quantity_not_existing_in_project": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Grandeur ${quantityName} : elle doit être une grandeur dans le projet.`,
  "setting.filter.import.error.std_positive": ({ quantityName }: { quantityName: string }) =>
    `Grandeur ${quantityName} : le champ 'maximumStd' doit contenir des entiers positifs.`,
  "setting.parser.display_filter.import.error.default_without_group": ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) =>
    `Le filtre "${name}" n'appartient à aucun groupe mais "Default" est défini à ${value}. Laissez Default vide pour les filtres sans groupe.`,
  "setting.parser.display_filter.import.error.filter_name_empty": ({ row }: { row: number }) =>
    `Filter Name is missing at row ${row}.`,
  "setting.parser.display_filter.import.error.filter_name_too_long": ({ name }: { name: string }) =>
    `Filter "${name}" cannot be longer than 50 characters.`,
  "setting.parser.display_filter.import.error.group_multiple_defaults": ({
    groupName,
  }: {
    groupName: string;
  }) =>
    `Le groupe "${groupName}" a plus d'un filtre par défaut. Un seul filtre par groupe peut être défini comme défaut.`,
  "setting.parser.display_filter.import.error.invalid_default_value": ({
    row,
    value,
  }: {
    row: number;
    value: string;
  }) =>
    `Valeur "${value}" invalide dans la colonne isDefault à la ligne ${row}. Les valeurs attendues sont "true" ou "false".`,
  "setting.parser.display_filter.import.error.invalid_number_maximum": ({
    row,
    value,
  }: {
    row: number;
    value: string;
  }) => `Valeur "${value}" invalide dans la colonne Max à la ligne ${row}. Un nombre est attendu.`,
  "setting.parser.display_filter.import.error.invalid_number_maximum_std": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filtre <strong>{name}</strong> - Grandeur <strong>{quantityName}</strong> : le champ
      &apos;maximumStd&apos; doit contenir des valeurs numériques positives.
    </>
  ),
  "setting.parser.display_filter.import.error.invalid_number_minimum": ({
    row,
    value,
  }: {
    row: number;
    value: string;
  }) => `Valeur "${value}" invalide dans la colonne Min à la ligne ${row}. Un nombre est attendu.`,
  "setting.parser.display_filter.import.error.invalid_source_quantity": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filtre <strong>{name}</strong> - Grandeur <strong>{quantityName}</strong> : le champ
      &apos;maximumStd&apos; ne peut pas être renseigné pour une grandeur calculée.
    </>
  ),
  "setting.parser.display_filter.import.error.least_one_filter_value_required": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filtre <strong>{name}</strong> - Grandeur <strong>{quantityName}</strong> : au moins l&apos;un
      des champs &apos;minimumValue&apos;, &apos;maximumValue&apos; ou &apos;maximumStd&apos; doit
      être renseigné.
    </>
  ),
  "setting.parser.display_filter.import.error.max_inferior_to_min": ({ row }: { row: number }) =>
    `La valeur Min est supérieure à la valeur Max à la ligne ${row}.`,
  "setting.parser.display_filter.import.error.quantity_empty": ({ row }: { row: number }) =>
    `La grandeur est manquante à la ligne ${row}.`,
  "setting.parser.display_filter.import.error.quantity_not_existing_in_project": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `La grandeur "${quantityName}" doit exister dans le projet.`,
  "setting.parser.display_filter.import.error.std_positive": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filtre <strong>{name}</strong> - Grandeur <strong>{quantityName}</strong> : le champ
      &apos;maximumStd&apos; doit contenir des entiers positifs.
    </>
  ),
  "setting.parser.quantity.display_filter.import.error.filter_name_not_unique": ({
    name,
    firstRow,
    secondRow,
  }: {
    name: string;
    firstRow: number;
    secondRow: number;
  }) =>
    `Nom de filtre "${name}" en doublon aux lignes ${firstRow} et ${secondRow}. Les noms de filtres doivent être uniques.`,
  "setting.parser.quantity.display_filter.import.error.invalid_columns": () =>
    `Les noms de colonnes obligatoires sont 'name', 'quantity', 'minimumValue', 'maximumValue' et 'maximumStd'.`,
  "setting.parser.quantity.import.error.invalid_number_maximum": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : le champ 'maximumValue' doit contenir des valeurs numériques.`,
  "setting.parser.quantity.import.error.invalid_number_maximum_std": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : le champ 'maximumStd' doit contenir des valeurs numériques positives.`,
  "setting.parser.quantity.import.error.invalid_number_minimum": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : le champ 'minimumValue' doit contenir des valeurs numériques.`,
  "setting.parser.quantity.import.error.least_one_filter_value_required": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : au moins l'un des champs 'minimumValue', 'maximumValue' ou 'maximumStd' doit être renseigné.`,
  "setting.parser.quantity.import.error.max_inferior_to_min": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : le champ 'minimumValue' doit contenir une valeur inférieure à celle du champ 'maximumValue'.`,
  "setting.parser.quantity.import.error.quantity_not_existing_in_project": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) => `Grandeur ${quantityName} - Source ${source} : elle doit être une grandeur dans le projet.`,
  "setting.parser.quantity.import.error.std_positive": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Grandeur ${quantityName} - Source ${source} : le champ 'maximumStd' doit contenir des entiers positifs.`,
  "settings.display_filter.table.column.group": "Nom du groupe",
  "settings.display_filter.table.column.name": "Nom du filtre",
  "settings.display_filter.table.column.quantity": "Grandeur",
  "setup.diagnosis.algorithm.link_button.edit": "Modifier",
  "setup.diagnosis.algorithm.modal.ok_button_name": "Modifier",
  "setup.diagnosis.algorithm.modal.title": "Modifier les paramètres de l'algorithme",
  "setup.diagnosis.algorithm.parameters.edit.text": "Paramètres modifiés",
  "setup.diagnosis.algorithm.title": "Algorithme",
  "setup.diagnosis.algorithm.tooltip.text":
    "L'algorithme utilisé par défaut est défini dans les réglages de l'application. Vous pouvez l’éditer au niveau de chaque paramétrage.",
  "setup.diagnosis.failure.blank.button": "Importer les défaillances",
  "setup.diagnosis.failure.blank.subtitle": "Importez la liste des défaillances du paramétrage",
  "setup.diagnosis.failure.blank.title": "Aucune défaillance définie",
  "setup.diagnosis.failure.compare.axis": "Impact normalisé",
  "setup.diagnosis.failure.compare.blocked":
    "Au moins deux défaillances sélectionnées doivent être disponibles dans cette configuration pour comparer leurs signatures.",
  "setup.diagnosis.failure.compare.button": "Comparer",
  "setup.diagnosis.failure.compare.chart.observable_count": (count: number) =>
    `${count} observable${count === 1 ? "" : "s"}`,
  "setup.diagnosis.failure.compare.chart.title": "Impact normalisé par observable",
  "setup.diagnosis.failure.compare.controls": "Options d’affichage du graphique",
  "setup.diagnosis.failure.compare.disabled.maximum":
    "Sélectionnez au maximum trois défaillances à comparer.",
  "setup.diagnosis.failure.compare.disabled.minimum":
    "Sélectionnez deux ou trois défaillances à comparer.",
  "setup.diagnosis.failure.compare.disabled.mode":
    "La comparaison des défaillances est uniquement disponible dans la matrice de défaillances.",
  "setup.diagnosis.failure.compare.empty":
    "Aucune observable ne satisfait cet écart minimal. Diminuez le pourcentage minimal pour les afficher.",
  "setup.diagnosis.failure.compare.excluded": (faults: string) =>
    `Indisponible dans cette configuration : ${faults}.`,
  "setup.diagnosis.failure.compare.minimum_difference": "Écart minimal",
  "setup.diagnosis.failure.compare.minimum_difference.summary":
    "Augmentez cette valeur pour afficher moins d’observables.",
  "setup.diagnosis.failure.compare.minimum_difference.tooltip":
    "Une observable est affichée lorsque l’écart entre l’impact normalisé le plus élevé et le plus faible des défaillances dépasse ce pourcentage. Augmentez-le pour vous concentrer sur les observables qui distinguent le mieux les défaillances sélectionnées.",
  "setup.diagnosis.failure.compare.score": "Score de colinéarité",
  "setup.diagnosis.failure.compare.score.level.distinct": "Distinctes",
  "setup.diagnosis.failure.compare.score.level.similar": "Similaires",
  "setup.diagnosis.failure.compare.score.level.very_similar": "Très similaires",
  "setup.diagnosis.failure.compare.score.tooltip":
    "Le score est l’angle entre deux vecteurs de défaillance normalisés par sigma puis par norme L2.",
  "setup.diagnosis.failure.compare.select": (fault: string) =>
    `Sélectionner ${fault} pour la comparaison`,
  "setup.diagnosis.failure.compare.setup": "Configuration",
  "setup.diagnosis.failure.compare.sort.alphabetical": "Alphabétique",
  "setup.diagnosis.failure.compare.sort.label": "Trier les observables par",
  "setup.diagnosis.failure.compare.title": "Comparer les signatures de défaillance",
  "setup.diagnosis.failure.confirm.activate.title": "Activer une défaillance",
  "setup.diagnosis.failure.confirm.deactivate.title": "Désactiver une défaillance",
  "setup.diagnosis.failure.filter_observables": "Filtrer les observables",
  "setup.diagnosis.failure.import.new_categories":
    "Le fichier comporte de nouvelles catégories, qui seront créées à la validation de l'import.",
  "setup.diagnosis.failure.import.new_categories.summary": "Détails des nouvelles catégories",
  "setup.diagnosis.failure.show_disabled": "Afficher les défaillances desactivées",
  "setup.diagnosis.failure.show_unused": "Afficher les grandeurs non utilisées pour le diagnostic",
  "setup.diagnosis.failure.title": "Défaillances",
  "setup.diagnosis.failure.tooltip.color":
    "Couleur associée à la représentation de la défaillance sur les graphiques",
  "setup.diagnosis.failures.delete_failures.toast.success": "Les défaillances ont été supprimées",
  "setup.diagnosis.failures.delete_observables.title": "Supprimer les défaillances ?",
  "setup.diagnosis.failures.import_checking": "Les défaillances ont été importées",
  "setup.diagnosis.failures.modal.error.activated_invalid": ({
    nameFailures,
  }: {
    nameFailures: string;
  }) => `Défaillance ${nameFailures} : le champ 'activated' doit contenir un 0 ou un 1`,
  "setup.diagnosis.failures.modal.error.bad_impacts": ({
    failure,
    quantity,
  }: {
    failure: string;
    quantity: string;
  }) =>
    `Défaillance ${failure} : l'impact sur la grandeur ${quantity} doit être une valeur numérique`,
  "setup.diagnosis.failures.modal.error.bad_magnitude": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Magnitude doit être un nombre non null pour ${
      nameFailures.length > 0 ? "les défaillances" : "la défaillance"
    } ${nameFailures}`,
  "setup.diagnosis.failures.modal.error.duplicated_technical_name": ({
    nameFaults,
  }: {
    nameFaults: string[];
  }) =>
    `Défaillance(s) ${nameFaults.join(
      ", ",
    )} : une défaillance ne peut apparaître sur deux lignes différentes`,
  "setup.diagnosis.failures.modal.error.filterMinMax_must_be_number": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : les champs 'filterMin' et 'filterMax' doivent contenir des valeurs numériques`,
  "setup.diagnosis.failures.modal.error.filterMin_must_be_less_than_FilterMax": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : le champ 'filterMin' doit contenir une valeur inférieure à celle du champ 'filterMax'.`,
  "setup.diagnosis.failures.modal.error.filter_formula_unknown_quantity": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : le champ 'FilterFormula' doit se baser sur une grandeur du projet.`,
  "setup.diagnosis.failures.modal.error.invalid_filter_type": ({
    failure,
    allowed,
  }: {
    failure: string;
    allowed: string[];
  }) => {
    const allowedFilter =
      allowed.length === 1
        ? `‘${allowed[0]}’`
        : `‘${allowed.slice(0, -1).join("’, ‘")}’ ou ‘${allowed.at(-1)}’`;
    return `Défaillance(s) ${failure}: la valeur utilisée dans le champ 'filterType' doit être ${allowedFilter}.`;
  },
  "setup.diagnosis.failures.modal.error.max_not_a_number": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures} : le champ 'maximumValue' doit contenir une valeur numérique.`,
  "setup.diagnosis.failures.modal.error.min_greater_than_max": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures} : le champ 'minimumValue' doit contenir une valeur inférieure à celle du champ 'maximumValue'.`,
  "setup.diagnosis.failures.modal.error.min_not_a_number": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures} : le champ 'minimumValue' doit contenir une valeur numérique`,
  "setup.diagnosis.failures.modal.error.missingMandatoryColumns": ({
    properties,
  }: {
    properties: string[];
  }) =>
    `${properties.length > 1 ? " Les colonnes " : " La colonne "} ${properties.join(", ")} ${
      properties.length > 1 ? " sont requises " : " est requise "
    }dans le fichier`,
  "setup.diagnosis.failures.modal.error.missing_column": ({
    properties,
  }: {
    properties: string[];
  }) => `Erreur de format : les colonnes attendues sont ${properties.join(", ")}`,
  "setup.diagnosis.failures.modal.error.missing_filterFomula_dependencied": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures} : lorsque le champ 'filterFormula' est renseigné, au moins une des champs 'filterMin' ou 'filterMax' doit être renseigné.`,
  "setup.diagnosis.failures.modal.error.name_not_human_readable": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) => `Défaillance(s) ${nameFailures} : le nom de la défaillance n'a pas le bon format`,
  "setup.diagnosis.failures.modal.error.name_too_long": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `La taille de "name" doit être limité à 100 caractères pour ${
      nameFailures.length > 1 ? "les défaillances" : "la défaillance"
    } ${nameFailures}`,
  "setup.diagnosis.failures.modal.error.unit_too_long": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Le champ 'unit' doit contenir moins de 100 caractères pour ${
      nameFailures.length > 1 ? "les défaillances" : "la défaillance"
    } ${nameFailures}`,
  "setup.diagnosis.failures.modal.error.unknown_quantity": ({
    failureNames,
  }: {
    failureNames: string[];
  }) => (
    <>
      Colonne(s) {failureNames.join(", ")} : le nom de la colonne ne correspond pas à une grandeur
      du projet
    </>
  ),
  "setup.diagnosis.failures.modal.error.wrongInputMultiValue": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : l'élément n'a pas le bon format pour une entrée à multiples valeurs`,
  "setup.diagnosis.failures.modal.error.wrongInputReparability": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : le type de réparabilité de la défaillance n'a pas le bon format`,
  "setup.diagnosis.failures.modal.error.wrong_duplication_filterFomula": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : il n'est pas possible de déclarer deux filterFormula du même type reposant sur la même grandeur`,
  "setup.diagnosis.failures.modal.error.wrong_format_filterFormula": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : le format utilisé dans le champ 'filterFormula' est incorrect`,
  "setup.diagnosis.failures.modal.error.wrong_format_filterMax": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : le champ 'filterMax' doit être vide lorsque le champ 'filterFormula' est vide.`,
  "setup.diagnosis.failures.modal.error.wrong_format_filterMin": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Défaillance(s) ${nameFailures.join(
      ", ",
    )} : le champ 'filterMin' doit être vide lorsque le champ 'filterFormula' est vide.`,
  "setup.diagnosis.failures.modal.permissionRequired.notAllow": ({
    infos,
  }: {
    infos: string[];
  }) => (
    <>
      Vous n&apos;avez pas les droits pour la création de nouveaux types de grandeurs :{" "}
      {printListStrings(infos, "et")} .
    </>
  ),
  "setup.diagnosis.faults.modal.error.display_name_empty": ({
    nameFaults,
  }: {
    nameFaults: string[];
  }) =>
    `Le champ "displayName" doit être renseigné lorsque la colonne est présente,
    il est vide pour ${
      nameFaults.length > 1 ? "les défaillances" : "la défaillance"
    } ${nameFaults.join(", ")}`,
  "setup.diagnosis.faults.modal.error.duplicated_display_name": ({
    technicalNames,
  }: {
    technicalNames: string[];
  }) =>
    `Noms d'affichage de défaillances en doublon pour les défaillances avec le nom technique ${technicalNames.join(", ")}`,
  "setup.diagnosis.faults.modal.error.technical_name_empty": ({ lines }: { lines: string[] }) =>
    `Le nom technique ne peut pas être vide pour ${lines.length > 1 ? "les lignes" : "la ligne"} ${lines.join(", ")}`,
  "setup.diagnosis.faults.modal.error.technical_name_too_long": ({
    nameFaults,
  }: {
    nameFaults: string[];
  }) =>
    `Le champ "technicalName" ne doit pas dépasser 100 caractères,
      il est trop long pour ${
        nameFaults.length > 1 ? "les défaillances" : "la défaillance"
      } ${nameFaults.join(", ")}`,
  "setup.diagnosis.modal_form.algorithm.input_boolean.value_diff_default_value": (
    defaultValue: boolean,
  ) =>
    `Modifiée: par défaut, la case à cocher devrait être  ${defaultValue ? "cochée" : "décochée"}.`,
  "setup.diagnosis.modal_form.algorithm.input_number.error.max": (max: number) =>
    `La valeur ne peut pas être supérieure à ${max}`,
  "setup.diagnosis.modal_form.algorithm.input_number.error.min": (min: number) =>
    `La valeur ne peut pas être inférieure à ${min}`,
  "setup.diagnosis.modal_form.algorithm.input_number.value_diff_default_value": (
    defaultValue: string,
  ) => `Modifiée: la valeur par défaut est ${defaultValue}.`,
  "setup.diagnosis.modal_form.algorithm.message.update_algorithm": (nbAnalyses: number) =>
    `Dès modification de la version de l'algorithme et/ou de ses paramètres, les calculs de diagnostic seront impactés pour ${nbAnalyses} analyse${
      nbAnalyses > 1 ? "s" : ""
    }.`,
  "setup.diagnosis.modal_form.algorithm.select.label": "Version",
  "setup.diagnosis.modal_form.section.modifiable_parameters": "Paramètres modifiables",
  "setup.diagnosis.observable.blank.button": "Importer les observables",
  "setup.diagnosis.observable.blank.subtitle": "Importez la liste des observables du paramétrage",
  "setup.diagnosis.observable.blank.title": "Aucune observable définie",
  "setup.diagnosis.observable.confirm.edit.body": (quantityName: string) => (
    <>
      Souhaitez-vous confirmer la modification de <strong>{quantityName}</strong> ?
    </>
  ),
  "setup.diagnosis.observable.confirm.edit.title": "Confirmer la modification",
  "setup.diagnosis.observable.confirm.toggle.body": (enable: boolean, quantityName: string) =>
    enable ? (
      <>Souhaitez-vous confirmer la désactivation de {quantityName}</>
    ) : (
      <>Souhaitez-vous confirmer l&amp;activation de {quantityName}</>
    ),
  "setup.diagnosis.observable.confirm.toggle.title": (enable: boolean): string =>
    enable ? "Activer l'observable" : "Désactiver l'observable",
  "setup.diagnosis.observable.delete_observables.body": () =>
    "Souhaitez-vous confirmer la suppression des observables ?",
  "setup.diagnosis.observable.delete_observables.title": "Supprimer les observables",
  "setup.diagnosis.observable.delete_observables.toast.success": ({ name }: { name: string }) =>
    `Les observables du paramétrage ${name} ont été supprimés.`,
  "setup.diagnosis.observable.import_observables.error.data_activated_invalid": (
    quantityId: string,
  ) => `Grandeur ${quantityId} : le champ 'activatedObservable' doit contenir un 0 ou un 1.`,
  "setup.diagnosis.observable.import_observables.error.data_duplicated": (quantityId: string) =>
    `Grandeur ${quantityId} : une grandeur ne peut apparaître sur deux lignes différentes`,
  "setup.diagnosis.observable.import_observables.error.data_quantity_id_missing":
    "Le champ 'name' doit être renseigné.",
  "setup.diagnosis.observable.import_observables.error.data_quantity_id_not_allowed": (
    quantityId: string,
  ) => `Grandeur ${quantityId} : le champ 'name' doit contenir une grandeur définie dans le projet`,
  "setup.diagnosis.observable.import_observables.error.data_sigma_activated_required": (
    quantityId: string,
  ) =>
    `Grandeur ${quantityId} : le champ 'observableSigmaDiagnosis' doit être renseigné lorsque la grandeur est une observable`,
  "setup.diagnosis.observable.import_observables.error.data_sigma_invalid": (quantityId: string) =>
    `Grandeur ${quantityId} : le champ 'observableSigmaDiagnosis' doit contenir des nombres strictement positifs`,
  "setup.diagnosis.observable.import_observables.error.empty_file": "Le fichier est vide.",
  "setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded":
    "Le fichier dépasse la taille limite de 20 Mo.",
  "setup.diagnosis.observable.import_observables.error.invalid_content":
    "Erreur de format : les colonnes attendues sont 'name', 'activatedObservable' et 'observableSigmaDiagnosis'.",
  "setup.diagnosis.observable.import_observables.error.invalid_format":
    "Le fichier doit être au format csv.",
  "setup.diagnosis.observable.import_observables.title": "Importer les observables",
  "setup.diagnosis.observable.import_observables.toast.success": ({ count }: { count: number }) =>
    count === 1 ? `${count} observable a été définie` : `${count} observables ont été définies`,
  "setup.diagnosis.observable.import_quantities.error.invalid_format":
    "Le fichier doit être au format csv.",
  "setup.diagnosis.observable.show_disabled": "Afficher les observables désactivées",
  "setup.diagnosis.observable.table.column.description": "Description",
  "setup.diagnosis.observable.table.column.description.hint":
    "Lié à la colonne Description dans la table des grandeurs",
  "setup.diagnosis.observable.table.column.offset.hint":
    "Lié à la colonne Offset dans la table des sorties de modèle dans les paramètres de simulation",
  "setup.diagnosis.observable.table.column.quantity": "Grandeur",
  "setup.diagnosis.observable.table.column.sigma": "Sigma",
  "setup.diagnosis.observable.table.column.unit": "Unité",
  "setup.diagnosis.observable.table.column.unit.hint":
    "Lié à la colonne Unité dans la table des grandeurs",
  "setup.diagnosis.observable.title": "Observables",
  "setup.diagnosis.status.complete": () => (
    <>
      Le paramétrage du diagnostic est <strong>valide</strong>
    </>
  ),
  "setup.diagnosis.status.incomplete": () => (
    <>
      Le paramétrage du diagnostic est <strong>incomplet</strong> car :
    </>
  ),
  "setup.diagnosis.status.incomplete.noEnabledFailure": "toutes les défaillances sont désactivées",
  "setup.diagnosis.status.incomplete.noEnabledInput": "toutes les observables sont désactivées",
  "setup.diagnosis.status.incomplete.noFailure": "aucune matrice de défaillances n'a été importée",
  "setup.diagnosis.status.incomplete.noImpact":
    "aucun impact défini dans la matrice des défaillances",
  "setup.diagnosis.status.incomplete.noInput": "aucune liste d’observables n'a été importée",
  "setup.diagnosis.status.invalid": () => (
    <>
      Le paramétrage du diagnostic est <strong>invalide</strong> car les grandeurs suivantes sont
      déclarées comme des observables mais :
    </>
  ),
  "setup.diagnosis.status.invalid.missingImpact": (QuantitiesName: string[]) =>
    `ne sont pas présentes dans la liste de défaillance : ${QuantitiesName.join(", ")}`,
  "setup.diagnosis.status.invalid.notSimulationOutput": (QuantitiesName: string[]) =>
    `ne sont pas des sorties du modèle : ${QuantitiesName.join(", ")}`,
  "setup.failure.table.failure_filter.definitions.column.failure": "Défaillance",
  "setup.failure.table.failure_filter.definitions.column.maximum": "Max",
  "setup.failure.table.failure_filter.definitions.column.minimum": "Min",
  "setup.failure.table.failure_filter.definitions.column.quantity": "Grandeur",
  "setup.failure.table.failure_filter.definitions.column.rule": "Filtre",
  "setup.failure.table.failure_filter.definitions.column.rule.tooltip": () => (
    <>
      • Filtre de contrainte : la défaillance ne sera considérée par l’algorithme de diagnostic que
      si le filtre est respecté.
      <br />• Filtre d’incitation : la défaillance aura plus de chance d’être proposée par
      l’algorithme de diagnostic si le filtre est respecté.
    </>
  ),
  "setup.failure.table.failure_filter.definitions.value.rule.constraint": "Contrainte",
  "setup.failure.table.failure_filter.definitions.value.rule.promotion": "Incitation",
  "setup.failures.table.selectors.filter": "Filtres de défaillance",
  "setup.failures.table.selectors.filter.disabled":
    "Aucun filtre de défaillance défini. Importez un nouveau fichier pour les définir.",
  "setup.failures.table.selectors.matrix": "Matrice de défaillance",
  "setup.header.nav.diagnosis": "Diagnostic",
  "setup.header.nav.simulation": "Simulation",
  "setup.import.fileName": "Fichier chargé :",
  "setup.import.quantities.message.info.ignore_sensor_tags.details": (
    quantity: string,
    sensorTag: string,
  ) => `${quantity} ${sensorTag ? `(tag capteur : ${sensorTag}).` : ""}`,
  "setup.import.quantities.message.info.ignore_sensor_tags.details.summary":
    "Détails des grandeurs ignorées",
  "setup.import.quantities.message.info.to_be_created": `Le fichier comporte de nouvelles catégories, qui seront créées à la validation de l'import.`,
  "setup.import.quantities.message.info.to_be_created.details.equipment": (equipments: string[]) =>
    `Équipement${equipments.length > 1 ? "s" : ""} : ${equipments.join(", ")}.`,
  "setup.import.quantities.message.info.to_be_created.details.equipment_type": (
    equipmentType: string[],
  ) => `Type${equipmentType.length > 1 ? "s" : ""} d'équipement : ${equipmentType.join(", ")}.`,
  "setup.import.quantities.message.info.to_be_created.details.summary":
    "Détails des nouvelles catégories",
  "setup.import.quantities.message.info.to_be_created.details.system": (systems: string[]) =>
    `Système${systems.length > 1 ? "s" : ""}  : ${systems.join(", ")}.`,
  "setup.import.quantities.message.warning.ignore_sensorTag":
    "Certaines grandeurs du fichiers ont été ignorées car leur sensorTag n’a pas été identifié dans la source de données associée au projet.",
  "setup.modal.add_setup.toast": ({ name }: { name: string }) =>
    `Le paramétrage ${name} a été créé`,
  "setup.modal.delete.analysis.additionnal_message.warning": ({
    simulationValid,
    diagnosisValid,
    impactedAnalysisCount,
  }: {
    simulationValid?: boolean;
    diagnosisValid: boolean;
    impactedAnalysisCount: number;
  }) => {
    const simulationResults = simulationValid ? "de simulation" : "";
    const diagnosisResults = diagnosisValid ? "de diagnostic" : "";
    const and = simulationValid && diagnosisValid ? "et" : "";
    const analysis = `${impactedAnalysisCount} analyse${impactedAnalysisCount > 1 ? "s" : ""}`;
    return `Suite à cette action, les éventuels résultats ${simulationResults} ${and} ${diagnosisResults} seront effacés pour ${analysis}.`;
  },
  "setup.modal.duplicate_setup.toast.success": ({ name }: { name: string }) =>
    `Le paramétrage ${name} a été dupliqué`,
  "setup.modal.update_setup.toast": ({
    setupName,
    name,
    description,
  }: {
    setupName: string;
    name: boolean;
    description: boolean;
  }) =>
    name && description
      ? `Nom et description du paramétrage ${setupName} modifiés`
      : name
        ? `Nom du paramétrage modifié`
        : `Description du paramétrage ${setupName} modifiée`,
  "setup.paragraphs.failures.modal.delete_failures.body":
    "Souhaitez-vous confirmer la suppression des défaillances ?",
  "setup.paragraphs.failures.modal.import_failures": "Importer des défaillances",
  "setup.paragraphs.failures.table.column.category": "Catégorie",
  "setup.paragraphs.failures.table.column.degradationType": "Type de dégradation",
  "setup.paragraphs.failures.table.column.description": "Description",
  "setup.paragraphs.failures.table.column.displayName": "Nom d'affichage",
  "setup.paragraphs.failures.table.column.equipmentType": "Type d'équipement",
  "setup.paragraphs.failures.table.column.equipments": "Équipements",
  "setup.paragraphs.failures.table.column.magnitude": "Amplitude",
  "setup.paragraphs.failures.table.column.max": "Max",
  "setup.paragraphs.failures.table.column.min": "Min",
  "setup.paragraphs.failures.table.column.reparability": "Réparabilité",
  "setup.paragraphs.failures.table.column.separator": "Impacts",
  "setup.paragraphs.failures.table.column.suspectedElements": "Éléments suspectés",
  "setup.paragraphs.failures.table.column.system": "Système",
  "setup.paragraphs.failures.table.column.technicalName": "Nom technique",
  "setup.paragraphs.failures.table.column.unit": "Unité",
  "setup.paragraphs.failures.table.definitions.column.degradationType": "Type de dégradation",
  "setup.paragraphs.failures.table.definitions.column.failureCategory": "Catégorie",
  "setup.paragraphs.failures.table.definitions.column.roles.observable.disabled.tooltip":
    "OBS : Observable désactivée",
  "setup.paragraphs.failures.table.definitions.column.roles.observable.label": "OBS",
  "setup.paragraphs.failures.table.definitions.column.roles.observable.tooltip": "OBS : Observable",
  "setup.paragraphs.failures.table.definitions.column.roles.quantity_of_interest.label": "IP",
  "setup.paragraphs.failures.table.definitions.column.roles.quantity_of_interest.tooltip":
    "IP : Indicateur de performance",
  "setup.paragraphs.failures.table.failures.no_failures.blank_state.button":
    "Importer les défaillances",
  "setup.paragraphs.failures.table.failures.no_failures.blank_state.no_quantities.text":
    "Une fois les grandeurs définies, vous pourrez importer le fichier des défaillances dans cette section.",
  "setup.paragraphs.failures.table.failures.no_failures.blank_state.title":
    "La bibliothèque de défaillances est vide.",
  "setup.paragraphs.model": "Modèle",
  "setup.paragraphs.model.actions.import.modal.select.prompt": "Choisissez un type de modèle",
  "setup.paragraphs.model.blank_state.button": "Importer le modèle",
  "setup.paragraphs.model.blank_state.text":
    "Importez le jumeau numérique de votre installation pour lancer des simulations.",
  "setup.paragraphs.model.blank_state.title": "Aucun modèle chargé.",
  "setup.paragraphs.model.error": "Erreur",
  "setup.paragraphs.model.error.copied": "Message d'erreur copié",
  "setup.paragraphs.model.error.copy": "Copier l'ensemble des messages d'erreur",
  "setup.paragraphs.model.error.download": "Message d’erreur téléchargé",
  "setup.paragraphs.model.info.label.files": "Fichiers chargés :",
  "setup.paragraphs.model.info.label.image": "Image :",
  "setup.paragraphs.model.info.label.type": "Type : ",
  "setup.paragraphs.model.modal.delete_image.text":
    "Souhaitez-vous confirmer la suppression de l'image associée au modèle ?",
  "setup.paragraphs.model.modal.delete_image.title": "Supprimer l'image",
  "setup.paragraphs.model.modal.delete_image.toast.success": "L'image a été supprimée",
  "setup.paragraphs.model.modal.delete_input.text":
    "Souhaitez-vous confirmer la suppression des entrées du modèle ?",
  "setup.paragraphs.model.modal.delete_input.title": "Supprimer les entrées du modèle",
  "setup.paragraphs.model.modal.delete_model.text":
    "Souhaitez-vous confirmer la suppression du modèle ?",
  "setup.paragraphs.model.modal.delete_model.title": "Supprimer le modèle",
  "setup.paragraphs.model.modal.delete_model.warningText": ({
    impactedAnalysisCount,
  }: {
    impactedAnalysisCount: number;
  }) => (
    <>
      Suite à cette action, les calculs de diagnostic seront impactés pour{" "}
      <strong>
        {impactedAnalysisCount} analyse{impactedAnalysisCount > 1 ? "s" : ""}
      </strong>
      .
    </>
  ),
  "setup.paragraphs.model.modal.delete_output.text":
    "Souhaitez-vous confirmer la suppression des sorties du modèle ?",
  "setup.paragraphs.model.modal.delete_output.title": "Supprimer les sorties du modèle",
  "setup.paragraphs.model.modal.update_image.title": "Importer l'image associée au modèle",
  "setup.paragraphs.model.modal.update_image.toast.success": "L'image a été mise à jour",
  "setup.paragraphs.model.synoptic.no_model.title": "Aucune image associée au modèle.",
  "setup.paragraphs.model.synoptic.no_model.title.short": "Aucune image",
  "setup.paragraphs.model.synoptic.no_model.upload_a_model.text":
    "Chargez l'image de votre installation.",
  "setup.paragraphs.projects.blank_state.button": "Créer un nouveau projet",
  "setup.paragraphs.quantities.input": "Entrées du modèle",
  "setup.paragraphs.quantities.input.blank_subtitle":
    "Importez les grandeurs à associer aux entrées du modèle.",
  "setup.paragraphs.quantities.input.blank_title": "Les entrées du modèle ne sont pas définies.",
  "setup.paragraphs.quantities.input.import.button": "Importer les entrées du modèle",
  "setup.paragraphs.quantities.output": "Sorties du modèle",
  "setup.paragraphs.quantities.output.blank_subtitle":
    "Importez les grandeurs à associer aux sorties du modèle.",
  "setup.paragraphs.quantities.output.blank_title": "Les sorties du modèle ne sont pas définies.",
  "setup.paragraphs.quantities.output.import.button": "Importer les sorties du modèle",
  "setup.paragraphs.quantities.table.definitions.column.actions": "Actions",
  "setup.paragraphs.quantities.table.definitions.column.description": "Description",
  "setup.paragraphs.quantities.table.definitions.column.equipmentType": "Type d'équipements",
  "setup.paragraphs.quantities.table.definitions.column.equipments": "Équipements",
  "setup.paragraphs.quantities.table.definitions.column.formula": "Formule en cours",
  "setup.paragraphs.quantities.table.definitions.column.hidden": "Calcul intermédiaire",
  "setup.paragraphs.quantities.table.definitions.column.hidden.no": "Non",
  "setup.paragraphs.quantities.table.definitions.column.hidden.yes": "Oui",
  "setup.paragraphs.quantities.table.definitions.column.inputModelName":
    "Variable d'entrée du modèle",
  "setup.paragraphs.quantities.table.definitions.column.max": "Max",
  "setup.paragraphs.quantities.table.definitions.column.maxStd": "Écart-type max",
  "setup.paragraphs.quantities.table.definitions.column.min": "Min",
  "setup.paragraphs.quantities.table.definitions.column.name": "Nom",
  "setup.paragraphs.quantities.table.definitions.column.name.tooltip": "Grandeur calculée",
  "setup.paragraphs.quantities.table.definitions.column.sensorTag": "Tag capteur",
  "setup.paragraphs.quantities.table.definitions.column.system": "Système",
  "setup.paragraphs.quantities.table.definitions.column.tagDataSource": "Tag capteur",
  "setup.paragraphs.quantities.table.definitions.column.technicalName": "Nom technique",
  "setup.paragraphs.quantities.table.definitions.column.type": "Nature",
  "setup.paragraphs.quantities.table.definitions.column.unit": "Unité",
  "setup.paragraphs.quantities.table.definitions.column.value": "Formule en cours",
  "setup.paragraphs.quantities.table.locked.tooltip":
    "Formule verrouillée, modifiable uniquement par les paramétreurs et admins",
  "setup.quantities.edit.max.inferior.min.error":
    "La valeur maximale doit être supérieure à la valeur minimale",
  "setup.quantities.edit.min.superior.max.error":
    "La valeur minimale doit être inférieure à la valeur maximale",
  "setup.quantities.import.error.missing_columns_helium":
    "Erreur de format : les colonnes attendues sont 'technicalName', 'calculatedValueExpression', 'parameterValue' et 'tagDataSourceImageValue'.",
  "setup.quantities.import.error.missing_columns_lithium":
    "Erreur de format : les colonnes attendues sont 'name', 'sensorTag' et 'formula'.",
  "setup.quantity.import.error.data_description_too_long": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'description' doit contenir moins de 500 caractères.`,
  "setup.quantity.import.error.data_file_empty": "Le CSV est vide.",
  "setup.quantity.import.error.data_formula_too_long": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'formula' doit contenir moins de 4000 caractères.`,
  "setup.quantity.import.error.data_formula_version_base_formula_required": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : une grandeur de formule versionnée doit avoir une ligne de base sans 'startDate' avec une formule.`,
  "setup.quantity.import.error.data_formula_version_base_sensor_tag_not_allowed": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : la ligne de base d'une grandeur de formule versionnée ne peut pas définir de 'sensorTag'.`,
  "setup.quantity.import.error.data_formula_version_duplicate_startsAt": (
    quantityName: string,
    startsAt: string,
  ) => {
    void startsAt;
    return `Grandeur ${quantityName} : deux lignes ne peuvent pas avoir le même 'startDate' pour une même grandeur.`;
  },
  "setup.quantity.import.error.data_formula_version_empty_formula": (
    quantityName: string,
    startsAt: string,
  ) =>
    `Grandeur ${quantityName} : le champ 'formula' est requis pour la version démarrant le ${startsAt}.`,
  "setup.quantity.import.error.data_formula_version_metadata_mismatch": (quantityName: string) =>
    `Grandeur ${quantityName} : les lignes versionnées ne peuvent différer de la ligne de base que par 'formula' et 'startDate' ; tout autre champ renseigné doit correspondre à la ligne de base.`,
  "setup.quantity.import.error.data_formula_version_multiple_base_rows": (quantityName: string) =>
    `Grandeur ${quantityName} : une grandeur de formule versionnée ne peut pas avoir plusieurs lignes sans 'startDate'.`,
  "setup.quantity.import.error.data_formula_version_sensor_tag_mismatch": (quantityName: string) =>
    `Grandeur ${quantityName} : deux versions d’une grandeur ne peuvent pas avoir des sensorTag différents`,
  "setup.quantity.import.error.data_formula_version_unit_mismatch": (quantityName: string) =>
    `Grandeur ${quantityName} : deux versions d’une grandeur ne peuvent pas avoir des unités différentes`,
  "setup.quantity.import.error.data_input_model_name_too_long": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'inputModelName' doit contenir moins de 100 caractères.`,
  "setup.quantity.import.error.data_invalid_equipments_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'correspondingEquipmentType' doit avoir pour séparateur une virgule.`,
  "setup.quantity.import.error.data_invalid_equipments_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'equipments' doit avoir pour séparateur une virgule.`,
  "setup.quantity.import.error.data_invalid_formula": (quantityName: string) =>
    `Grandeur ${quantityName} : la formule est invalide.`,
  "setup.quantity.import.error.data_invalid_hidden_field": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'hidden' doit contenir des valeurs booléennes (true ou false) ou être vide`,
  "setup.quantity.import.error.data_invalid_locked_field": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'locked' doit contenir des valeurs booléennes (true ou false) ou être vide`,
  "setup.quantity.import.error.data_invalid_permission_equipment_creation": (
    quantityName: string,
  ) => `Grandeur ${quantityName} : permission pour la créer de nouveau équipement requise.`,
  "setup.quantity.import.error.data_invalid_permission_equipment_type_creation": (
    quantityName: string,
  ) => `Grandeur ${quantityName} : permission pour la créer de nouveau type d'équipement requise.`,
  "setup.quantity.import.error.data_invalid_permission_system_creation": (quantityName: string) =>
    `Grandeur ${quantityName} : permission pour créer de nouveau système requise.`,
  "setup.quantity.import.error.data_invalid_quantity_name_format_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'technicalName' ne doit contenir que des caractères alphanumériques et des _, sans espace.`,
  "setup.quantity.import.error.data_invalid_quantity_name_format_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'name' ne doit contenir que des caractères alphanumériques et des _, sans espace.`,
  "setup.quantity.import.error.data_invalide_pair_value_formula_sensor_helium": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : les champs 'formula' et 'tagDataSourceImageValue' ne doivent pas être renseignés en même temps.`,
  "setup.quantity.import.error.data_invalide_pair_value_formula_sensor_lithium": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : les champs 'formula' et 'sensorTag' ne doivent pas être renseignés en même temps.`,
  "setup.quantity.import.error.data_maxStd_with_formula_invalid_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'filterMax' ne doit pas être renseigné lorsque la grandeur est une formule.`,
  "setup.quantity.import.error.data_maxStd_with_formula_invalid_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'maximumStd' ne doit pas être renseigné lorsque la grandeur est une formule.`,
  "setup.quantity.import.error.data_min_max_std_invalid_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : les champs 'filterMin' et 'filterMax' doivent contenir des nombres.`,
  "setup.quantity.import.error.data_min_max_std_invalid_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : les champs 'minimumValue', 'maximumValue' et 'maximumStd' doivent contenir des nombres.`,
  "setup.quantity.import.error.data_min_sup_max_invalid_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : la valeur du champ 'filterMin' doit être inférieure à celle du champ 'filterMax'.`,
  "setup.quantity.import.error.data_min_sup_max_invalid_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : la valeur du champ 'minValue' doit être inférieure à celle du champ 'maxValue'.`,
  "setup.quantity.import.error.data_negative_maxStd_invalid_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'filterMax' doit contenir un nombre positif.`,
  "setup.quantity.import.error.data_negative_maxStd_invalid_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'maximumStd' doit contenir un nombre positif.`,
  "setup.quantity.import.error.data_output_model_name_too_long": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'outputModelName' doit contenir moins de 100 caractères.`,
  "setup.quantity.import.error.data_quantity_duplicated": (quantityName: string) =>
    `Grandeur ${quantityName} : une grandeur ne peut apparaître sur deux lignes différentes.`,
  "setup.quantity.import.error.data_quantity_name_too_long_helium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'technicalName' doit contenir moins de 100 caractères.`,
  "setup.quantity.import.error.data_quantity_name_too_long_lithium": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'name' doit contenir moins de  100 caractères.`,
  "setup.quantity.import.error.data_sensor_tag_too_long": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'sensorTag' doit contenir moins de 100 caractères.`,
  "setup.quantity.import.error.data_start_date_invalid_format": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'startDate' doit être au format YYYY-MM-DD, YYYY-MM-DD HH:mm(:ss) ou ISO UTC (ex. 2024-01-01T00:00:00.000Z)`,
  "setup.quantity.import.error.data_start_date_missing_base": (quantityName: string) =>
    `Grandeur ${quantityName} : une autre ligne doit exister pour la même grandeur avec une 'startDate' vide`,
  "setup.quantity.import.error.data_unit_too_long": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'unit' doit contenir moins de 100 caractères.`,
  "setup.quantity.import.error.locked_true_without_formula": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'locked' ne peut pas être à true pour une grandeur liée à un capteur`,
  "setup.quantity.import.error.missing_sensortag_or_formula": (quantityName: string) =>
    `Grandeur ${quantityName} : au moins un des champs 'sensorTag' ou 'formula' doit être renseigné.`,
  "setup.simulation.blank_state.button": "Importer tout ou partie des fichiers",
  "setup.simulation.blank_state.text":
    "Importez les fichiers définissant le modèle, ainsi que ses entrées et sorties",
  "setup.simulation.blank_state.title":
    "Le paramétrage de la brique Simulation est actuellement vide",
  "setup.simulation.image_model.import.error.wrong_file_format":
    "Le fichier doit être au format svg ou png.",
  "setup.simulation.import_batch.checking": "Paramétrage de la brique de simulation modifié",
  "setup.simulation.import_files": "Importer vos fichiers",
  "setup.simulation.import_image": "Importer l'image",
  "setup.simulation.import_model.modal.subtitle": "Format de modèle",
  "setup.simulation.input_model.error.data_file_empty": "Le CSV est vide.",
  "setup.simulation.input_model.error.data_quantity_duplicated": (quantityName: string) =>
    `Grandeur ${quantityName} : une grandeur ne peut apparaître sur deux lignes différentes.`,
  "setup.simulation.input_model.error.data_quantity_name_not_allowed": (quantityName: string) =>
    `Grandeur ${quantityName} : les champs 'name' ou 'technicalName' doivent contenir des grandeurs définies dans le projet.`,
  "setup.simulation.input_model.error.file_invalid_content":
    "Erreur de format détectée : les colonnes attendues sont 'name', 'inputModelName' (avec optionnellement : 'orderExternalGap' et 'referenceValue').",
  "setup.simulation.input_model.error.file_invalid_type": "Le fichier doit être au format csv.",
  "setup.simulation.input_model.error.has_time_function_dependency":
    "Une grandeur basée sur le temps ne peut pas être utilisée dans le paramétrage",
  "setup.simulation.input_model.error.name.empty":
    "Le champ 'name' ou 'technicalName' ne peut pas être vide.",
  "setup.simulation.input_model.error.order_external_gap.ascending":
    "La colonne 'orderExternalGap' doit contenir une série croissante d'entiers positifs commençant par 1.",
  "setup.simulation.input_model.error.order_external_gap.invalid": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'orderExternalGap' doit contenir un entier positif supérieur ou égal à 1.`,
  "setup.simulation.input_model.error.reference_value.invalid": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'referenceValue' doit contenir un nombre.`,
  "setup.simulation.input_model.import.error.external_gap.invalid": (quantityName: string) =>
    `Grandeur ${quantityName} : le champ 'referenceValue' doit être renseigné lorsque le champ 'orderExternalGap' est défini.`,
  "setup.simulation.modal.binding.error.invalid_external_gap": ({ data }: { data: string[] }) => (
    <>
      {data.join(", ")}
      {data.length > 1 ? " doivent " : " doit "}{" "}
      {`avoir à la fois la valeur de référence et l'ordre de l'écart externe définis.`}
    </>
  ),
  "setup.simulation.modal.binding.error.missing_column":
    "Erreur de format détectée : les colonnes attendues sont 'name', 'inputModelName', 'outputModelName' ",
  "setup.simulation.modal.binding.error.orderExternalGapValue": ({ data }: { data: string[] }) =>
    `Le champ 'orderExternalGap' doit contenir des nombres entiers pour ${
      data.length > 1 ? "les grandeurs" : "la grandeur"
    } ${data.join(", ")}.`,
  "setup.simulation.modal.binding.error.order_external_no_ascending_starting_one":
    "L'ordre des écarts externes doit être strictement croissant et commencer à 1.",
  "setup.simulation.modal.binding.error.referenceValue": ({ data }: { data: string[] }) =>
    `Le champ 'referenceValue' doit contenir des nombres pour ${
      data.length > 1 ? "les grandeurs" : "la grandeur"
    } ${data.join(", ")}.`,
  "setup.simulation.modal.binding.error.unknown_quantity": ({ data }: { data: string[] }) => (
    <>
      {data.join(", ")} ne
      {data.length > 1 ? " correspondent " : " correspond "} à aucune grandeur du projet
    </>
  ),
  "setup.simulation.modal.binding.tooltip.info": "Format attendu : .csv",
  "setup.simulation.modal.expected_file_format.already_existing":
    "Un autre fichier a été identifié pour l'import : ",
  "setup.simulation.modal.expected_file_format.inputs": "Entrées du modèle :",
  "setup.simulation.modal.expected_file_format.inputs_outputs": "Entrées et sorties :",
  "setup.simulation.modal.expected_file_format.inputs_outputs.value": (displayOptional = true) =>
    `.csv ${displayOptional ? "(facultatif)" : ""} `,
  "setup.simulation.modal.expected_file_format.model.file": (
    modelType: string,
    fileNumber: number,
  ) => renderFileText(modelType, fileNumber),
  "setup.simulation.modal.expected_file_format.model.file.value": (
    modelType: string,
    fileNumber: number,
  ) => renderFileValue(modelType, fileNumber),
  "setup.simulation.modal.expected_file_format.model_image": "Image du modèle :",
  "setup.simulation.modal.expected_file_format.model_image.value": ".svg ou .png (facultatif)",
  "setup.simulation.modal.expected_file_format.non_identified": "Fichiers non utilisés : ",
  "setup.simulation.modal.expected_file_format.outputs": "Sorties du modèle :",
  "setup.simulation.modal.expected_file_format.unexpected_file":
    "Le nom ou le format du fichier n'est pas attendu : ",
  "setup.simulation.modal.fmu.parameters.tooltip.info":
    "Format attendu : .csv (nom contenant parameters)",
  "setup.simulation.modal.fmu.tooltip.info":
    "Format attendu : .csv (nom contenant iteration_variables)",
  "setup.simulation.modal.image.tooltip.info": "Format attendu : .png ou .svg",
  "setup.simulation.modal.main.dymola.tooltip.error":
    "Fichier dymosim requis pour réaliser l’import du modèle",
  "setup.simulation.modal.main.fmu.tooltip.error":
    "Fichier .fmu requis pour réaliser l’import du modèle",
  "setup.simulation.modal.variables.dymola.tooltip.error":
    "Fichier dsin.txt requis pour réaliser l’import du modèle",
  "setup.simulation.output_model.error.data_quantity_duplicated": (quantityName: string) =>
    `Grandeur ${quantityName} : une grandeur ne peut apparaître sur deux lignes différentes.`,
  "setup.simulation.output_model.error.file_invalid_content":
    "Erreur de format : Les colonnes attendues sont 'name', 'outputModelName' et 'offsetExpected'.",
  "setup.simulation.output_model.error.file_invalid_type": "Le fichier doit être au format csv.",
  "setup.simulation.output_model.import.error.data_file_empty": "Le CSV est vide.",
  "setup.simulation.output_model.import.error.data_offset_expected_invalid": (
    quantityName: string,
  ) => `Grandeur ${quantityName} : le champ 'offsetExpected' doit contenir un nombre.`,
  "setup.simulation.output_model.import.error.data_offset_expected_invalid_definition": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : le champ 'offsetExpected' ne peut être renseignée que lorsque la grandeur est une sortie du modèle.`,
  "setup.simulation.output_model.import.error.data_output_model_invalid_definition": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : le champ 'offsetExpected' doit être renseignée lorsque la grandeur est une sortie du modèle.`,
  "setup.simulation.output_model.import.error.data_quantity_name_not_allowed": (
    quantityName: string,
  ) =>
    `Grandeur ${quantityName} : les champs 'name' ou 'technicalName' doivent contenir des grandeurs définies dans le projet.`,
  "setup.simulation.status.incomplete": () => (
    <>
      Le paramétrage des simulations est <strong>incomplet</strong> car:
    </>
  ),
  "setup.simulation.status.incomplete.noInput": "Aucune liste d’entrées du modèle n'a été importée",
  "setup.simulation.status.incomplete.noModel": "Aucun modèle n’a été importé",
  "setup.simulation.status.incomplete.noOutput":
    "Aucune liste de sorties du modèle n'a été importée",
  "setup.simulation.status.invalid": () => (
    <>
      Le paramétrage des simulations est <strong>invalide</strong> car la vérification du modèle a
      échoué.
    </>
  ),
  "setup.simulation.status.valid": () => (
    <>
      Le paramétrage des simulations est <strong>valide</strong>.
    </>
  ),
  "setup.simulation.status.validating": () => (
    <>
      Le paramétrage des simulations est <strong>en cours de validation</strong>.
    </>
  ),
  "setup.simulation.table.input": "Variable d'entrée du modèle",
  "setup.simulation.table.offset": "Offset",
  "setup.simulation.table.orderExternalGap": "Ordre de l'écart externe",
  "setup.simulation.table.output": "Variable de sortie du modèle",
  "setup.simulation.table.quantities": "Grandeur",
  "setup.simulation.table.referenceValue": "Valeur de référence",
  "setup.simulation.table.unit": "Unité",
  "setups.modal.add_or_edit.create_setup.create": "Créer un paramétrage",
  "setups.modal.add_or_edit.create_setup.update": "Éditer le paramétrage",
  "setups.modal.create_or_edit.input.application_date.error.cannot_be_empty":
    "La date d'application d'application ne peut pas être vide",
  "setups.modal.create_or_edit.input.name.error.cannot_be_empty":
    "Le nom de paramétrage ne peut pas être vide",
  "setups.modal.create_or_edit.input.name.error.name_already_exists":
    "Le nom du paramétrage existe déjà",
  "setups.modal.create_or_edit.input.startDate.error.already_exists":
    "Un autre paramétrage utilise déjà cette date de début d'application",
  "setups.modal.create_or_edit.input.startDate.error.cannot_be_empty":
    "La date de début d'application ne peut pas être vide",
  "setups.modal.create_or_edit.input.startDate.error.should_be_greater":
    "La date de début d'application doit être postérieure à celle du dernier paramétrage",
  "setups.modal.delete.last_setup.infos": ({ currentSetupName }: { currentSetupName: string }) => (
    <>
      Souhaitez-vous confirmer la suppression du paramétrage{" "}
      <>
        <strong>{currentSetupName}</strong> ?
      </>{" "}
      Aucun paramétrage ne sera alors associé au projet.
    </>
  ),
  "setups.modal.delete.setup.infos": ({
    currentSetupName,
    linkedSetupName,
  }: {
    currentSetupName: string;
    linkedSetupName: string | undefined;
  }) => (
    <>
      Souhaitez-vous confirmer la suppression du paramétrage{" "}
      <>
        <strong>{currentSetupName}</strong> ?
      </>{" "}
      La période actuellement couverte par ce paramétrage sera alors rattachée au paramétrage{" "}
      <>
        <strong>{linkedSetupName}</strong>.
      </>
    </>
  ),
  "setups.modal.delete_setup": "Supprimer le paramétrage",
  "setups.modal.duplicate.duplicated_name": ({ name }: { name: string }) => `Copie de ${name}`,
  "setups.modal.duplicate_setup": "Dupliquer le paramétrage",
  "setups.modal.duplicate_setup.message.warning.reset_simulation_diagnostic":
    "Suite à cette action, les résultats de simulation et diagnostic seront effacés pour les analyses postérieures à la date d'application.",
  "setups.modal.duplicate_setup.title.description": "Description",
  "setups.modal.duplicate_setup.title.description.placeholder": "Description",
  "setups.modal.duplicate_setup.title.effective_start_date": "Date d'application",
  "setups.modal.duplicate_setup.title.name": "Nom",
  "setups.modal.duplicate_setup.title.name.placeholder": "Nom du paramétrage",
  "setups.modal.toast.delete_setup": ({ name }: { name: string }) => `Paramétrage ${name} supprimé`,
  "setups.modal.update_analyses_frequency.description":
    "L’intervalle choisi correspondra au temps moyen entre deux analyses et s’appliquera pour les imports de données à venir.",
  "setups.modal.update_analyses_frequency.title": "Modifier l'intervalle moyen",
  "setups.table.column.analysisCount": "Nombre d’analyses",
  "setups.table.column.author": "Auteur",
  "setups.table.column.description": "Description",
  "setups.table.column.endDate": "Fin d'application",
  "setups.table.column.name": "Nom",
  "setups.table.column.startDate": "Début d'application",
  "setups.table.column.status": "Statut",
  "showPublishedAnalysis.text": "Afficher les marqueurs de publication",
  "sidepanel.tab.activity": "Activité",
  "sidepanel.tab.activity.fault_event": "Fil d'activité",
  "sidepanel.tab.comments": "Commentaires",
  "simulationOutput.edit.confirm.title": "Confirmer la modification",
  "simulationOutput.edit.confirmation": (name: string) => (
    <>
      Souhaitez-vous confirmer la modification de <strong>{name}</strong> ?
    </>
  ),
  "sources.button.import": "Importer des données",
  "sources.delete.forbidden.title":
    "Impossible de supprimer la source, supprimez d'abord les projets associés",
  "sources.delete.label": "Nom de la source à supprimer",
  "sources.delete.placeholder": "Nom de la source",
  "sources.delete.title": "Supprimer la source",
  "sources.delete.toast.info": ({ name }: { name: string }) =>
    `Source ${name} en cours de suppression, cela peut prendre quelques minutes`,
  "sources.delete.toast.success": ({ name }: { name: string }) =>
    `Source ${name} supprimée avec succès`,
  "sources.delete.warning": ({ name }: { name: string }) => (
    <>
      Vous vous apprêtez à supprimer définitivement la source <strong>{name}</strong>.
      <br />
      Veuillez écrire ci-dessous le nom de la source pour confirmer la suppression.
    </>
  ),
  "sources.duplicate.duplicated_name": ({ name }: { name: string }) => `${name}_Copie`,
  "sources.duplicate.input.date.error.invalid_datetime": "Date et heure invalides",
  "sources.duplicate.input.date.error.invalid_range":
    "La date de début doit être antérieure ou égale à la date de fin",
  "sources.duplicate.input.name.error.cannot_be_empty": "Le nom de la source ne peut pas être vide",
  "sources.duplicate.input.name.error.name_already_exists": "Le nom de la source existe déjà",
  "sources.duplicate.input.name.error.too_long": "Le nom ne doit pas dépasser 100 caractères",
  "sources.duplicate.label": "Nom de la nouvelle source",
  "sources.duplicate.range.from": "De",
  "sources.duplicate.range.helper":
    "Vous pouvez limiter la duplication à une plage de dates en ajoutant une date de début ou de fin.",
  "sources.duplicate.range.helper_bounds":
    "Les jours sélectionnés sont interprétés dans votre fuseau local : la borne de début commence à 00:00:00 et la borne de fin se termine à 23:59:59.",
  "sources.duplicate.range.to": "À",
  "sources.duplicate.title": "Dupliquer la source",
  "sources.duplicate.toast.info": ({
    originSourceId,
    targetSourceId,
  }: {
    originSourceId: string;
    targetSourceId: string;
  }) =>
    `Duplication de la source ${originSourceId} vers ${targetSourceId} en cours, cela peut prendre quelques minutes`,
  "sources.duplicate.toast.success": ({
    originSourceId,
    targetSourceId,
  }: {
    originSourceId: string;
    targetSourceId: string;
  }) => `Duplication de la source ${originSourceId} vers ${targetSourceId} terminée`,
  "sources.duplicate.warning": ({ name }: { name: string }) => (
    <>
      Vous êtes sur le point de dupliquer la source <strong>{name}</strong>.
    </>
  ),
  "sources.groups.editor.delete.apply": "Supprimer les cycles",
  "sources.groups.editor.delete.from": "Cycles à supprimer",
  "sources.groups.editor.delete.prompt": "Sélectionner un ou plusieurs cycles",
  "sources.groups.editor.delete.title": "Supprimer des cycles",
  "sources.groups.editor.error.delete_required": "Sélectionnez au moins un cycle.",
  "sources.groups.editor.error.group_not_found": "Cycle introuvable.",
  "sources.groups.editor.error.group_required": "Le nom du cycle est obligatoire.",
  "sources.groups.editor.error.invalid_range": "Plage de dates invalide.",
  "sources.groups.editor.error.segment_required": "Sélectionnez d'abord les dates.",
  "sources.groups.editor.error.segment_same": "Ces dates sont déjà dans ce cycle.",
  "sources.groups.editor.group.label": "Nom du cycle",
  "sources.groups.editor.pending.clear": "Vider la liste",
  "sources.groups.editor.pending.confirm": "Confirmer les modifications",
  "sources.groups.editor.pending.empty": "Aucune modification en attente.",
  "sources.groups.editor.pending.item.delete": ({
    groups,
    count,
  }: {
    groups: string;
    count: number;
  }) => `Supprimer les cycles : ${groups} (${count} points)`,
  "sources.groups.editor.pending.item.range": ({
    group,
    from,
    to,
    count,
  }: {
    group: string;
    from: string;
    to: string;
    count: number;
  }) => `Assigner "${group}" à ${from} → ${to} (${count} points)`,
  "sources.groups.editor.pending.item.segment": ({
    group,
    from,
    to,
    count,
  }: {
    group: string;
    from: string;
    to: string;
    count: number;
  }) => `Assigner "${group}" aux dates ${from} → ${to} (${count} points)`,
  "sources.groups.editor.pending.title": "Modifications en attente",
  "sources.groups.editor.range.apply": "Assigner le cycle",
  "sources.groups.editor.range.copied": "Plage copiée depuis la ligne sélectionnée.",
  "sources.groups.editor.range.from": "Du",
  "sources.groups.editor.range.none":
    "Sélectionnez une plage de dates pour préremplir les champs, ou définissez une plage personnalisée.",
  "sources.groups.editor.range.title": "Assigner un cycle à une plage de dates",
  "sources.groups.editor.range.to": "Au",
  "sources.groups.editor.success": "Cycles mis à jour.",
  "sources.groups.empty": "Aucun cycle à afficher.",
  "sources.groups.error": ({ details }: { details: string }) =>
    `Impossible de charger les cycles : ${details}`,
  "sources.groups.link": "Gérer les cycles",
  "sources.groups.modal.title": "Gérer les cycles de la source",
  "sources.groups.table.action.select": "Sélectionner la plage",
  "sources.groups.table.column.dateRange": "Plage de dates",
  "sources.groups.table.column.group": "Cycle",
  "sources.groups.table.group.none": "Sans cycle",
  "sources.groups.timeline.gap": "Aucune donnée",
  "sources.header.title": "Sources de données",
  "sources.patchModal.action.import.errorMultipleFiles": "Merci d’importer un fichier à la fois",
  "sources.patchModal.action.success.action": "Créer un projet associé à cette source.",
  "sources.patchModal.action.success.message": ({ sourceId }: { sourceId: string }) =>
    `Données importées dans la source de données ${sourceId}.`,
  "sources.patchModal.data.period": "Période",
  "sources.patchModal.data.sensorCount": "Nombre de capteurs",
  "sources.patchModal.data.snapshotCount": "Nombre de points",
  "sources.patchModal.source.conflicting": "Conflit - bloquant l'import",
  "sources.patchModal.source.conflicting.tooltip":
    "Les données déjà présentes sont en conflit avec les données du fichier. L’import est impossible.",
  "sources.patchModal.source.ignored": "Déjà présents - ignorés",
  "sources.patchModal.source.ignored.tooltip":
    "Les données du fichier déjà présentes seront ignorées lors de l’import.",
  "sources.patchModal.source.merged": "Déjà présents - fusionnés",
  "sources.patchModal.source.merged.tooltip":
    "Les données déjà présentes seront complétées par les données du fichier.",
  "sources.patchModal.source.new": "Nouveaux",
  "sources.patchModal.source.title": "Source",
  "sources.selectFilter.project": "Projet",
  "sources.table.column.firstDate": "Premier point",
  "sources.table.column.lastDate": "Dernier point",
  "sources.table.column.linkedProjects": "Projets",
  "sources.table.column.meanInterval": "Écart moyen",
  "sources.table.column.meanInterval.format"({
    days,
    hours,
    minutes,
  }: {
    days: number;
    hours: number;
    minutes: number;
  }) {
    if (days > 0) return `${days} jour${days > 1 ? "s" : ""}`;
    if (hours > 0) return `${hours} heure${hours > 1 ? "s" : ""}`;
    return `${Math.ceil(minutes)} minute${minutes > 1 ? "s" : ""}`;
  },
  "sources.table.column.name": "Nom",
  "sources.table.column.sensorCount": "Capteurs",
  "sources.table.column.snapshotCount": "Points temporels",
  "sources.title": "Sources de données",
  "summary.chart.evolution.noAnalysisPublished": "Analyse non publiée",
  "summary.chart.evolution.option.qoi.description": (translations: {
    FR: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
    EN: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
  }) =>
    `Les affichages par Indicateur de performance donnent une vue focalisée sur un indicateur de performance particulier.\nLes défaillances sont affichées selon leurs impacts sur cette grandeur, et les ${lowercase(
      translations.FR.LOSSES,
    )} ainsi que les ${lowercase(translations.FR.LOSSES)} ${lowercase(
      translations.FR.UNEXPLAINED,
    )} sont celles de la grandeur en question.`,
  "summary.chart.evolution.title": "Suivi temporel",
  "summary.chart.evolution.tooltip.description.confirmed": "Défaillance confirmée",
  "summary.chart.evolution.tooltip.description.false_detection": "Fausse détection",
  "summary.chart.evolution.tooltip.description.suspected": "Défaillance suspectée",
  "summary.chart.evolution.tooltip.description.to_be_analysed": "À analyser",
  "summary.dashboard.actions.add": (translations: CustomTranslations) =>
    `Ajouter un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "summary.dashboard.actions.edit": "Modifier",
  "summary.dashboard.actions.export_pdf": "Exporter en PDF",
  "summary.dashboard.actions.move_left": "Déplacer vers la gauche",
  "summary.dashboard.actions.move_right": "Déplacer vers la droite",
  "summary.dashboard.actions.remove": "Retirer",
  "summary.dashboard.actions.remove.summary_disabled": "Cette vue ne peut pas être retirée",
  "summary.dashboard.menu.tooltip.actions": "Actions",
  "summary.dashboard.modal.remove.text": (
    { dashboardName }: { dashboardName: ReactNode },
    translations: CustomTranslations,
  ) => (
    <>
      Êtes-vous sûr de vouloir retirer le {translations.FR.DASHBOARD.toLowerCase()} {dashboardName}{" "}
      ? <br /> Cette action n&apos;affectera pas le {translations.FR.DASHBOARD.toLowerCase()}{" "}
      lui-même, mais il ne sera plus visible sur cette page.
    </>
  ),
  "summary.dashboard.modal.remove.title": (translations: CustomTranslations) =>
    `Retirer le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "summary.dashboard.popup.create": (translations: CustomTranslations) =>
    `Créer un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "summary.dashboard.popup.empty": (translations: CustomTranslations) =>
    `Ajoutez un ${translations.FR.DASHBOARD.toLowerCase()} pour suivre l’état de votre site`,
  "summary.dashboard.popup.empty.subtitle": (translations: CustomTranslations) =>
    `Démarrez rapidement avec notre modèle standard préconfiguré et modifiable ou créez votre propre ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "summary.dashboard.popup.template": "Ajouter le modèle préconfiguré",
  "summary.dashboard.tab.tooltip.see_more": "Voir plus",
  "summary.empty.action": "Aller à la page des réglages",
  "summary.empty.title":
    "Veuillez ajouter au moins un indicateur de performance pour afficher les résultats de la page synthèse",
  "summary.failures.table.amplitude": "Amplitude",
  "summary.failures.table.degradationType": "Type de dégradation",
  "summary.failures.table.equipmentType": "Type d'équipements",
  "summary.failures.table.equipments": "Équipements",
  "summary.failures.table.failureCategory": "Catégorie",
  "summary.failures.table.failures": "Défaillances",
  "summary.failures.table.loss": (quantity: string) => `${quantity}`,
  "summary.failures.table.reparability": "Réparabilité",
  "summary.failures.table.suspectedElements": "Éléments suspectés",
  "summary.failures.table.system": "Système",
  "summary.financial_impact_estimation.cost.label": ({
    reparability,
    translations,
  }: {
    reparability: "REPARABILITY_0" | "REPARABILITY_1" | "REPARABILITY_2";
    translations: {
      FR: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
      EN: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
    };
  }) => `avec réparabilité en ${translations.FR[reparability]}`,
  "summary.financial_impact_estimation.cost.price": "*prix de 60€/MWh",
  "summary.financial_impact_estimation.title": "Estimation de l'impact financier",
  "summary.financial_impact_estimation.tooltip": (translations: {
    FR: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
    EN: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
  }) =>
    `L’impact financier est calculé en projetant pour chaque défaillance la perte de production d’ici à la récupération de la perte associée, et en la valorisant à un prix de 60€/MWh. La durée de la défaillance est estimée à 3 semaines pour une réparabilité en ${translations.FR.REPARABILITY_0}, 2 mois pour ${translations.FR.REPARABILITY_1} et 7 000 h pour ${translations.FR.REPARABILITY_2}.`,
  "summary.instrumentation.state.invalid_measure": (count: number): string =>
    count > 1 ? "Mesures invalides" : "Mesure invalide",
  "summary.instrumentation.state.missing_measure": (count: number): string =>
    count > 1 ? "Capteurs manquants" : "Capteur manquant",
  "summary.instrumentation.state.unavailable_formula": (count: number): string =>
    count > 1 ? "Formules indisponibles" : "Formule indisponible",
  "summary.instrumentation.table.column.name": "Nom",
  "summary.instrumentation.table.column.state": "État",
  "summary.instrumentation.title": "Instrumentation",
  "summary.widget.failures.confirmed_failures": "Défaillances confirmées",
  "summary.widget.failures.rate": "Taux d'explication",
  "summary.widget.losses.expected": "Attendu",
  "summary.widget.losses.external_loss.metadata.boundary_condition": "Entrée du modèle",
  "summary.widget.losses.external_loss.metadata.measured": "Mesuré",
  "summary.widget.losses.external_loss.metadata.order": "Ordre",
  "summary.widget.losses.external_loss.metadata.reference": "Référence",
  "summary.widget.losses.internal": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "summary.widget.losses.measured": "Mesuré",
  "summary.widget.losses.performanceIndicator": "Indicateur de performance",
  "summaryPage.widgets.losses.switch.label": "Détails",
  "summaryPage.widgets.losses.title.external_losses": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)} externes`,
  "summaryPage.widgets.losses.title.internal_losses": (translations: CustomTranslations): string =>
    `${capitalize(translations.FR.LOSSES)} internes`,
  "symptom.chart.header.option.amplitude": "Valeur",
  "symptom.chart.header.option.amplitude.description":
    "Superposition des valeurs mesurées (même courbe que dans la page Données) et des valeurs attendues.",
  "symptom.chart.header.option.gap": "Écart",
  "symptom.chart.header.option.gap.description":
    "Evolution de la différence entre valeur mesurée et valeur attendue.\nSymptôme = Mesuré - Attendu.",
  "symptom.chart.header.option.normalizedGap": "Écart normalisé",
  "symptom.chart.header.option.normalizedGap.description":
    "Écart divisé par le facteur de normalisation (Sigma) de la grandeur définie dans le paramétrage.\nSeul ce mode d'affichage permet de comparer les symptômes de plusieurs grandeurs.",
  "symptom.chart.header.option.title": "Mode d'affichage",
  "symptoms.chart.legend.expected.description": "Valeur attendue calculée par le jumeau numérique",
  "symptoms.chart.legend.expected.name": "Attendu",
  "symptoms.chart.legend.measured.description": "Valeur remontée par un capteur ou calculée",
  "symptoms.chart.legend.measured.name": "Mesuré",
  "symptoms.chart.legend.no_symptom.description": "Pas de symptôme détecté",
  "symptoms.chart.legend.no_symptom.name": "Pas de symptôme",
  "symptoms.chart.legend.symptom.description": "Symptôme detecté",
  "symptoms.chart.legend.symptom.name": "Symptôme",
  "symptoms.chart.legend.valid_partial_inputs": "Calcul basé sur les données valides uniquement",
  "symptoms.chart.title": "Symptômes",
  "symptoms.chart.tooltip.description.expected": "Valeur attendue",
  "symptoms.chart.tooltip.description.missingBoundaryConditions": (missingBoundaries: string[]) =>
    missingBoundaries.length === 1
      ? `Entrée du modèle manquante: \n${missingBoundaries.join(", ")}`
      : `Entrées du modèle manquantes: \n${missingBoundaries.join(", ")}`,
  "symptoms.chart.tooltip.description.simulationError": (statusInfo: string) =>
    `Erreur lors de la réalisation de la simulation\n${statusInfo}\n...\nCliquez sur le marqueur rouge pour copier l'intégralité du message d'erreur`,
  "symptoms.chart.tooltip.description.uncompletedSimulation": "Simulation non réalisée",
  "symptoms.chart.tooltip.selected_chart.gap": "Écart",
  "symptoms.gap.chart.noSymptom": "|Symptôme| < 2σ",
  "symptoms.gap.chart.symptom": "|Symptôme| > 2σ",
  "users.modal.add_project_to_user.add_project": "Ajouter un projet",
  "users.modal.add_project_to_user.select.select_project": "Sélectionner un projet",
  "users.modal.create.add_user": "Ajouter un utilisateur",
  "users.modal.create.create_user.subtitle": "Identifiant",
  "users.modal.create.create_user.text":
    "Veuillez saisir l’identifiant et, si nécessaire, sélectionner le rôle global correspondant au niveau d’accès souhaité pour cette personne :",
  "users.modal.create.create_user.toast": ({ name }: { name: string }) =>
    `Utilisateur ${name} créé.`,
  "users.modal.create.input.id": "#",
  "users.modal.create.input.login.error.text_to_long": `Le champ est limité à 100 caractères`,
  "users.modal.create.input.name.error.cannot_be_empty": "Le nom d'utilisateur ne peut être vide",
  "users.modal.create.input.name.error.text_to_long": "Ce champ est limité à 100 caractères",
  "users.modal.create.select.global_role.label": "Rôle global",
  "users.modal.create.user_name.input.name": "Prénom NOM",
  "users.modal.create.user_name.input.name.label": "Nom",
  "users.modal.create.user_name.subtitle": "Détails personnels",
  "users.modal.create.user_name.text":
    "Afin que vos collègues puissent vous identifier correctement dans le Metroscope, veuillez saisir vos nom et prénom.",
  "users.modal.create.user_name.title": "Votre profil",
  "users.modal.create.user_name.toast": "Votre nom a été défini.",
  "users.modal.delete.are_you_sure": ({ login }: { login: string }) => (
    <span>
      Souhaitez-vous confirmer la suppression de l&apos;utilisateur <b>{login}</b> ?
    </span>
  ),
  "users.modal.delete.delete_user": "Supprimer l'utilisateur",
  "users.modal.delete.delete_user.toast": ({ name }: { name: string }) =>
    `Profil et droits d'accès de ${name} supprimés de l'application`,
  "users.modal.roles.global_role": "Rôle global",
  "users.modal.roles.identifier": "Identifiant",
  "users.modal.roles.specific_roles": "Rôles spécifiques",
  "users.modal.roles.table.project_name": "Nom du projet",
  "users.modal.roles.table.role": "Rôle",
  "users.modal.update.input.name": "Prénom NOM",
  "users.modal.update.input.name.label": "Nom",
  "users.modal.update.update_user": "Votre profil",
  "users.modal.update.update_user.toast": "Votre nom a été modifié.",
  "users.paragraphs.users.table.column.global_roles": "Rôles globaux",
  "users.paragraphs.users.table.column.id": "Identifiant",
  "users.paragraphs.users.table.column.name": "Nom",
  "users.paragraphs.users.table.column.project_roles": "Rôles spécifiques",
  "users.selectFilter.application_admin": "Admin",
  "users.selectFilter.none": "Aucun",
  "users.selectFilter.project": "Projet",
  "users.selectFilter.project_admin": "Gestion des accès",
  "users.selectFilter.project_prod_read_only": "Lecture projets en production",
  "users.selectFilter.project_reader": "Lecture",
  "users.selectFilter.project_setuper": "Paramétrage",
  "users.selectFilter.project_writer": "Écriture",
  "users.selectFilter.role": "Rôle global",
  "users.table.row.add_project": "Ajouter un projet",
  "users.table.row.icons.delete.user": "Effacer l'utilisateur",
  "users.table.row.menu.roles.application.admin.description":
    "Peut modifier le paramétrage et écrire dans tous les projets, créer/supprimer des projets et gérer les utilisateurs",
  "users.table.row.menu.roles.application.admin.title": "Admin",
  "users.table.row.menu.roles.application.read.description":
    "Peut consulter tous les projets, y compris les projets de recherche",
  "users.table.row.menu.roles.application.read.title": "Lecture",
  "users.table.row.menu.roles.application.read_access_jupyterhub.description":
    "Peut consulter tous les projets, y compris les projets de recherche et accéder à JupyterHub",
  "users.table.row.menu.roles.application.read_access_jupyterhub.title":
    "Lecture + Accès JupyterHub",
  "users.table.row.menu.roles.application.read_prod_only.description":
    "Peut consulter tous les projets en production",
  "users.table.row.menu.roles.application.read_prod_only.title": "Lecture projets en production",
  "users.table.row.menu.roles.application.setup.description":
    "Peut modifier le paramétrage et écrire dans tous les projets",
  "users.table.row.menu.roles.application.setup.title": "Paramétrage",
  "users.table.row.menu.roles.application.super_admin.description":
    "Accès complet à tous les projets et aux fonctions d'administration avancées",
  "users.table.row.menu.roles.application.write.description":
    "Peut charger des données, lancer des diagnostics, et ajouter des commentaires dans tous les projets",
  "users.table.row.menu.roles.application.write.title": "Écriture",
  "users.table.row.menu.roles.no_role": "Aucun",
  "users.table.row.menu.roles.project.admin.description":
    "Peut ajouter des utilisateurs et leur attribuer des rôles dans ce projet",
  "users.table.row.menu.roles.project.admin.title": "Gestion des accès",
  "users.table.row.menu.roles.project.read.description": "Peut consulter ce projet",
  "users.table.row.menu.roles.project.read.title": "Lecture",
  "users.table.row.menu.roles.project.setup.description":
    "Peut modifier le paramétrage et écrire dans ce projet",
  "users.table.row.menu.roles.project.setup.title": "Paramétrage",
  "users.table.row.menu.roles.project.write.description":
    "Peut charger des données, lancer des diagnostics, et ajouter des commentaires dans ce projet",
  "users.table.row.menu.roles.project.write.title": "Écriture",
  "users.table.row.modal.content.delete_specific_role": ({ name }: { name: string }) =>
    `Souhaitez-vous réellement enlever le rôle attribué sur le projet : ${name} ?`,
  "users.table.row.modal.title.delete_specific_role": "Supprimer un rôle spécifique",
  "users.table.row.projects_count": ({ count }: { count: number }) =>
    count === 1 ? `${count} Projet` : `${count} Projets`,
  "users.table.row.roles.application.toast_new_role": ({
    userName,
    role,
  }: {
    userName: string;
    role: string;
  }) => `Le role de ${userName} est désormais ${role}`,
  "users.table.row.roles.application.toast_no_role": ({ userName }: { userName: string }) =>
    `Aucun rôle n'a été attribué à ${userName}`,
  "users.table.row.roles.project.toast_add_role": ({
    userName,
    projectName,
    role,
  }: {
    userName: string;
    projectName: string;
    role: string;
  }) => `Le rôle de ${userName} dans le projet ${projectName} est désormais ${role}`,
  "users.table.row.roles.project.toast_remove_project": ({
    userName,
    projectName,
  }: {
    userName: string;
    projectName: string;
  }) => `${userName} a été supprimé du projet ${projectName}`,
  "users.table.row.roles.project.toast_remove_role": ({
    userName,
    projectName,
    role,
  }: {
    userName: string;
    projectName: string;
    role: string;
  }) => `Le rôle ${role} de ${userName} a été supprimé du projet ${projectName}`,
  "users.title": "Utilisateurs",
  "validations.manualFaultEvents": frValidations.manualFaultEvents,
};

export default fr;
